import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Trophy, ChartBar, Star, ArrowRight, Target, Fire, DownloadSimple, Table as TableIcon, ArrowsClockwise } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import { SavedPredictionDiagnostics } from '../components/w50/SavedPredictionDiagnostics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BALL_KEYS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

/* ================================================================
 * Miss-streak computation (frontend-only, purely derived).
 * For a window's per_draw_detail (chronological ascending), walk
 * from the most-recent draw backwards and count consecutive misses
 * per key for Top-5 and Top-10 until the first hit is observed.
 *
 * `keys` defaults to BALL_KEYS for backwards compatibility, but the
 * same computation works verbatim for LS_KEYS
 * (['lucky_star_1', 'lucky_star_2']) — we just iterate a different
 * set of position identifiers over the same `ball_by_ball` array
 * (which stores each position's per-draw hit/miss regardless of
 * whether it's a main ball or a lucky star).
 * ================================================================ */
function computeMissStreaks(perDrawDetail, keys = BALL_KEYS) {
  const out = { top5: {}, top10: {}, maxStreak5: 0, maxStreak10: 0 };
  keys.forEach((k) => { out.top5[k] = 0; out.top10[k] = 0; });
  if (!Array.isArray(perDrawDetail) || perDrawDetail.length === 0) return out;

  keys.forEach((k) => {
    let streak5 = 0;
    let streak5Done = false;
    let streak10 = 0;
    let streak10Done = false;
    // Walk from most recent to oldest
    for (let i = perDrawDetail.length - 1; i >= 0; i--) {
      const dd = perDrawDetail[i];
      const bb = (dd.ball_by_ball || []).find(b => b.ball === k);
      if (!bb) { streak5Done = true; streak10Done = true; break; }
      if (!streak5Done) { if (bb.hit_top5) { streak5Done = true; } else { streak5 += 1; } }
      if (!streak10Done) { if (bb.hit_top10) { streak10Done = true; } else { streak10 += 1; } }
      if (streak5Done && streak10Done) break;
    }
    out.top5[k] = streak5;
    out.top10[k] = streak10;
    if (streak5 > out.maxStreak5) out.maxStreak5 = streak5;
    if (streak10 > out.maxStreak10) out.maxStreak10 = streak10;
  });
  return out;
}

const LS_KEYS = ['lucky_star_1', 'lucky_star_2'];

/* Colour ladder for miss-streak cells */
function streakCellClasses(streak, isMidBall) {
  // Red ladder — deeper red = longer streak. Mid balls (2/3/4) get subtle amber ring.
  if (streak >= 5) return `bg-rose-600 text-white font-black ${isMidBall ? 'ring-2 ring-amber-300' : ''}`;
  if (streak >= 3) return `bg-rose-400 text-white font-black ${isMidBall ? 'ring-2 ring-amber-300' : ''}`;
  if (streak === 2) return `bg-amber-100 text-amber-900 font-black`;
  if (streak === 1) return `bg-gray-100 text-gray-700 font-bold`;
  return `bg-emerald-50 text-emerald-800 font-bold`;
}

/**
 * Compute the "expected miss gap" and a verdict tier for a (streak, hit%) pair.
 *
 * expected_gap = round(1 / p) - 1  → misses expected between two consecutive hits
 *   e.g. hit% 47.1% → 1/0.471 = 2.12 → gap ~1 (miss then hit, miss then hit …)
 *        hit% 25.5% → 1/0.255 = 3.92 → gap ~3
 *
 * Verdicts:
 *   • streak === 0                        → "Fresh" (just hit)
 *   • streak < expected                   → "Within norm"
 *   • streak === expected                 → "Due next"
 *   • streak === expected + 1             → "OVERDUE"
 *   • streak > expected + 1               → "DEEP OVERDUE"
 *
 * Edge cases:
 *   • pct === 0        → window has never hit that ball → always "NO HITS YET"
 *   • pct >= 100       → window always hits → "impossible drought" (shouldn't occur)
 */
function computeVerdict(streak, pct) {
  if (pct == null || pct <= 0) {
    return { tier: 'no_hits', gap: null, label: 'NO HITS YET', badge: 'bg-gray-800 text-white' };
  }
  const gap = Math.max(0, Math.round(100 / pct) - 1); // integer expected miss gap
  if (streak === 0) return { tier: 'fresh', gap, label: 'Fresh hit', badge: 'bg-emerald-500 text-white' };
  if (streak < gap)  return { tier: 'norm', gap, label: `Within norm (exp ${gap})`, badge: 'bg-gray-300 text-gray-800' };
  if (streak === gap) return { tier: 'due', gap, label: 'DUE NEXT', badge: 'bg-amber-400 text-amber-900' };
  if (streak === gap + 1) return { tier: 'over', gap, label: 'OVERDUE', badge: 'bg-rose-500 text-white' };
  return { tier: 'deep', gap, label: `DEEP OVERDUE (+${streak - gap})`, badge: 'bg-rose-700 text-white' };
}

// Excel/panel-style verdict label — shared across bucket-diagnostic views.
function formatVerdict(v) {
  if (v.tier === 'no_hits') return 'No Hits Yet';
  if (v.tier === 'fresh')   return `Fresh Hit (Exp. ${v.gap})`;
  if (v.tier === 'norm')    return `Within Norm (Exp. ${v.gap})`;
  if (v.tier === 'due')     return `Exp ${v.gap} (Due Next)`;
  if (v.tier === 'over')    return `Overdue (Exp. ${v.gap})`;
  if (v.tier === 'deep')    return `Deep Overdue (+${(v.label.match(/\+(\d+)/) || [])[1] || ''} · Exp. ${v.gap})`;
  return v.label;
}

const RankBadge = ({ rank }) => {
  const colors = rank === 1 ? 'bg-sky-100 text-sky-900' : rank === 2 ? 'bg-gray-300 text-gray-800' : rank === 3 ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-bold text-sm ${colors}`}>{rank}</span>;
};

const HitRateBar = ({ rate, color = 'bg-[#002FA7]' }) => (
  <div className="flex items-center gap-2 flex-1">
    <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(rate, 100)}%` }} />
    </div>
    <span className="font-mono font-bold text-sm w-14 text-right">{rate}%</span>
  </div>
);

export default function WindowAccuracy() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('ranking');
  const [selectedDrawIdx, setSelectedDrawIdx] = useState(null); // null = latest

  // Keyboard arrow navigation for the scorecard draw selector
  useEffect(() => {
    if (activeTab === 'saved-buckets') return undefined;
    const pdd = data?.ball_accuracy?.[0]?.per_draw_detail || [];
    if (pdd.length === 0) return undefined;
    const latestIdx = pdd.length - 1;
    const handler = (e) => {
      // Ignore when user is typing in an input / textarea / contenteditable
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      setSelectedDrawIdx(prev => {
        const cur = prev != null && prev >= 0 && prev < pdd.length ? prev : latestIdx;
        if (e.key === 'ArrowLeft') {
          return Math.max(0, cur - 1);  // older
        }
        return Math.min(latestIdx, cur + 1);  // newer
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [data, activeTab]);

  const [refreshing, setRefreshing] = useState(false);
  const fetchSequence = useRef(0);

  const fetchAccuracyData = async (isManualRefresh = false) => {
    const requestId = ++fetchSequence.current;
    if (isManualRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/analysis/window-accuracy`);
      if (requestId !== fetchSequence.current) return;
      setData(res.data);
      if (isManualRefresh) toast.success(`Refreshed — latest draw #${res.data?.ball_accuracy?.[0]?.per_draw_detail?.slice(-1)?.[0]?.draw_number ?? ''}`);
    } catch { if (requestId === fetchSequence.current) toast.error('Failed to fetch accuracy data'); }
    finally { if (requestId === fetchSequence.current) { setLoading(false); setRefreshing(false); } }
  };

  useEffect(() => { fetchAccuracyData(false); }, []);

  const windowPath = (num) => num === 0 ? '/summary' : `/window${num}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className={`${activeTab === 'saved-buckets' ? 'max-w-[1920px]' : 'max-w-[1400px]'} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex flex-wrap items-center justify-between gap-3 py-3 min-h-16">
            <div className="flex items-center gap-3 min-w-0 w-full md:w-auto md:flex-1">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-emerald-400 to-teal-600 rounded flex items-center justify-center"><Trophy size={24} weight="fill" className="text-white" /></div>
              <div className="min-w-0"><h1 className="text-lg font-bold text-gray-900">W50: Window Accuracy Tracker</h1><p className="text-xs text-gray-500" data-testid="w50-tracker-subtitle">{activeTab === 'saved-buckets' ? 'Displayed comparisons · From #46085 · Hits and draws considered for every P and T' : <>Which windows predict the best? Backtested on last {data?.test_draws_count || 50} draws · window grows +1 per new draw (cap {data?.baseline_max || 150})</>}</p></div>
            </div>
            {data && (
              <div className="flex items-center justify-between md:justify-start gap-3 min-w-0 w-full md:w-auto">
                <span className="text-xs text-gray-400" data-testid="w50-last-fetched-info">
                  {activeTab !== 'saved-buckets' && <>Trained on {data.train_draws_count} draws, tested on {data.test_draws_count}</>}
                  {data.ball_accuracy?.[0]?.per_draw_detail?.length ? (
                    <>{activeTab !== 'saved-buckets' ? ' · ' : ''}Latest draw synced: #{data.ball_accuracy[0].per_draw_detail.slice(-1)[0].draw_number}</>
                  ) : null}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchAccuracyData(true)}
                  disabled={refreshing}
                  data-testid="w50-refresh-btn"
                  title="Re-fetch latest data — press this after syncing/adding a new draw if numbers look unchanged"
                >
                  <ArrowsClockwise size={14} className={refreshing ? 'animate-spin mr-1.5' : 'mr-1.5'} />
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className={`${activeTab === 'saved-buckets' ? 'max-w-[1920px]' : 'max-w-[1400px]'} mx-auto px-4 sm:px-6 lg:px-8 py-6`}>


        <WindowNarrative windowId="W50" {...NARRATIVES.W50} className="mb-5" />
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex flex-wrap h-auto justify-start gap-1" data-testid="w50-tab-list">
              <TabsTrigger value="ranking" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-ranking">Accuracy Ranking</TabsTrigger>
              <TabsTrigger value="perball" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-perball">🎯 Per-Ball Accuracy</TabsTrigger>
              <TabsTrigger value="buckets" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white font-bold" data-testid="tab-buckets">🪣 BD</TabsTrigger>
              <TabsTrigger value="saved-buckets" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-saved-buckets">BD1</TabsTrigger>
              <TabsTrigger value="misses" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white font-bold" data-testid="tab-misses">🔥 Miss Streaks</TabsTrigger>
              <TabsTrigger value="detail" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-detail">Per-Draw Detail</TabsTrigger>
              <TabsTrigger value="stars" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white font-bold" data-testid="tab-stars">⭐ LS Ranking</TabsTrigger>
              <TabsTrigger value="ls-detail" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white font-bold" data-testid="tab-ls-detail">⭐ LS Per-Draw</TabsTrigger>
              <TabsTrigger value="alignment" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white font-bold" data-testid="tab-alignment">🔗 Alignment Audit</TabsTrigger>
            </TabsList>

            {/* MAIN RANKING */}
            <TabsContent value="ranking">
              {/* Champion Banner */}
              {data.ball_accuracy?.[0] && (
                <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 rounded-xl p-6 mb-6 text-white shadow-lg" data-testid="champion-banner">
                  <div className="flex items-center gap-4">
                    <Trophy size={48} weight="fill" className="text-yellow-100" />
                    <div className="flex-1">
                      <p className="text-sm text-yellow-100 font-medium">Most Accurate Predictor</p>
                      <h2 className="text-2xl font-bold">{data.ball_accuracy[0].window_id}: {data.ball_accuracy[0].window_name}</h2>
                      <p className="text-sm text-yellow-100 mt-1">
                        Top-5 hit rate: <b className="text-white text-lg">{data.ball_accuracy[0].top5_hit_rate}%</b>
                        {' '} | Top-10 hit rate: <b className="text-white">{data.ball_accuracy[0].top10_hit_rate}%</b>
                        {' '} | Avg {data.ball_accuracy[0].avg_hits_per_draw} hits per draw
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => navigate(windowPath(data.ball_accuracy[0].window_num))}
                      className="bg-white/20 hover:bg-white/30 text-white border-0" data-testid="go-champion">
                      Open Window <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Consolidated per-bucket % matrix — all windows in one table */}
              <AllWindowsBucketMatrix windows={data.ball_accuracy} />

              {/* Full Ranking Table */}
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <span className="text-sm font-bold text-gray-700">Ball Prediction Accuracy — Top 5 vs Top 10 per Position</span>
                </div>
                <div className="overflow-x-auto" data-testid="accuracy-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-center w-16">Rank</th>
                        <th className="px-4 py-3 text-left">Window</th>
                        <th className="px-4 py-3 text-left w-64">Top-5 Hit Rate</th>
                        <th className="px-4 py-3 text-left w-64">Top-10 Hit Rate</th>
                        <th className="px-4 py-3 text-center w-24">Avg Hits</th>
                        <th className="px-4 py-3 text-center w-20">Best Draw</th>
                        <th className="px-4 py-3 text-center w-16">Go</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ball_accuracy.map((w, idx) => (
                        <tr key={w.window_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`accuracy-row-${w.window_id}`}>
                          <td className="px-4 py-4 text-center"><RankBadge rank={w.rank} /></td>
                          <td className="px-4 py-4">
                            <span className="font-bold text-gray-900">{w.window_id}</span>
                            <span className="text-gray-500 text-xs ml-2">{w.window_name}</span>
                          </td>
                          <td className="px-4 py-4">
                            <HitRateBar rate={w.top5_hit_rate} color={w.rank <= 3 ? 'bg-emerald-500' : 'bg-[#002FA7]'} />
                          </td>
                          <td className="px-4 py-4">
                            <HitRateBar rate={w.top10_hit_rate} color={w.rank <= 3 ? 'bg-teal-500' : 'bg-blue-400'} />
                          </td>
                          <td className="px-4 py-4 text-center font-mono font-bold">{w.avg_hits_per_draw}<span className="text-gray-400 text-xs">/5</span></td>
                          <td className="px-4 py-4 text-center">
                            {w.best_draw && (
                              <div className="flex flex-col items-center leading-tight">
                                <span className="text-xs font-mono font-bold">{w.best_draw.hits_top5}/5</span>
                                {w.best_draw_count > 0 && (
                                  <span className="text-[10px] text-gray-500 font-mono" data-testid={`best-draw-count-${w.window_id}`}>
                                    × {w.best_draw_count} {w.best_draw_count === 1 ? 'time' : 'times'}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Button variant="ghost" size="sm" onClick={() => navigate(windowPath(w.window_num))} className="h-7 w-7 p-0" data-testid={`go-${w.window_id}`}>
                              <ArrowRight size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Consolidated Last-Draw Scorecard — actual ONCE, then top-3 windows compared side-by-side */}
              {(() => {
                const top3 = (data.ball_accuracy || []).slice(0, 3);
                if (top3.length === 0) return null;
                const pdd0 = top3[0]?.per_draw_detail || [];
                if (pdd0.length === 0) return null;
                const latestIdx = pdd0.length - 1;
                const activeIdx = selectedDrawIdx != null && selectedDrawIdx >= 0 && selectedDrawIdx < pdd0.length ? selectedDrawIdx : latestIdx;
                const activeRow0 = pdd0[activeIdx];
                const optionOrder = pdd0.map((_, i) => i).reverse();
                const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

                // Per-window: pick the row at the same index from per_draw_detail
                const windowRows = top3.map((w) => {
                  const pdd = w?.per_draw_detail || [];
                  const row = pdd[activeIdx] || pdd[pdd.length - 1];
                  return { window: w, row };
                });

                return (
                  <div className="mt-6 bg-white border-2 border-gray-200 rounded-xl overflow-hidden" data-testid="consolidated-scorecard">
                    {/* Header — single, with draw selector */}
                    <div className="bg-gradient-to-r from-[#002FA7] via-indigo-700 to-purple-700 text-white px-5 py-3">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <h3 className="font-black text-base flex items-center gap-2">
                          <Target size={18} />
                          Last-Draw Scorecard — Top-3 Windows compared side-by-side
                        </h3>
                        <div className="flex items-center gap-2">
                          <label className="text-[11px] font-bold text-white/90">Draw:</label>
                          <select
                            value={activeIdx}
                            onChange={(e) => setSelectedDrawIdx(parseInt(e.target.value, 10))}
                            className="px-2 py-1 rounded text-gray-900 font-mono text-xs font-bold"
                            data-testid="scorecard-date-selector"
                          >
                            {optionOrder.map((i) => {
                              const row = pdd0[i];
                              const isLatest = i === latestIdx;
                              return (
                                <option key={i} value={i} data-testid={`scorecard-draw-option-${row.draw_number}`}>
                                  {`#${row.draw_number} — ${row.draw_date}${isLatest ? ' (latest)' : ''}`}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>
                      <p className="text-[11px] text-white/85 mt-1">
                        These predictions were made <strong>{pdd0.length} draws ago</strong> and tested against every draw since.
                        Use <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/30 rounded text-[10px] font-mono">←</kbd>/<kbd className="px-1.5 py-0.5 bg-white/15 border border-white/30 rounded text-[10px] font-mono">→</kbd> to step through the last {pdd0.length} draws.
                      </p>
                    </div>

                    {/* Body */}
                    <div className="p-5 overflow-x-auto">
                      <table className="w-full text-sm" data-testid="consolidated-scorecard-table">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left px-3 py-2 text-[10px] font-black text-gray-500 uppercase tracking-wider w-44">Window</th>
                            {ballKeys.map((_, i) => (
                              <th key={`h-${i}`} className="text-center px-3 py-2 text-[10px] font-black text-gray-500 uppercase tracking-wider">Ball {i + 1}</th>
                            ))}
                            <th className="text-center px-3 py-2 text-[10px] font-black text-gray-500 uppercase tracking-wider w-20">Hits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Actual drawn — ONCE at top */}
                          <tr className="bg-gray-50 border-b-2 border-gray-300" data-testid="consolidated-actual-row">
                            <td className="px-3 py-3 align-middle">
                              <div className="text-[10px] font-black text-gray-700 uppercase tracking-wider">Actual drawn</div>
                              <div className="text-[10px] text-gray-500 font-mono">#{activeRow0.draw_number} · {activeRow0.draw_date}</div>
                            </td>
                            {ballKeys.map((bk, i) => {
                              const ballRow = activeRow0?.ball_by_ball?.find(b => b.ball === bk);
                              const actual = ballRow?.actual;
                              return (
                                <td key={`a-${bk}`} className="text-center px-3 py-3" data-testid={`consolidated-actual-ball-${i + 1}`}>
                                  <span className="inline-flex w-12 h-12 rounded-full items-center justify-center font-mono font-black text-xl bg-gray-900 text-white shadow">{actual}</span>
                                </td>
                              );
                            })}
                            <td className="text-center px-3 py-3 text-[10px] text-gray-500 font-mono">drawn</td>
                          </tr>

                          {/* One row per window — predictions with hits highlighted */}
                          {windowRows.map(({ window: w, row }, rank) => {
                            const accent = rank === 0 ? 'border-l-4 border-l-amber-400' : rank === 1 ? 'border-l-4 border-l-slate-400' : 'border-l-4 border-l-orange-400';
                            const rankLabel = rank === 0 ? '🥇 #1' : rank === 1 ? '🥈 #2' : '🥉 #3';
                            const totalHits = row?.hits_top5 || 0;

                            return (
                              <tr key={w.window_id} className={`border-b border-gray-100 hover:bg-gray-50/50 ${accent}`} data-testid={`consolidated-window-row-${rank + 1}`}>
                                <td className="px-3 py-3 align-top">
                                  <button
                                    onClick={() => navigate(windowPath(w.window_num))}
                                    className="block text-left group"
                                    data-testid={`consolidated-go-${w.window_id}`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[11px] font-black text-gray-500">{rankLabel}</span>
                                      <span className="font-bold text-gray-900 group-hover:text-[#002FA7]">{w.window_id}</span>
                                    </div>
                                    <div className="text-[10px] text-gray-500 leading-tight">{w.window_name}</div>
                                    <div className="text-[10px] font-mono text-gray-600 mt-0.5">top-5: <b>{w.top5_hit_rate}%</b></div>
                                  </button>
                                </td>
                                {ballKeys.map((bk, i) => {
                                  const predictions = w?.top_predictions?.[bk] || [];
                                  const ballRow = row?.ball_by_ball?.find(b => b.ball === bk);
                                  const actual = ballRow?.actual;
                                  const wasHit = ballRow?.hit_top5;
                                  return (
                                    <td key={`p-${w.window_id}-${bk}`} className="px-2 py-3 align-middle" data-testid={`consolidated-${w.window_id}-ball-${i + 1}`}>
                                      <div className={`flex flex-wrap justify-center gap-1 p-1.5 rounded-lg ${wasHit ? 'bg-emerald-50 ring-2 ring-emerald-400' : ''}`}>
                                        {predictions.slice(0, 5).map((n) => {
                                          const isHit = n === actual;
                                          return (
                                            <span key={n} className={`w-7 h-7 rounded-full flex items-center justify-center font-mono font-bold text-[11px] transition-all ${isHit ? 'bg-emerald-500 text-white ring-2 ring-emerald-300 scale-110 shadow' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>{n}</span>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="text-center px-3 py-3">
                                  <span className={`inline-flex items-center justify-center px-2 py-1 rounded font-mono font-black text-sm ${totalHits >= 3 ? 'bg-emerald-500 text-white' : totalHits >= 1 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`} data-testid={`consolidated-hits-${rank + 1}`}>
                                    {totalHits}/5
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
                        <strong>Reading this:</strong> Top row shows the <strong>actual drawn numbers</strong> (shown once). Each window row below shows the <strong>5 numbers it predicted {pdd0.length} draws ago for that ball position</strong>. A <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white font-mono text-[10px] font-bold align-middle">✓</span> green ball means that prediction matched the actual drawn number. Cell background turns green if any of the 5 predictions hit. Click a window name to jump to its full analysis.
                      </div>
                    </div>
                  </div>
                );
              })()}
            </TabsContent>

            {/* PER-BALL ACCURACY MATRIX — window × ball_1..ball_5 hit-rate table */}
            <TabsContent value="perball">
              <div className="bg-white border rounded-xl overflow-hidden" data-testid="per-ball-accuracy-block">
                <div className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Target size={20} weight="fill" />
                      <h2 className="font-bold text-base">🎯 Per-Ball Projection Accuracy — Window × Position Matrix</h2>
                    </div>
                    <span className="text-xs text-emerald-50 font-mono">
                      Trained on <b className="text-white">{data.train_draws_count}</b> draws · Tested on <b className="text-white">{data.test_draws_count}</b> draws
                    </span>
                  </div>
                  <p className="text-[11px] text-emerald-50 mt-1">
                    For each window, the number of times its Top-5 projection at that ball position matched the actual drawn number, across the last <b>{data.test_draws_count}</b> draws. E.g., &quot;40 / {data.test_draws_count} (26.7%)&quot; means the window&apos;s Ball 1 Top-5 contained the actual Ball 1 number 40 times.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="per-ball-accuracy-table">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-center w-14 border-r border-gray-200">Rank</th>
                        <th className="px-3 py-2 text-left border-r border-gray-200">Window</th>
                        {['Ball 1', 'Ball 2', 'Ball 3', 'Ball 4', 'Ball 5'].map((lab) => (
                          <th key={lab} className="px-3 py-2 text-center border-r border-gray-200">{lab}</th>
                        ))}
                        <th className="px-3 py-2 text-center bg-amber-50 font-black text-amber-900">Overall Top-5</th>
                        <th className="px-3 py-2 text-center bg-sky-50 font-black text-sky-900">Overall Top-10</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.ball_accuracy || []).map((w, idx) => {
                        const pba = w.per_ball_accuracy || {};
                        return (
                          <tr key={w.window_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`per-ball-row-${w.window_id}`}>
                            <td className="px-3 py-3 text-center border-r border-gray-200"><RankBadge rank={w.rank} /></td>
                            <td className="px-3 py-3 border-r border-gray-200">
                              <span className="font-bold text-gray-900">{w.window_id}</span>
                              <span className="text-gray-500 text-xs ml-2">{w.window_name}</span>
                            </td>
                            {['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'].map((bk) => {
                              const cell = pba[bk] || {};
                              const pct5 = cell.pct_top5 ?? 0;
                              const pct10 = cell.pct_top10 ?? 0;
                              const cls5 =
                                pct5 >= 40 ? 'bg-emerald-100 text-emerald-900 font-black' :
                                pct5 >= 25 ? 'bg-emerald-50 text-emerald-800 font-bold' :
                                pct5 >= 15 ? 'text-gray-700' :
                                'text-rose-600';
                              return (
                                <td key={bk} className={`px-3 py-3 text-center border-r border-gray-200 ${cls5}`} data-testid={`per-ball-cell-${w.window_id}-${bk}`}>
                                  <div className="font-mono text-sm leading-tight" title={`Top-5: ${cell.hits_top5 ?? 0}/${cell.possible ?? 0} · Top-10: ${cell.hits_top10 ?? 0}/${cell.possible ?? 0} (${pct10}%)`}>
                                    <div><b>{cell.hits_top5 ?? 0}</b>/<span className="opacity-60">{cell.possible ?? 0}</span></div>
                                    <div className="text-[11px] opacity-90">{pct5}%</div>
                                  </div>
                                </td>
                              );
                            })}
                            <td className="px-3 py-3 text-center bg-amber-50/60 font-mono font-black text-amber-900" data-testid={`per-ball-overall-top5-${w.window_id}`}>
                              {w.top5_hit_rate}%
                            </td>
                            <td className="px-3 py-3 text-center bg-sky-50/60 font-mono font-black text-sky-900" data-testid={`per-ball-overall-top10-${w.window_id}`}>
                              {w.top10_hit_rate}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t text-[11px] text-gray-600 flex flex-wrap items-center gap-3">
                  <span>Colour legend (Top-5 %):</span>
                  <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 font-black">≥ 40% (elite)</span>
                  <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 font-bold">≥ 25% (strong)</span>
                  <span className="inline-block px-2 py-0.5 rounded text-gray-700">15–25% (fair)</span>
                  <span className="inline-block px-2 py-0.5 rounded text-rose-600">&lt; 15% (weak)</span>
                  <span className="ml-auto italic">Hover any cell for Top-10 % detail.</span>
                </div>
              </div>
            </TabsContent>


            {/* BUCKET DIAGNOSTIC — T5/T10/T15/T20 blocks side by side */}
            <TabsContent value="buckets">
              <BucketDiagnosticPanel data={data} />
            </TabsContent>

            <TabsContent value="saved-buckets" data-testid="saved-buckets-content">
              <SavedPredictionDiagnostics refreshKey={data} onDrawUpdate={() => fetchAccuracyData(false)} />
            </TabsContent>

            {/* PER-DRAW DETAIL */}
            <TabsContent value="misses">
              <MissStreaksPanel data={data} onGoTo={(num) => navigate(windowPath(num))} />
            </TabsContent>

            {/* PER-DRAW DETAIL */}
            <TabsContent value="detail">
              <div className="space-y-4">
                {data.ball_accuracy.map(w => {
                  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
                  return (
                  <div key={w.window_id} className="bg-white border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <RankBadge rank={w.rank} />
                        <span className="font-bold">{w.window_id}: {w.window_name}</span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-3">
                        <span>top-5: <b className="text-gray-700">{w.top5_hit_rate}%</b></span>
                        <span>top-10: <b className="text-gray-700">{w.top10_hit_rate}%</b></span>
                        <span>{(w.per_draw_detail || []).length} draws backtested</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-2 italic">
                      Numbers in blue = projected top-5 (with score). <span className="inline-block w-3 h-3 rounded-full bg-emerald-500 align-middle mx-0.5"></span> green = positional hit (predicted for this exact ball position). <span className="inline-block w-3 h-3 rounded-full bg-violet-500 align-middle mx-0.5"></span> violet = cross-position hit (predicted number came out at a different ball position). Plain = miss.
                    </p>
                    {/* Scroll container — 50 draws × 9 windows would dominate the page,
                        so each window's per-draw block scrolls inside ~600px max height. */}
                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 border border-gray-100 rounded-lg p-2" data-testid={`detail-${w.window_id}`}>
                      {(w.per_draw_detail || []).slice().reverse().map((dd) => {
                        const rowBg = dd.hits_top5 >= 3 ? 'bg-emerald-50 border-emerald-300'
                          : dd.hits_top5 >= 2 ? 'bg-blue-50 border-blue-200'
                          : dd.hits_top5 >= 1 ? 'bg-gray-50 border-gray-200'
                          : 'bg-red-50 border-red-200';
                        return (
                          <div key={dd.draw_number} className={`border rounded-lg p-3 ${rowBg}`} data-testid={`detail-${w.window_id}-row-${dd.draw_number}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-mono font-black text-gray-800">#{dd.draw_number}</span>
                                <span className="text-[10px] text-gray-500 ml-2">{dd.draw_date}</span>
                              </div>
                              <div className="text-right">
                                <div className={`font-mono font-black text-base ${dd.hits_top5 >= 3 ? 'text-emerald-600' : dd.hits_top5 >= 1 ? 'text-blue-600' : 'text-red-400'}`}>{dd.hits_top5}/5</div>
                                <div className="text-[10px] text-gray-500">top-5 · {dd.hits_top10}/5 top-10</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-5 gap-2" data-testid={`detail-${w.window_id}-balls-${dd.draw_number}`}>
                              {ballKeys.map((bk, bi) => {
                                const predictions = (w.top_predictions && w.top_predictions[bk]) || [];
                                const predScores = (w.top_prediction_scores && w.top_prediction_scores[bk]) || [];
                                const bb = (dd.ball_by_ball || []).find(b => b.ball === bk) || {};
                                const actual = bb.actual;
                                const actualScore = bb.score;
                                const drawnSet = new Set(dd.actual_balls || []);
                                const actualClasses = bb.hit_top5
                                  ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                  : bb.hit_top10
                                  ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400'
                                  : 'bg-red-100 text-red-600 line-through';
                                return (
                                  <div key={bk} className="border border-gray-200 rounded-md p-2 bg-white/80" data-testid={`detail-${w.window_id}-${dd.draw_number}-${bk}`}>
                                    <div className="text-[9px] font-black text-gray-500 uppercase tracking-wider mb-1">B{bi + 1}</div>
                                    <div className="text-[8px] uppercase tracking-wider text-gray-400 font-bold">Projected top-5</div>
                                    <div className="flex flex-wrap gap-1 mt-1 mb-2">
                                      {predictions.length === 0 ? (
                                        <span className="text-[9px] text-gray-400 italic">no projection</span>
                                      ) : predictions.map((n, idx) => {
                                        const sc = predScores[idx];
                                        const isPositional = n === actual;
                                        const isCrossPos = !isPositional && drawnSet.has(n);
                                        const pillClasses = isPositional
                                          ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                          : isCrossPos
                                          ? 'bg-violet-500 text-white ring-2 ring-violet-300'
                                          : 'bg-[#002FA7]/10 text-[#002FA7]';
                                        const titleText = isPositional
                                          ? `Positional hit — B${bi + 1} actually drew ${n}`
                                          : isCrossPos
                                          ? `Cross-position hit — ${n} came out in this draw at a different ball position`
                                          : `${n} was projected but did not come out in this draw`;
                                        return (
                                          <div key={`${n}-${idx}`} className="flex flex-col items-center" data-testid={`detail-${w.window_id}-${dd.draw_number}-${bk}-pred-${n}`}>
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-mono font-bold text-[10px] ${pillClasses}`} title={titleText}>{n}</span>
                                            {sc != null && (
                                              <span className="text-[8px] text-gray-500 font-mono mt-0.5 leading-none">{Number(sc).toFixed(2)}</span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="text-[8px] uppercase tracking-wider text-gray-400 font-bold">Actual</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-black text-xs ${actualClasses}`} title={bb.hit_top5 ? 'Top-5 HIT' : bb.hit_top10 ? 'Top-10 hit' : 'Miss'}>
                                        {actual}
                                      </span>
                                      {actualScore != null && (
                                        <span className="text-[10px] font-mono text-gray-700 leading-tight">
                                          <span className="text-gray-400 font-sans uppercase tracking-wider text-[8px] block">score</span>
                                          <b>{Number(actualScore).toFixed(2)}</b>
                                        </span>
                                      )}
                                      {actualScore == null && (
                                        <span className="text-[9px] text-gray-400 italic">no score</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* LS RANKING — full schema (top-5 / top-10 / per-LS breakdown / projections) */}
            <TabsContent value="stars">
              <div className="bg-white border rounded-xl overflow-hidden" data-testid="ls-accuracy">
                <div className="px-4 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-white flex items-center gap-2">
                  <Star size={16} weight="fill" />
                  <span className="text-sm font-bold">Lucky Star Prediction Accuracy — same analytics as main balls (Top-5 / Top-10 rate, per-LS breakdown)</span>
                  <span className="ml-auto text-[10px] font-mono opacity-80">{(data.ls_accuracy || []).length} LS windows · {data.test_draws_count} test draws</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-yellow-50 border-b border-yellow-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-black text-gray-600 uppercase tracking-wider">Rank</th>
                        <th className="px-3 py-2 text-left text-[10px] font-black text-gray-600 uppercase tracking-wider">Window</th>
                        <th className="px-3 py-2 text-center text-[10px] font-black text-gray-600 uppercase tracking-wider">Top-5%</th>
                        <th className="px-3 py-2 text-center text-[10px] font-black text-gray-600 uppercase tracking-wider">Top-10%</th>
                        <th className="px-3 py-2 text-center text-[10px] font-black text-gray-600 uppercase tracking-wider">LS1 · T5 / T10</th>
                        <th className="px-3 py-2 text-center text-[10px] font-black text-gray-600 uppercase tracking-wider">LS2 · T5 / T10</th>
                        <th className="px-3 py-2 text-left text-[10px] font-black text-gray-600 uppercase tracking-wider">Projected Top-5 LS1</th>
                        <th className="px-3 py-2 text-left text-[10px] font-black text-gray-600 uppercase tracking-wider">Projected Top-5 LS2</th>
                        <th className="px-3 py-2 text-center text-[10px] font-black text-gray-600 uppercase tracking-wider">Best draw</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.ls_accuracy || []).map((ls, idx) => {
                        const ls1 = ls.per_ball_accuracy?.lucky_star_1 || {};
                        const ls2 = ls.per_ball_accuracy?.lucky_star_2 || {};
                        const preds1 = ls.top_predictions?.lucky_star_1 || [];
                        const preds2 = ls.top_predictions?.lucky_star_2 || [];
                        const scores1 = ls.top_prediction_scores?.lucky_star_1 || [];
                        const scores2 = ls.top_prediction_scores?.lucky_star_2 || [];
                        return (
                          <tr key={ls.window_id} className={idx === 0 ? 'bg-yellow-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`ls-${ls.window_id}`}>
                            <td className="px-3 py-3 align-top"><RankBadge rank={ls.rank || idx + 1} /></td>
                            <td className="px-3 py-3 align-top">
                              <div className="font-black text-gray-900">{ls.window_id}</div>
                              <div className="text-[10px] text-gray-500">{ls.window_name}</div>
                            </td>
                            <td className="px-3 py-3 text-center align-top">
                              <div className="font-mono font-black text-base text-amber-800">{ls.top5_hit_rate}%</div>
                              <div className="text-[9px] text-gray-500 font-mono">{ls.total_hits_top5}/{ls.total_possible}</div>
                            </td>
                            <td className="px-3 py-3 text-center align-top">
                              <div className="font-mono font-black text-base text-amber-700">{ls.top10_hit_rate}%</div>
                            </td>
                            <td className="px-3 py-3 text-center align-top">
                              <div className="font-mono font-bold text-gray-800">{ls1.pct_top5 || 0}% / {ls1.pct_top10 || 0}%</div>
                              <div className="text-[9px] text-gray-500 font-mono">{ls1.hits_top5 || 0}/{ls1.possible || 0} · {ls1.hits_top10 || 0}/{ls1.possible || 0}</div>
                            </td>
                            <td className="px-3 py-3 text-center align-top">
                              <div className="font-mono font-bold text-gray-800">{ls2.pct_top5 || 0}% / {ls2.pct_top10 || 0}%</div>
                              <div className="text-[9px] text-gray-500 font-mono">{ls2.hits_top5 || 0}/{ls2.possible || 0} · {ls2.hits_top10 || 0}/{ls2.possible || 0}</div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-wrap gap-1">
                                {preds1.map((n, i) => (
                                  <div key={n} className="flex flex-col items-center">
                                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-black text-[10px] ${i === 0 ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-900'}`}>{n}</span>
                                    {scores1[i] != null && <span className="text-[8px] text-gray-500 font-mono">{Number(scores1[i]).toFixed(2)}</span>}
                                  </div>
                                ))}
                                {preds1.length === 0 && <span className="text-[10px] text-gray-400 italic">—</span>}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-wrap gap-1">
                                {preds2.map((n, i) => (
                                  <div key={n} className="flex flex-col items-center">
                                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-black text-[10px] ${i === 0 ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-900'}`}>{n}</span>
                                    {scores2[i] != null && <span className="text-[8px] text-gray-500 font-mono">{Number(scores2[i]).toFixed(2)}</span>}
                                  </div>
                                ))}
                                {preds2.length === 0 && <span className="text-[10px] text-gray-400 italic">—</span>}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center align-top">
                              {ls.best_draw ? (
                                <div>
                                  <div className="font-mono font-black text-emerald-700">{ls.best_draw.hits_top5}/2</div>
                                  <div className="text-[9px] text-gray-500 font-mono">#{ls.best_draw.draw_number}{ls.best_draw_count > 1 ? ` · ×${ls.best_draw_count}` : ''}</div>
                                </div>
                              ) : <span className="text-[10px] text-gray-400">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* LS PER-DRAW DETAIL */}
            <TabsContent value="ls-detail">
              <div className="space-y-4" data-testid="ls-per-draw">
                {(data.ls_accuracy || []).map((w) => {
                  const lsKeys = ['lucky_star_1', 'lucky_star_2'];
                  return (
                    <div key={w.window_id} className="bg-white border rounded-xl p-4" data-testid={`ls-detail-${w.window_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <RankBadge rank={w.rank} />
                          <span className="font-black text-gray-900">{w.window_id}: {w.window_name}</span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-3">
                          <span>top-5: <b className="text-amber-700">{w.top5_hit_rate}%</b></span>
                          <span>top-10: <b className="text-amber-600">{w.top10_hit_rate}%</b></span>
                          <span>{(w.per_draw_detail || []).length} draws backtested</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 mb-2 italic">
                        Numbers in amber = projected top-5 (with score). <span className="inline-block w-3 h-3 rounded-full bg-emerald-500 align-middle mx-0.5"></span> green = positional hit. <span className="inline-block w-3 h-3 rounded-full bg-violet-500 align-middle mx-0.5"></span> violet = cross-position hit (LS1↔LS2). Plain = miss.
                      </p>
                      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 border border-gray-100 rounded-lg p-2">
                        {(w.per_draw_detail || []).slice().reverse().map((dd) => {
                          const rowBg = dd.hits_top5 === 2 ? 'bg-emerald-50 border-emerald-300'
                            : dd.hits_top5 === 1 ? 'bg-blue-50 border-blue-200'
                            : 'bg-red-50 border-red-200';
                          return (
                            <div key={dd.draw_number} className={`border rounded-lg p-3 ${rowBg}`} data-testid={`ls-detail-${w.window_id}-row-${dd.draw_number}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="font-mono font-black text-gray-800">#{dd.draw_number}</span>
                                  <span className="text-[10px] text-gray-500 ml-2">{dd.draw_date}</span>
                                </div>
                                <div className="text-right">
                                  <div className={`font-mono font-black text-base ${dd.hits_top5 === 2 ? 'text-emerald-600' : dd.hits_top5 === 1 ? 'text-blue-600' : 'text-red-400'}`}>{dd.hits_top5}/2</div>
                                  <div className="text-[10px] text-gray-500">top-5 · {dd.hits_top10}/2 top-10</div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {lsKeys.map((lk, li) => {
                                  const predictions = (w.top_predictions && w.top_predictions[lk]) || [];
                                  const predScores = (w.top_prediction_scores && w.top_prediction_scores[lk]) || [];
                                  const bb = (dd.ball_by_ball || []).find(b => b.ball === lk) || {};
                                  const actual = bb.actual;
                                  const actualScore = bb.score;
                                  const drawnSet = new Set(dd.actual_balls || []);
                                  const actualClasses = bb.hit_top5
                                    ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                    : bb.hit_top10
                                    ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400'
                                    : 'bg-red-100 text-red-600 line-through';
                                  return (
                                    <div key={lk} className="border border-amber-200 rounded-md p-2 bg-white/80">
                                      <div className="text-[9px] font-black text-amber-700 uppercase tracking-wider mb-1">LS{li + 1}</div>
                                      <div className="text-[8px] uppercase tracking-wider text-gray-400 font-bold">Projected top-5</div>
                                      <div className="flex flex-wrap gap-1 mt-1 mb-2">
                                        {predictions.length === 0 ? (
                                          <span className="text-[9px] text-gray-400 italic">no projection</span>
                                        ) : predictions.map((n, idx) => {
                                          const sc = predScores[idx];
                                          const isPositional = n === actual;
                                          const isCrossPos = !isPositional && drawnSet.has(n);
                                          const pillClasses = isPositional
                                            ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                            : isCrossPos
                                            ? 'bg-violet-500 text-white ring-2 ring-violet-300'
                                            : 'bg-amber-100 text-amber-900';
                                          const titleText = isPositional
                                            ? `Positional hit — LS${li + 1} drew ${n}`
                                            : isCrossPos
                                            ? `Cross-position hit — ${n} came out at the OTHER lucky-star position`
                                            : `${n} was projected but did not come out in this draw`;
                                          return (
                                            <div key={`${n}-${idx}`} className="flex flex-col items-center">
                                              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-mono font-bold text-[10px] ${pillClasses}`} title={titleText}>{n}</span>
                                              {sc != null && (
                                                <span className="text-[8px] text-gray-500 font-mono mt-0.5 leading-none">{Number(sc).toFixed(2)}</span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="text-[8px] uppercase tracking-wider text-gray-400 font-bold">Actual</div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-black text-xs ${actualClasses}`} title={bb.hit_top5 ? 'Top-5 HIT' : bb.hit_top10 ? 'Top-10 hit' : 'Miss'}>
                                          {actual}
                                        </span>
                                        {actualScore != null && (
                                          <span className="text-[10px] font-mono text-gray-700 leading-tight">
                                            <span className="text-gray-400 font-sans uppercase tracking-wider text-[8px] block">score</span>
                                            <b>{Number(actualScore).toFixed(2)}</b>
                                          </span>
                                        )}
                                        {actualScore == null && (
                                          <span className="text-[9px] text-gray-400 italic">no score</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* ================================================================
             * Alignment Audit — live drift check between accuracy.py's simplified
             * `_predict_*` predictors and each window's page-native algorithm
             * (used by W41 Summary Dashboard). Every row flags whether the two
             * agree on the current top-5 per ball position. Green = MATCH,
             * amber = same numbers different order, red = drift.
             * ================================================================ */}
            <TabsContent value="alignment">
              <AlignmentAuditPanel rows={data?.ball_accuracy || []} />
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}


/* ================================================================
 * Top2ByHitBlock — for every window × ball, surfaces the 2 predictions
 * with the highest historical hit % in the current top-10. Grouped by
 * ball so users can compare "which 2 numbers is each window best at
 * hitting for Ball K?" at a glance. Reads `w.current_top10_hits` from
 * the accuracy payload — same data the Miss Streaks cells use, just
 * distilled to 2-per-window-per-ball and laid out as ball-wise cards.
 * ================================================================ */
function Top2ByHitBlock({ rows, totalDraws, onGoTo }) {
  const BALLS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const heat = (p) =>
    p >= 20 ? 'bg-emerald-500 text-white'
    : p >= 12 ? 'bg-emerald-100 text-emerald-900'
    : p >= 6  ? 'bg-amber-100 text-amber-900'
    : p > 0   ? 'bg-rose-100 text-rose-900'
    :           'bg-gray-100 text-gray-500';

  // Build per-ball map: { bk: [ {w, top2: [{rank, number, pct, hits}]} ] }
  const perBall = {};
  BALLS.forEach((bk) => {
    perBall[bk] = rows.map(({ w }) => {
      const raw = (w.current_top10_hits || {})[bk] || [];
      const top2 = [...raw].sort((a, b) => (b.pct - a.pct) || (a.rank - b.rank)).slice(0, 2);
      return { w, top2 };
    }).filter(x => x.top2.length > 0);
  });

  // Cross-window tally per ball — how many times each number appears in
  // the top-2 of any window's per-ball predictions. Surfaces the "final"
  // strongest picks per ball as a compact summary underneath the 5 cards.
  const finalByBall = {};
  BALLS.forEach((bk) => {
    const counts = new Map();
    perBall[bk].forEach(({ top2 }) => {
      top2.forEach((h) => {
        counts.set(h.number, (counts.get(h.number) || 0) + 1);
      });
    });
    finalByBall[bk] = [...counts.entries()]
      .sort((a, b) => (b[1] - a[1]) || (a[0] - b[0]))
      .map(([n, c]) => ({ n, c }));
  });
  const finalHeat = (c) =>
    c >= 6 ? 'bg-rose-700 text-white ring-2 ring-amber-400'
    : c >= 4 ? 'bg-rose-500 text-white'
    : c >= 2 ? 'bg-emerald-500 text-white'
    :          'bg-gray-100 text-gray-700';

  return (
    <div className="bg-gradient-to-r from-fuchsia-50 via-pink-50 to-rose-50 border-2 border-fuchsia-400 rounded-xl p-5"
         data-testid="top2-by-hit-block">
      <h3 className="text-base font-black text-fuchsia-900 uppercase tracking-wider">
        🔥 Top-2 by hit % · per window × ball
      </h3>
      <p className="text-sm text-fuchsia-800 mt-1.5 mb-4">
        For each window, this block shows the <strong>2 predictions with the highest historical hit %</strong> at each ball position across the last {totalDraws} draws. These are the &quot;safest&quot; 2 picks per window per ball — regardless of the model&apos;s own P1..P10 ranking. Format: <span className="font-mono">Pn=NUMBER (%)</span>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {BALLS.map((bk, i) => (
          <div key={bk}
               className="bg-white border border-fuchsia-200 rounded-lg p-3 shadow-sm"
               data-testid={`top2-ball-card-${bk}`}>
            <div className="font-black text-fuchsia-900 uppercase text-xs mb-2 border-b border-fuchsia-100 pb-1">
              Ball {i + 1}
            </div>
            <div className="flex flex-col gap-1.5 max-h-[560px] overflow-y-auto pr-1">
              {perBall[bk].map(({ w, top2 }) => (
                <button key={w.window_id}
                        type="button"
                        onClick={() => onGoTo(w.window_num)}
                        className="text-left w-full rounded border border-gray-200 bg-gray-50 hover:bg-fuchsia-50 hover:border-fuchsia-300 transition-colors px-2 py-1.5"
                        data-testid={`top2-row-${w.window_id}-${bk}`}
                        title={`Click to open ${w.window_name}`}>
                  <div className="text-[10px] font-black text-gray-900 leading-tight truncate">
                    {w.window_id} <span className="font-normal text-gray-500 text-[9px]">{w.window_name}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1 flex-wrap">
                    {top2.map((h) => (
                      <span key={h.number}
                            className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[10px] leading-none font-black ${heat(h.pct)}`}
                            data-testid={`top2-chip-${w.window_id}-${bk}-p${h.rank}`}
                            title={`Model rank P${h.rank} · number ${h.number} · hit ${h.hits}/${totalDraws} = ${h.pct}%`}>
                        <span className="opacity-60 mr-0.5">P{h.rank}=</span>{h.number}
                        <span className="opacity-80 ml-1 text-[9px]">{h.pct}%</span>
                      </span>
                    ))}
                  </div>
                </button>
              ))}
              {perBall[bk].length === 0 && (
                <div className="text-[10px] text-gray-400 italic">No prediction data.</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ================== FINAL NUMBERS — cross-window tally ================== */}
      <div className="mt-5 pt-4 border-t-2 border-dashed border-fuchsia-300"
           data-testid="top2-final-numbers">
        <h4 className="text-sm font-black text-fuchsia-900 uppercase tracking-wider mb-1">
          🎯 Final Numbers · aggregated across all windows
        </h4>
        <p className="text-xs text-fuchsia-800 mb-3">
          Each square below is a number followed by (Nx) — how many windows placed that number in their top-2 for this ball. Higher count = stronger cross-window agreement. Numbers are sorted from most-agreed-on to least.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {BALLS.map((bk, i) => (
            <div key={bk}
                 className="bg-white/70 border border-fuchsia-200 rounded-lg p-2.5"
                 data-testid={`top2-final-ball-${bk}`}>
              <div className="font-black text-fuchsia-900 uppercase text-[11px] mb-1.5">
                Ball {i + 1}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {finalByBall[bk].length === 0 && (
                  <span className="text-[10px] text-gray-400 italic">—</span>
                )}
                {finalByBall[bk].map(({ n, c }) => (
                  <span key={n}
                        className={`inline-flex items-center rounded px-2 py-1 font-mono text-[11px] leading-none font-black ${finalHeat(c)}`}
                        data-testid={`top2-final-chip-${bk}-${n}`}
                        title={`Number ${n} appears in top-2 of ${c} window${c !== 1 ? 's' : ''} at Ball ${i + 1}`}>
                    {n}
                    <span className="ml-1 opacity-80 text-[9px] font-bold">({c}x)</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-fuchsia-900">
        <span className="font-black uppercase tracking-wider text-fuchsia-800">Row chip legend:</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-emerald-500 text-white">≥ 20%</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-emerald-100 text-emerald-900">12–19%</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-amber-100 text-amber-900">6–11%</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-rose-100 text-rose-900">1–5%</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-gray-100 text-gray-500">0%</span>
        <span className="mx-1 text-gray-400">·</span>
        <span className="font-black uppercase tracking-wider text-fuchsia-800">Final legend:</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-rose-700 text-white ring-2 ring-amber-400">6+ agree</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-rose-500 text-white">4–5</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-emerald-500 text-white">2–3</span>
        <span className="inline-block rounded px-1.5 py-0.5 font-mono font-black bg-gray-100 text-gray-700">1 window</span>
      </div>
    </div>
  );
}


/* ================================================================
 * AlignmentAuditPanel — per-window drift check between accuracy.py's
 * simplified `_predict_*` predictors and each window's page-native
 * algorithm (used by W41 Summary Dashboard). Reads `w.alignment` from
 * the backend: predictor_top5 vs endpoint_top5 per ball.
 *
 * Status per window × ball:
 *   MATCH             — same numbers in same order (green)
 *   SAME SET          — same 5 numbers, different order (amber)
 *   DIVERGE (n/5)     — n of 5 numbers overlap (red)
 *   NOT OVERRIDDEN    — endpoint doesn't cover this window (gray)
 * ================================================================ */
function AlignmentAuditPanel({ rows }) {
  const BALLS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const cell = (a, b) => {
    if (!b || b.length === 0) return { label: 'NO ENDPOINT', tone: 'bg-gray-100 text-gray-500' };
    const sa = new Set(a || []); const sb = new Set(b);
    const sameOrder = (a || []).length === b.length && (a || []).every((n, i) => n === b[i]);
    if (sameOrder) return { label: 'MATCH', tone: 'bg-emerald-500 text-white' };
    let shared = 0; sb.forEach((n) => { if (sa.has(n)) shared += 1; });
    if (shared === b.length) return { label: 'SAME SET', tone: 'bg-amber-400 text-amber-900' };
    return { label: `DIVERGE ${shared}/5`, tone: 'bg-rose-500 text-white' };
  };
  const worstFor = (row) => {
    const cells = BALLS.map((bk) => cell(row.alignment?.predictor_top5?.[bk], row.alignment?.endpoint_top5?.[bk]));
    if (cells.some((c) => c.label.startsWith('DIVERGE'))) return 3;
    if (cells.some((c) => c.label === 'SAME SET')) return 2;
    if (cells.some((c) => c.label === 'NO ENDPOINT')) return 0;
    return 1;
  };
  const sorted = [...rows].sort((a, b) => worstFor(b) - worstFor(a));
  const badgeCount = { match: 0, same_set: 0, diverge: 0, no_endpoint: 0 };
  sorted.forEach((r) => {
    BALLS.forEach((bk) => {
      const c = cell(r.alignment?.predictor_top5?.[bk], r.alignment?.endpoint_top5?.[bk]);
      if (c.label === 'MATCH') badgeCount.match += 1;
      else if (c.label === 'SAME SET') badgeCount.same_set += 1;
      else if (c.label === 'NO ENDPOINT') badgeCount.no_endpoint += 1;
      else badgeCount.diverge += 1;
    });
  });

  return (
    <div className="space-y-4" data-testid="alignment-audit-panel">
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl p-5">
        <h3 className="text-lg font-black uppercase tracking-wider">🔗 W50 ↔ W41 Alignment Audit</h3>
        <p className="text-sm text-white/90 mt-2">
          For every window in this table W50 has TWO ways to compute the current top-5 per ball:
          the simplified <strong>predictor</strong> baked into <code>accuracy.py</code> (used for backtest hit-rate math), and the page-native
          algorithm the <strong>endpoint</strong> exposes (identical to W41 Summary Dashboard). Both should agree — if they don&apos;t, W50 will show numbers the user can&apos;t find on the actual window page.
        </p>
        <div className="flex flex-wrap gap-3 mt-3 text-xs font-black">
          <span className="px-2 py-1 rounded bg-emerald-500">MATCH · {badgeCount.match}</span>
          <span className="px-2 py-1 rounded bg-amber-400 text-amber-900">SAME SET · {badgeCount.same_set}</span>
          <span className="px-2 py-1 rounded bg-rose-500">DIVERGE · {badgeCount.diverge}</span>
          <span className="px-2 py-1 rounded bg-gray-400 text-gray-900">NO ENDPOINT · {badgeCount.no_endpoint}</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-xs" data-testid="alignment-table">
          <thead>
            <tr className="bg-slate-100 text-slate-800 uppercase text-[10px] font-black">
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-100 z-10">Window</th>
              {BALLS.map((bk, i) => (
                <th key={bk} className="text-center px-2 py-2 border-l border-slate-200">Ball {i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.window_id} className="border-t border-gray-200 hover:bg-slate-50" data-testid={`alignment-row-${r.window_id}`}>
                <td className="px-3 py-2 sticky left-0 bg-inherit z-10 align-top">
                  <div className="font-black text-gray-900">{r.window_id}</div>
                  <div className="text-[10px] text-gray-500">{r.window_name}</div>
                  {!r.alignment?.endpoint_covered && (
                    <div className="text-[10px] text-gray-400 italic mt-1">no endpoint override</div>
                  )}
                </td>
                {BALLS.map((bk) => {
                  const pred = r.alignment?.predictor_top5?.[bk] || [];
                  const endp = r.alignment?.endpoint_top5?.[bk] || [];
                  const c = cell(pred, endp);
                  return (
                    <td key={bk} className="px-2 py-2 border-l border-gray-200 align-top" data-testid={`alignment-cell-${r.window_id}-${bk}`}>
                      <div className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase leading-none ${c.tone}`}>
                        {c.label}
                      </div>
                      <div className="mt-1 text-[10px] font-mono">
                        <div className="text-slate-500">pred: <span className="text-slate-900">{pred.length ? pred.join(', ') : '—'}</span></div>
                        <div className="text-slate-500">endp: <span className="text-slate-900">{endp.length ? endp.join(', ') : '—'}</span></div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-500 italic px-1">
        Displayed numbers come from the same backend request as the rest of W50 — no separate API call. If a window drifts (endpoint algorithm changes or a new window forgets its override), the row will jump to the top of this table.
      </div>
    </div>
  );
}


/* ================================================================
 * MissStreaksPanel — a dedicated tab that surfaces "cold" windows
 * per ball position. For each (Window, Ball, K∈{5,10}), it computes
 * the number of consecutive recent draws in which the window's
 * projected Top-K MISSED the actual number at that ball position.
 *
 * The premise (per user's request): if a window has missed a ball
 * position 3+ times in a row — especially for balls 2/3/4 where
 * every window is historically weaker — reversion makes the next
 * hit from that window disproportionately likely.
 * ================================================================ */
/* ================================================================
 * BUCKET DIAGNOSTIC PANEL
 * ----------------------------------------------------------------
 * Splits each frozen prediction into 4 rank-slice buckets:
 *   T5   = P1..P5   (top-5)
 *   T10  = P6..P10
 *   T15  = P11..P15
 *   T20  = P16..P20
 * Shows the hit % per bucket over the frozen test window for every
 * (window × ball) cell. The bucket with the highest hit % gets a ★.
 *
 * Each row also carries a stability tag derived from a H1/H2 split of
 * the test window on the backend:
 *   🟢 green — the same bucket won in both halves (real signal)
 *   🟡 amber — halves disagree but the full-window winner matches one
 *   ⚪ gray  — winner unstable across halves (treat with caution)
 * ================================================================ */
function BucketDiagTableScroller({ children }) {
  const topRef = React.useRef(null);
  const bottomRef = React.useRef(null);
  const [contentWidth, setContentWidth] = React.useState(0);
  const syncingRef = React.useRef(false);

  // Measure the table width so the top scrollbar's dummy child matches it.
  React.useEffect(() => {
    const el = bottomRef.current;
    if (!el) return undefined;
    const measure = () => {
      const tbl = el.querySelector('table');
      if (tbl) setContentWidth(tbl.scrollWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const tbl = el.querySelector('table');
    if (tbl) ro.observe(tbl);
    return () => ro.disconnect();
  }, [children]);

  const onTopScroll = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (bottomRef.current && topRef.current) {
      bottomRef.current.scrollLeft = topRef.current.scrollLeft;
    }
    syncingRef.current = false;
  };
  const onBottomScroll = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (topRef.current && bottomRef.current) {
      topRef.current.scrollLeft = bottomRef.current.scrollLeft;
    }
    syncingRef.current = false;
  };

  return (
    <>
      {/* Top-mirror horizontal scrollbar — width matches the table so scrolling here scrolls the real table below. */}
      <div
        ref={topRef}
        onScroll={onTopScroll}
        className="overflow-x-auto overflow-y-hidden"
        style={{ height: 16 }}
        data-testid="bucket-diag-top-scroll"
      >
        <div style={{ width: contentWidth || '100%', height: 1 }} />
      </div>
      {/* Actual table scroll container */}
      <div
        ref={bottomRef}
        onScroll={onBottomScroll}
        className="overflow-x-auto"
        data-testid="bucket-diag-bottom-scroll"
      >
        {children}
      </div>
    </>
  );
}

/**
 * Small helper — the backend emits both a "current" (default) and a
 * "frozen" backtest-ranking view of each bucket diagnostic. This picks the
 * requested one, falling back to the current view if the frozen sibling
 * is missing (older payloads, LS windows).
 */
function pickDiag(bd, source) {
  if (!bd) return bd;
  if (source === 'frozen') return bd.frozen || bd;
  return bd;
}

/**
 * AllWindowsBucketMatrix
 * One consolidated table, matching the requested format: every window as a
 * row, with Ball 1-5 each split into T5/T10/T15/T20 hit-rate % sub-columns
 * (pulled straight from each window's existing `bucket_diagnostic`, same
 * source as the Bucket Diagnostic tab — just laid out as a single matrix
 * instead of per-window cards).
 */
function AllWindowsBucketMatrix({ windows }) {
  const bucketLabels = ['T5', 'T10', 'T15', 'T20'];
  const rows = windows || [];
  if (rows.length === 0) return null;
  const cellCls = "px-2 py-1.5 text-center font-mono text-[12px] border-l border-gray-100";

  const handleExportExcel = () => {
    const header1 = ['S.No.', 'Window', ...BALL_KEYS.flatMap((_, bi) => [`Ball ${bi + 1}`, '', '', ''])];
    const header2 = ['', '', ...BALL_KEYS.flatMap(() => bucketLabels)];
    const body = rows.map((w, idx) => {
      const bd = w.bucket_diagnostic || {};
      const cells = BALL_KEYS.flatMap((bk) => {
        const diag = pickDiag(bd[bk], 'current');
        const predCount = (diag && diag.pred_count != null) ? diag.pred_count : 50;
        return bucketLabels.map((label) => {
          const lo = { T5: 1, T10: 6, T15: 11, T20: 16 }[label];
          if (predCount < lo) return 'Empty';
          const pct = diag?.buckets?.[label]?.pct;
          return pct != null ? pct : '';
        });
      });
      return [idx + 1, `${w.window_id} ${w.window_name}`, ...cells];
    });
    const ws = XLSX.utils.aoa_to_sheet([header1, header2, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bucket % Matrix');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_AllWindows_BucketMatrix_${today}.xlsx`);
    toast.success('Downloaded bucket % matrix workbook');
  };

  return (
    <div className="bg-white border rounded-xl overflow-hidden mb-5" data-testid="all-windows-bucket-matrix">
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="text-sm font-bold text-gray-700">% Accuracy per Bucket (T5/T10/T15/T20) — All Windows</span>
          <span className="block text-[11px] text-gray-400">Same source as 🪣 BD tab · rolling, current ranking · <span className="inline-block px-1 rounded bg-rose-100 text-rose-700 font-bold">red</span> = below 10%</span>
        </div>
        <button
          type="button"
          onClick={handleExportExcel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 font-bold text-xs uppercase transition"
          data-testid="all-windows-bucket-matrix-export"
        >
          <DownloadSimple size={14} weight="bold" />
          Download Excel
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="all-windows-bucket-matrix-table">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-center w-12 border-b border-gray-200" rowSpan={2}>S.No.</th>
              <th className="px-3 py-2 text-left border-b border-gray-200" rowSpan={2}>Window</th>
              {BALL_KEYS.map((bk, bi) => (
                <th key={bk} className="px-2 py-1.5 text-center border-b border-l border-gray-200" colSpan={4}>Ball {bi + 1}</th>
              ))}
            </tr>
            <tr>
              {BALL_KEYS.map((bk) => bucketLabels.map((label) => (
                <th key={`${bk}-${label}`} className="px-2 py-1.5 text-center border-b border-l border-gray-200 text-gray-600">{label}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {rows.map((w, idx) => {
              const bd = w.bucket_diagnostic || {};
              return (
                <tr key={w.window_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`bucket-matrix-row-${w.window_id}`}>
                  <td className="px-3 py-1.5 text-center text-gray-500">{idx + 1}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <span className="font-bold text-gray-900">{w.window_id}</span>
                    <span className="text-gray-500 ml-1.5">{w.window_name}</span>
                  </td>
                  {BALL_KEYS.map((bk) => {
                    const diag = pickDiag(bd[bk], 'current');
                    const predCount = (diag && diag.pred_count != null) ? diag.pred_count : 50;
                    return bucketLabels.map((label) => {
                      const lo = { T5: 1, T10: 6, T15: 11, T20: 16 }[label];
                      const bucketEmpty = predCount < lo;
                      const pct = diag?.buckets?.[label]?.pct;
                      const low = pct != null && pct < 10;
                      return (
                        <td
                          key={`${w.window_id}-${bk}-${label}`}
                          className={`${cellCls} ${bucketEmpty ? 'text-slate-400 italic' : (low ? 'bg-rose-100 text-rose-700 font-bold' : '')}`}
                          data-testid={`bucket-matrix-${w.window_id}-${bk}-${label}`}
                        >
                          {bucketEmpty ? 'Empty' : (pct != null ? `${pct}%` : '—')}
                        </td>
                      );
                    });
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BucketDiagnosticPanel({ data }) {
  const [sortKey, setSortKey] = React.useState('rank');
  const [stabilityFilter, setStabilityFilter] = React.useState('all'); // all | green | amber | gray
  const [rankSource, setRankSource] = React.useState('current'); // 'current' | 'frozen'

  const testWindow = data?.test_draws_count || 0;
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const bucketDefs = [
    { label: 'T5',  range: 'P1-5',   color: 'from-emerald-500 to-emerald-600',  soft: 'bg-emerald-50',  text: 'text-emerald-900' },
    { label: 'T10', range: 'P6-10',  color: 'from-sky-500 to-sky-600',          soft: 'bg-sky-50',      text: 'text-sky-900' },
    { label: 'T15', range: 'P11-15', color: 'from-violet-500 to-violet-600',    soft: 'bg-violet-50',   text: 'text-violet-900' },
    { label: 'T20', range: 'P16-20', color: 'from-amber-500 to-amber-600',      soft: 'bg-amber-50',    text: 'text-amber-900' },
  ];

  const stabilityMeta = {
    green: { icon: '🟢', label: 'stable',   text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-300', tip: 'Same bucket wins both halves of the test window — real signal.' },
    amber: { icon: '🟡', label: 'caution',  text: 'text-amber-700',   bg: 'bg-amber-50 border-amber-300',     tip: 'Halves disagree but the full-window winner matches one half — treat with caution.' },
    gray:  { icon: '⚪', label: 'noise',    text: 'text-slate-600',   bg: 'bg-slate-50 border-slate-300',     tip: 'Winner unstable across halves — small-sample noise, not a reliable signal.' },
  };

  const rows = useMemo(() => {
    return (data?.ball_accuracy || []).slice();
  }, [data]);

  const filteredRows = useMemo(() => {
    let list = rows.slice();
    if (stabilityFilter !== 'all') {
      list = list.filter((w) => {
        const bd = w.bucket_diagnostic || {};
        return ballKeys.some((bk) => (pickDiag(bd[bk], rankSource) || {}).stability === stabilityFilter);
      });
    }
    if (sortKey === 'best_pct') {
      list.sort((a, b) => {
        const av = Math.max(...ballKeys.map((bk) => (pickDiag(a.bucket_diagnostic?.[bk], rankSource)?.best_bucket_pct || 0)));
        const bv = Math.max(...ballKeys.map((bk) => (pickDiag(b.bucket_diagnostic?.[bk], rankSource)?.best_bucket_pct || 0)));
        return bv - av;
      });
    }
    return list;
  }, [rows, sortKey, stabilityFilter, ballKeys]);

  // Aggregate stability counts for the header chip.
  const stabilitySummary = useMemo(() => {
    const counts = { green: 0, amber: 0, gray: 0 };
    (data?.ball_accuracy || []).forEach((w) => {
      const bd = w.bucket_diagnostic || {};
      ballKeys.forEach((bk) => {
        const s = pickDiag(bd[bk], rankSource)?.stability;
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
    });
    return counts;
  }, [data, ballKeys, rankSource]);

  const barMax = 45; // scale bars to this % ceiling for consistent visuals

  const handleExportExcel = () => {
    const bucketRows = [];
    filteredRows.forEach((w) => {
      const bd = w.bucket_diagnostic || {};
      const hasBd = ballKeys.some((bk) => pickDiag(bd[bk], rankSource)?.buckets);
      if (!hasBd) return;
      const overall = { green: 0, amber: 0, gray: 0 };
      ballKeys.forEach((bk) => { const s = pickDiag(bd[bk], rankSource)?.stability; if (s) overall[s] += 1; });
      const dominantStab = overall.green >= overall.amber && overall.green >= overall.gray
        ? 'green' : (overall.amber >= overall.gray ? 'amber' : 'gray');

      ballKeys.forEach((bk, ballIdx) => {
        const b = pickDiag(bd[bk], rankSource);
        if (!b) return;
        const bmStreaks = b.bucket_miss_streaks || {};
        const perP = b.per_p || {};
        const top20 = b.current_top20 || [];
        const predCount = (b.pred_count != null) ? b.pred_count : 50;
        bucketDefs.forEach((def) => {
          const bucketRange = def.range.replace('P', '').split('-').map(Number);
          const bucketEmpty = predCount < bucketRange[0];
          if (bucketEmpty) {
            bucketRows.push({
              'Window': `${w.window_id} ${w.window_name}`,
              'Rank': w.rank,
              'Ball': `Ball ${ballIdx + 1}`,
              'Bucket': def.label,
              'Bucket Range': def.range,
              'Hit %': 'Empty',
              'Best Bucket for Ball?': '',
              'Verdict': 'Empty',
              'Current Miss-Streak': '',
              'Expected Gap': '',
              'Stability (overall)': '',
              'P1': '', 'P2': '', 'P3': '', 'P4': '', 'P5': '',
            });
            return;
          }
          const cell = (b.buckets || {})[def.label] || { hits: 0, pct: 0 };
          const isBest = def.label === b.best_bucket;
          const streak = bmStreaks[def.label] ?? 0;
          const verdict = computeVerdict(streak, cell.pct);
          const pRows = [];
          for (let p = bucketRange[0]; p <= Math.min(bucketRange[1], predCount); p++) {
            const n = top20[p - 1];
            if (n == null) continue;
            pRows.push({ p, n, pct: perP[String(p)]?.pct || 0 });
          }
          bucketRows.push({
            'Window': `${w.window_id} ${w.window_name}`,
            'Rank': w.rank,
            'Ball': `Ball ${ballIdx + 1}`,
            'Bucket': def.label,
            'Bucket Range': def.range,
            'Hit %': Math.round(cell.pct || 0),
            'Best Bucket for Ball?': isBest ? 'Y' : '',
            'Verdict': formatVerdict(verdict),
            'Current Miss-Streak': streak,
            'Expected Gap': verdict.gap ?? '',
            'Stability (overall)': `${stabilityMeta[dominantStab].label} (${overall.green}🟢/${overall.amber}🟡/${overall.gray}⚪)`,
            // 5 separate columns (one per slot in this bucket's range, in
            // natural ascending P order) -- each shows ONLY the projected
            // number for that slot (no "P=", no "%").
            'P1': pRows[0]?.n ?? '',
            'P2': pRows[1]?.n ?? '',
            'P3': pRows[2]?.n ?? '',
            'P4': pRows[3]?.n ?? '',
            'P5': pRows[4]?.n ?? '',
          });
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(bucketRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BD');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_BucketDiagnostic_${rankSource}_${today}.xlsx`);
    toast.success('Downloaded BD workbook');
  };

  return (
    <div className="space-y-4" data-testid="w50-bucket-diagnostic">
      {/* Header + legend */}
      <div className="rounded-xl border-2 border-fuchsia-300 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-fuchsia-700 via-fuchsia-600 to-purple-600 text-white flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-[280px]">
            <h3 className="text-base font-black uppercase tracking-wider" data-testid="bd-title">🪣 BD — T5 / T10 / T15 / T20 per Window × Ball</h3>
            <p className="text-[11px] text-white/85 italic leading-snug mt-1">
              Excel-style wide table — one row per window, all 5 balls horizontal (Ball 1..5, each split into T5/T10/T15/T20 sub-columns). Each cell stacks: bucket header <b>T5 (48%)</b>, verdict (<em>Exp 1 (Due Next)</em> / <em>Fresh Hit (Exp. 6)</em>), then P rows in natural P1→P20 order (same sequence as the window's own prediction page) — format <code>P1 = 2 = 14%</code>, with the single best-hit-rate slot ringed. Scroll horizontally if needed. ★ = best bucket per ball. Stability from H1 vs H2 halves ({testWindow} test draws).
            </p>
          </div>
          <div className="flex gap-2 text-[11px]">
            <span className="rounded-full bg-emerald-500/25 border border-emerald-200/60 px-2 py-1 font-mono">
              🟢 {stabilitySummary.green} stable
            </span>
            <span className="rounded-full bg-amber-500/30 border border-amber-200/60 px-2 py-1 font-mono">
              🟡 {stabilitySummary.amber} caution
            </span>
            <span className="rounded-full bg-slate-500/30 border border-slate-200/60 px-2 py-1 font-mono">
              ⚪ {stabilitySummary.gray} noise
            </span>
          </div>
        </div>

        {/* Legend + controls */}
        <div className="px-4 py-3 border-b border-fuchsia-100 bg-fuchsia-50/50 flex flex-wrap items-center gap-3 text-[11px]">
          <span className="font-black uppercase tracking-wider text-fuchsia-800 mr-1">Buckets:</span>
          {bucketDefs.map((b) => (
            <span key={b.label} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-4 h-4 rounded bg-gradient-to-br ${b.color}`} />
              <span className="font-mono font-bold text-slate-700">{b.label}</span>
              <span className="text-slate-500">({b.range})</span>
            </span>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* Ranking source toggle — flips between current (default) and
                frozen backtest snapshot. Frozen shows historical rank
                interpretation for users who want the original meaning. */}
            <span className="text-slate-500">Ranking:</span>
            <div className="inline-flex rounded overflow-hidden border border-fuchsia-300" role="tablist" data-testid="bucket-ranking-toggle">
              <button
                onClick={() => setRankSource('current')}
                className={`px-2 py-1 font-mono ${rankSource === 'current' ? 'bg-fuchsia-600 text-white' : 'bg-white text-slate-700 hover:bg-fuchsia-50'}`}
                data-testid="bucket-rank-current"
                title="Current prediction rank — matches Miss Streaks & individual window pages"
              >
                Current
              </button>
              <button
                onClick={() => setRankSource('frozen')}
                className={`px-2 py-1 font-mono border-l border-fuchsia-300 ${rankSource === 'frozen' ? 'bg-fuchsia-600 text-white' : 'bg-white text-slate-700 hover:bg-fuchsia-50'}`}
                data-testid="bucket-rank-frozen"
                title="Frozen backtest ranking — historical rank interpretation (top_predictions)"
              >
                Frozen ❄️
              </button>
            </div>
            <span className="text-slate-500 ml-2">Filter:</span>
            {['all', 'green', 'amber', 'gray'].map((f) => (
              <button
                key={f}
                onClick={() => setStabilityFilter(f)}
                className={`px-2 py-1 rounded font-mono border ${stabilityFilter === f ? 'bg-fuchsia-600 text-white border-fuchsia-700' : 'bg-white text-slate-700 border-slate-300 hover:border-fuchsia-400'}`}
                data-testid={`bucket-filter-${f}`}
              >
                {f === 'all' ? 'All' : `${stabilityMeta[f].icon} ${stabilityMeta[f].label}`}
              </button>
            ))}
            <span className="ml-2 text-slate-500">Sort:</span>
            <button
              onClick={() => setSortKey(sortKey === 'rank' ? 'best_pct' : 'rank')}
              className="px-2 py-1 rounded font-mono border border-slate-300 bg-white text-slate-700 hover:border-fuchsia-400"
              data-testid="bucket-sort"
            >
              {sortKey === 'rank' ? 'Rank (default)' : 'Highest bucket %'}
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded font-mono bg-fuchsia-700 text-white hover:bg-fuchsia-800 font-bold"
              data-testid="bucket-diag-export"
            >
              <DownloadSimple size={14} weight="bold" /> Download Excel
            </button>
          </div>
        </div>
      </div>

      {/* Data table — Excel-style wide layout: 1 row per window, 5 balls × 4 buckets = 20 sub-columns */}
      <div className="rounded-xl border border-fuchsia-200 bg-white shadow-sm overflow-hidden">
        <BucketDiagTableScroller>
          <table className="w-full text-[11px] border-collapse" data-testid="bucket-diag-table">
            <thead className="sticky top-0 z-10 bg-fuchsia-50">
              <tr>
                <th rowSpan={2} className="sticky left-0 z-20 bg-fuchsia-100 border border-fuchsia-200 px-2 py-1.5 text-left font-black text-[11px] text-fuchsia-900 uppercase tracking-wider min-w-[190px]">
                  Window
                </th>
                {ballKeys.map((bk, i) => (
                  <th key={bk} colSpan={4} className={`border border-fuchsia-200 bg-fuchsia-100 px-2 py-1.5 text-center font-black text-[11px] text-fuchsia-900 uppercase tracking-wider ${i < ballKeys.length - 1 ? 'border-r-4 border-r-slate-800' : ''}`}>
                    Ball {i + 1}
                  </th>
                ))}
                <th rowSpan={2} className="border border-fuchsia-200 bg-fuchsia-100 px-2 py-1.5 text-center font-black text-[11px] text-fuchsia-900 uppercase tracking-wider min-w-[110px]">
                  Stability
                </th>
              </tr>
              <tr>
                {ballKeys.flatMap((bk, bi) => bucketDefs.map((def, di) => (
                  <th key={`${bk}-${def.label}`} className={`border border-fuchsia-200 px-1 py-1 text-center font-mono text-[10px] font-black ${def.text} ${def.soft} ${di === bucketDefs.length - 1 && bi < ballKeys.length - 1 ? 'border-r-4 border-r-slate-800' : ''}`}>
                    {def.label}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((w, wIdx) => {
                const bd = w.bucket_diagnostic || {};
                const hasBd = ballKeys.some((bk) => pickDiag(bd[bk], rankSource)?.buckets);
                if (!hasBd) return null;
                const overall = { green: 0, amber: 0, gray: 0 };
                ballKeys.forEach((bk) => { const s = pickDiag(bd[bk], rankSource)?.stability; if (s) overall[s] += 1; });
                const dominantStab = overall.green >= overall.amber && overall.green >= overall.gray
                  ? 'green' : (overall.amber >= overall.gray ? 'amber' : 'gray');
                const rowBg = wIdx % 2 === 0 ? 'bg-white' : 'bg-fuchsia-50/20';
                // The STICKY window-name column needs a fully OPAQUE
                // background (not the translucent row-stripe above) —
                // otherwise, when the table is scrolled horizontally, the
                // non-sticky cells behind it show through and visually
                // overlap/garble the sticky column's text.
                const rowBgSolid = wIdx % 2 === 0 ? 'bg-white' : 'bg-fuchsia-50';
                const pHitCls = (p) =>
                  p >= 20 ? 'bg-emerald-500 text-white'
                  : p >= 10 ? 'bg-emerald-100 text-emerald-900'
                  : p > 0   ? 'bg-amber-100 text-amber-900'
                  :           'bg-gray-100 text-gray-500';

                return (
                  <tr key={w.window_id} className={rowBg} data-testid={`bucket-window-${w.window_id}`}>
                    {/* Window name column — sticky left */}
                    <td className={`sticky left-0 z-10 border border-fuchsia-100 px-2 py-1.5 align-top ${rowBgSolid}`}>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center rounded bg-fuchsia-700 text-white font-black text-[10px] px-1.5 py-0.5">{w.window_id}</span>
                          <span className="text-[10px] text-slate-500 font-mono">#{w.rank}</span>
                        </div>
                        <div className="font-bold text-slate-800 text-[11px] leading-tight">{w.window_name}</div>
                        <div className="text-[9px] text-slate-500 font-mono">
                          T5 <b className="text-slate-700">{Math.round(w.top5_hit_rate)}%</b> · T10 <b className="text-slate-700">{Math.round(w.top10_hit_rate)}%</b>
                        </div>
                        <div className={`text-[9px] font-mono ${stabilityMeta[dominantStab].text}`} title={stabilityMeta[dominantStab].tip}>
                          {stabilityMeta[dominantStab].icon} {overall.green}🟢·{overall.amber}🟡·{overall.gray}⚪
                        </div>
                      </div>
                    </td>

                    {/* 5 balls × 4 buckets = 20 sub-columns */}
                    {ballKeys.map((bk, ballIdx) => {
                      const b = pickDiag(bd[bk], rankSource);
                      const bmStreaks = (b && b.bucket_miss_streaks) || {};
                      const perP = (b && b.per_p) || {};
                      const top20 = (b && b.current_top20) || [];
                      const predCount = (b && b.pred_count != null) ? b.pred_count : 50;
                      return bucketDefs.map((def, bucketIdx) => {
                        const isLastBucketOfBall = bucketIdx === bucketDefs.length - 1;
                        const isLastBall = ballIdx === ballKeys.length - 1;
                        const dividerCls = isLastBucketOfBall && !isLastBall ? 'border-r-4 border-r-slate-800' : '';
                        if (!b) {
                          return <td key={`${bk}-${def.label}`} className={`border border-fuchsia-100 px-1 py-1 text-center text-[9px] text-gray-400 ${dividerCls}`}>—</td>;
                        }
                        const bucketRange = def.range.replace('P', '').split('-').map(Number);
                        const bucketEmpty = predCount < bucketRange[0];
                        if (bucketEmpty) {
                          return (
                            <td
                              key={`${bk}-${def.label}`}
                              className={`border border-fuchsia-100 px-1 py-1 text-center align-middle ${dividerCls}`}
                              data-testid={`bucket-cell-${w.window_id}-${bk}-${def.label}`}
                              title={`${def.range} — no predictions currently (only ${predCount} number${predCount === 1 ? '' : 's'} projected for this window/ball)`}
                            >
                              <div className="text-[9px] font-mono italic text-slate-400" data-testid={`bucket-empty-${w.window_id}-${bk}-${def.label}`}>Empty</div>
                            </td>
                          );
                        }
                        const cell = (b.buckets || {})[def.label] || { hits: 0, pct: 0 };
                        const isBest = def.label === b.best_bucket;
                        const streak = bmStreaks[def.label] ?? 0;
                        const verdict = computeVerdict(streak, cell.pct);
                        const bucketPct = Math.round(cell.pct || 0);
                        const pRows = [];
                        for (let p = bucketRange[0]; p <= Math.min(bucketRange[1], predCount); p++) {
                          const n = top20[p - 1];
                          if (n == null) continue;
                          pRows.push({ p, n, pct: perP[String(p)]?.pct || 0, hits: perP[String(p)]?.hits || 0 });
                        }
                        // Display in NATURAL P1→P20 order (matches the number
                        // sequence shown on the window's own standalone page
                        // exactly) — previously sorted by hit% descending,
                        // which visually scrambled the order (e.g. P5 listed
                        // before P1) and looked like a mismatch even though
                        // every P-slot's assigned number was always correct.
                        const pRowsSorted = [...pRows].sort((a, b2) => a.p - b2.p);
                        const topP = [...pRows].sort((a, b2) => b2.pct - a.pct || a.p - b2.p)[0];
                        return (
                          <td
                            key={`${bk}-${def.label}`}
                            className={`border border-fuchsia-100 align-top ${isBest ? 'ring-1 ring-fuchsia-400 ring-inset' : ''} ${def.soft} p-1 ${dividerCls}`}
                            data-testid={`bucket-cell-${w.window_id}-${bk}-${def.label}`}
                            title={`${def.range} — ${cell.hits}/${testWindow} draws (${bucketPct}%) · streak ${streak} · exp gap ${verdict.gap ?? '—'} · ${verdict.label}`}
                          >
                            <div className="flex flex-col gap-0.5">
                              {/* Bucket header line: T5 (48%) */}
                              <div className={`text-center text-[10px] font-black font-mono ${def.text} leading-tight`}
                                   data-testid={`bucket-header-${w.window_id}-${bk}-${def.label}`}>
                                {def.label} ({bucketPct}%){isBest && <span className="ml-0.5 text-fuchsia-600">★</span>}
                              </div>
                              {/* Verdict line */}
                              <div className={`text-center rounded px-1 py-0.5 text-[8px] font-bold leading-tight ${verdict.badge}`}
                                   data-testid={`bucket-verdict-${w.window_id}-${bk}-${def.label}`}>
                                {formatVerdict(verdict)}
                              </div>
                              {/* P chips vertical stack */}
                              <div className="flex flex-col gap-0.5" data-testid={`bucket-plist-${w.window_id}-${bk}-${def.label}`}>
                                {pRowsSorted.map((x) => {
                                  const pctRounded = Math.round(x.pct);
                                  return (
                                    <div key={x.p}
                                         className={`flex items-center justify-between rounded px-1 py-0.5 text-[9px] leading-none font-mono ${pHitCls(x.pct)} ${topP && x.p === topP.p ? 'ring-1 ring-fuchsia-500' : ''}`}
                                         title={`P${x.p} · #${x.n} · ${x.hits}/${testWindow} = ${pctRounded}%`}
                                         data-testid={`bucket-p-${w.window_id}-${bk}-${def.label}-p${x.p}`}>
                                      <span className="opacity-70 font-bold">P{x.p}</span>
                                      <span className="opacity-60 mx-0.5">=</span>
                                      <span className="font-black">{x.n}</span>
                                      <span className="opacity-60 mx-0.5">=</span>
                                      <span className="font-bold">{pctRounded}%</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        );
                      });
                    })}

                    {/* Stability tag column */}
                    <td className={`border border-fuchsia-100 px-2 py-1.5 align-middle text-center ${stabilityMeta[dominantStab].bg}`}>
                      <div className={`text-[10px] font-mono ${stabilityMeta[dominantStab].text}`} title={stabilityMeta[dominantStab].tip}>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-sm">{stabilityMeta[dominantStab].icon}</span>
                          <span className="font-black uppercase tracking-wider">{stabilityMeta[dominantStab].label}</span>
                        </div>
                        <div className="text-[9px] opacity-70 mt-0.5">
                          {overall.green}🟢/{overall.amber}🟡/{overall.gray}⚪
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </BucketDiagTableScroller>
      </div>

      {/* Legend footer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] text-slate-600 leading-relaxed">
        <div><b>How to read this:</b> Each bucket header is <code>T5 (48%)</code> — bucket label + hit-rate % (rounded) on the last {testWindow} test draws. Verdict badge below combines current miss streak vs expected gap: <em>Fresh Hit</em> (just hit) / <em>Within Norm</em> / <em>Exp N (Due Next)</em> / <em>Overdue</em> / <em>Deep Overdue (+K)</em>.</div>
        <div className="mt-1"><b>P list below each bucket:</b> the specific P-ranks sitting inside that bucket, sorted by <em>historical hit %</em> desc so the strongest P bubbles to the top (marked with a fuchsia ring). Format: <code>P{'{'}rank{'}'} = {'{'}number{'}'} = {'{'}hit %{'}'}</code>. If P8 has hit more often than P1, P8 appears higher.</div>
        <div className="mt-1"><b>Recommended action:</b> If a green (🟢) row&apos;s best bucket is not T5 AND that bucket shows <em>Due Next</em> / <em>Overdue</em>, consider using its top-P number(s) instead of the naive T5 pick. Amber (🟡) = real spike, winner may drift. Gray (⚪) = noise — stick with T5.</div>
      </div>

      {/* Strong-Pick Summary — for each ball, count (window × P-slot) hits where hit% >= threshold */}
      <StrongPickSummary rows={filteredRows} ballKeys={ballKeys} rankSource={rankSource} />

      {/* Green-Pick Totals — for each ball, sum the hit% of every "green"
          (≥10%, same threshold as the emerald P-chip coloring in the main
          table) (window × P-slot) occurrence, grouped by number. Surfaces
          numbers that are a strong (green) pick across MULTIPLE windows,
          ranked by combined %. Sits directly above Low-Hit Predictions so
          the strongest and weakest picks bookend the summary panels. */}
      <GreenPickTotals rows={filteredRows} ballKeys={ballKeys} rankSource={rankSource} />

      {/* Low-Hit Predictions — for each ball, list numbers currently at
          ≤ 3% across (window × P-slot) pairs. Complements Strong-Pick. */}
      <ZeroHitPicks rows={filteredRows} ballKeys={ballKeys} rankSource={rankSource} />

      {/* Overdue / Deep Overdue Pool — per-number pile-up of Due/Overdue/
          Deep-Overdue P-slot occurrences (with sum-of-% conviction score
          and best-single-occurrence pointer). Sits at the bottom of the tab. */}
      <OverdueDuePool rows={filteredRows} ballKeys={ballKeys} rankSource={rankSource} />

      {/* Freq ∩ Strong-Pick — frequency-ranked candidates filtered by the
          Strong-Pick top-10, with a walk-forward hit-rate audit. Last block. */}
      <FreqStrongPickPanel rows={filteredRows} ballKeys={ballKeys} rankSource={rankSource} />
    </div>
  );
}

function GreenPickTotals({ rows, ballKeys, rankSource = 'current' }) {
  // Threshold: a (window × P-slot) occurrence counts as "green" when its
  // hit% is ≥ this value — the SAME threshold used by pHitCls() for the
  // emerald P-chip coloring in the main Bucket Diagnostic table above.
  const THRESHOLD = 10;

  // Same T5/T10/T15/T20 bucket ranges used by OverdueDuePool — reused here so
  // a number's "appeared in Overdue / Deep Overdue in T5..T20" tag matches
  // exactly what that panel (and the main table's verdict badges) would show.
  const bucketDefs = [
    { label: 'T5',  lo: 1,  hi: 5  },
    { label: 'T10', lo: 6,  hi: 10 },
    { label: 'T15', lo: 11, hi: 15 },
    { label: 'T20', lo: 16, hi: 20 },
  ];

  const summary = useMemo(() => {
    // per ball → { number → [{window_id, p_rank, pct}] } for slots where pct ≥ THRESHOLD
    const out = {};
    // per ball → Map(number → { overdue: [...], deep: [...] }) — every (window × bucket)
    // whose verdict is OVERDUE/DEEP OVERDUE, tagged onto every number sitting inside
    // that bucket's P-range (same logic as OverdueDuePool, not limited to green numbers).
    const overdueOut = {};
    ballKeys.forEach((bk) => { out[bk] = {}; overdueOut[bk] = new Map(); });
    rows.forEach((w) => {
      const bd = w.bucket_diagnostic || {};
      ballKeys.forEach((bk) => {
        const b = pickDiag(bd[bk], rankSource);
        if (!b) return;
        const top20 = b.current_top20 || [];
        const perP = b.per_p || {};
        const predCount = (b.pred_count != null) ? b.pred_count : 50;
        for (let p = 1; p <= Math.min(20, predCount); p++) {
          const n = top20[p - 1];
          if (n == null) continue;
          const raw = perP[String(p)]?.pct || 0;
          const pctRounded = Math.round(raw);
          if (pctRounded >= THRESHOLD) {
            if (!out[bk][n]) out[bk][n] = [];
            out[bk][n].push({ window_id: w.window_id, p_rank: p, pct: pctRounded });
          }
        }

        const buckets = b.buckets || {};
        const streaks = b.bucket_miss_streaks || {};
        bucketDefs.forEach(({ label, lo, hi }) => {
          const bucketPct = buckets[label]?.pct ?? 0;
          const streak = streaks[label] ?? 0;
          const v = computeVerdict(streak, bucketPct);
          if (!['over', 'deep'].includes(v.tier)) return;
          for (let p = lo; p <= Math.min(hi, predCount); p++) {
            const n = top20[p - 1];
            if (n == null) continue;
            const pct = Math.round(perP[String(p)]?.pct || 0);
            if (pct <= 0) continue;
            if (!overdueOut[bk].has(n)) overdueOut[bk].set(n, { overdue: [], deep: [] });
            const rec = overdueOut[bk].get(n);
            const occ = { window_id: w.window_id, p_rank: p, pct, bucket: label };
            (v.tier === 'over' ? rec.overdue : rec.deep).push(occ);
          }
        });
      });
    });
    const result = {};
    ballKeys.forEach((bk) => {
      result[bk] = Object.entries(out[bk])
        .map(([n, occ]) => {
          const total = occ.reduce((s, o) => s + o.pct, 0);
          const numKey = parseInt(n, 10);
          const od = overdueOut[bk].get(numKey) || { overdue: [], deep: [] };
          const overdueCount = od.overdue.length;
          const overdueSum = od.overdue.reduce((s, o) => s + o.pct, 0);
          const deepCount = od.deep.length;
          const deepSum = od.deep.reduce((s, o) => s + o.pct, 0);
          return {
            number: numKey, count: occ.length, total, occurrences: occ,
            overdueCount, overdueSum, overdueOccs: od.overdue,
            deepCount, deepSum, deepOccs: od.deep,
          };
        })
        // Descending by combined green % — strongest consensus number first
        .sort((a, b) => b.total - a.total || b.count - a.count || a.number - b.number);
    });
    return result;
  }, [rows, ballKeys, rankSource]);

  const ballColors = {
    ball_1: 'from-emerald-700 to-emerald-800',
    ball_2: 'from-teal-700 to-teal-800',
    ball_3: 'from-green-700 to-green-800',
    ball_4: 'from-emerald-600 to-emerald-700',
    ball_5: 'from-teal-600 to-teal-700',
  };

  const handleExportBallExcel = (bk, bi) => {
    const list = summary[bk] || [];
    const wb = XLSX.utils.book_new();
    // Sheet 1: Summary — one row per number (ranked by total % desc)
    const summaryRows = list.map((entry, idx) => ({
      'Rank': idx + 1,
      'Number': entry.number,
      'Total Green %': entry.total,
      [`Green (≥${THRESHOLD}%) slots`]: entry.count,
      'Overdue (count)': entry.overdueCount,
      'Overdue (sum %)': entry.overdueSum,
      'Deep Overdue (count)': entry.deepCount,
      'Deep Overdue (sum %)': entry.deepSum,
    }));
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Sheet 2: All occurrences (one row per window × P-rank)
    const detailRows = [];
    list.forEach((entry) => {
      entry.occurrences
        .slice()
        .sort((a, b) => b.pct - a.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
        .forEach((o) => {
          detailRows.push({
            'Number': entry.number,
            'Window': o.window_id,
            'P-Rank': o.p_rank,
            'Hit %': o.pct,
          });
        });
    });
    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    XLSX.utils.book_append_sheet(wb, wsDetail, 'All Occurrences');

    // Sheet 3: Overdue / Deep Overdue occurrences backing the bracket tag
    const overdueRows = [];
    list.forEach((entry) => {
      entry.overdueOccs.forEach((o) => overdueRows.push({
        'Number': entry.number, 'Status': 'Overdue', 'Window': o.window_id, 'Bucket': o.bucket, 'P-Rank': o.p_rank, 'Hit %': o.pct,
      }));
      entry.deepOccs.forEach((o) => overdueRows.push({
        'Number': entry.number, 'Status': 'Deep Overdue', 'Window': o.window_id, 'Bucket': o.bucket, 'P-Rank': o.p_rank, 'Hit %': o.pct,
      }));
    });
    const wsOverdue = XLSX.utils.json_to_sheet(overdueRows);
    XLSX.utils.book_append_sheet(wb, wsOverdue, 'Overdue Occurrences');

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_GreenPickTotals_Ball${bi + 1}_ge${THRESHOLD}pct_${rankSource}_${today}.xlsx`);
  };

  return (
    <div className="space-y-3" data-testid="green-pick-totals">
      <div className="rounded-xl border-2 border-emerald-600 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-emerald-700 via-emerald-800 to-teal-800 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-black uppercase tracking-wider">🟢 Green-Pick Totals — Combined % by Number</h3>
            <p className="text-[11px] text-white/85 italic mt-1">
              For each ball, every (window × P-slot) occurrence whose hit rate is <b>≥ {THRESHOLD}%</b> (the same &quot;green&quot; threshold used for the emerald P-chip coloring above) is grouped by its predicted <b>number</b>, and its % is <b>summed</b>. A number appearing as a green pick in multiple windows bubbles to the top — ranked <b>descending</b> by combined green %. If that same number also sits inside a bucket (T5–T20, any window) whose verdict is <span className="inline-block px-1 rounded bg-rose-100 text-rose-800 font-bold">Overdue</span> or <span className="inline-block px-1 rounded bg-rose-700 text-white font-bold">Deep Overdue</span>, a tag shows <code>Status = count = sum%</code> next to it. Ranking source: <b>{rankSource === 'frozen' ? 'Frozen ❄️' : 'Current'}</b>.
            </p>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {ballKeys.map((bk, bi) => {
            const list = summary[bk] || [];
            const ballLabel = `Ball ${bi + 1}`;
            const grandTotal = list.reduce((s, e) => s + e.total, 0);
            return (
              <div key={bk} className="rounded-lg border border-emerald-200 bg-emerald-50/40 overflow-hidden" data-testid={`green-pick-${bk}`}>
                <div className={`px-3 py-2 bg-gradient-to-r ${ballColors[bk]} text-white flex items-center justify-between gap-2`}>
                  <div className="flex flex-col min-w-0">
                    <span className="font-black text-sm uppercase tracking-wider">{ballLabel}</span>
                    <span className="text-[9px] font-mono opacity-90 leading-tight">
                      {list.length} nums · Σ <b>{grandTotal}%</b>
                    </span>
                  </div>
                  <button
                    onClick={() => handleExportBallExcel(bk, bi)}
                    disabled={list.length === 0}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-black border border-white/30 shrink-0"
                    data-testid={`green-pick-export-${bk}`}
                    title={`Download Ball ${bi + 1} green-pick totals as Excel`}
                  >
                    <DownloadSimple size={12} weight="bold" />
                    Excel
                  </button>
                </div>
                {list.length === 0 ? (
                  <div className="px-3 py-4 text-center text-[11px] text-slate-500 italic">No slots ≥ {THRESHOLD}% found for this ball.</div>
                ) : (
                  <div className="divide-y divide-emerald-100 max-h-[380px] overflow-y-auto">
                    {list.map((entry, idx) => (
                      <details key={entry.number} className="group" data-testid={`green-pick-${bk}-${entry.number}`}>
                        <summary className="cursor-pointer list-none px-3 py-2 hover:bg-emerald-100/50 select-none">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-mono text-emerald-700 w-4 shrink-0">#{idx + 1}</span>
                            <span className="inline-flex items-center justify-center rounded-full bg-emerald-600 text-white font-black w-7 h-7 text-xs shrink-0">
                              {entry.number}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] font-mono leading-tight">
                                <b className="text-emerald-800">{entry.total}%</b> total
                                <span className="text-slate-500"> ({entry.count} green slot{entry.count === 1 ? '' : 's'})</span>
                              </div>
                              {(entry.overdueCount > 0 || entry.deepCount > 0) && (
                                <div className="text-[9px] font-mono leading-tight mt-0.5 flex flex-wrap gap-1">
                                  {entry.overdueCount > 0 && (
                                    <span className="inline-flex items-center rounded bg-rose-100 text-rose-800 px-1 py-0.5 font-bold" data-testid={`green-pick-${bk}-${entry.number}-overdue-tag`}>
                                      Overdue = {entry.overdueCount} = {entry.overdueSum}%
                                    </span>
                                  )}
                                  {entry.deepCount > 0 && (
                                    <span className="inline-flex items-center rounded bg-rose-700 text-white px-1 py-0.5 font-bold" data-testid={`green-pick-${bk}-${entry.number}-deep-tag`}>
                                      Deep Overdue = {entry.deepCount} = {entry.deepSum}%
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="text-slate-400 group-open:rotate-90 transition-transform text-[10px] shrink-0">▶</span>
                          </div>
                        </summary>
                        <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1">
                          {entry.occurrences
                            .slice()
                            .sort((a, b) => b.pct - a.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
                            .map((o, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono bg-emerald-50 border-emerald-300"
                                title={`${o.window_id} · P${o.p_rank} · ${o.pct}%`}
                              >
                                <b className="text-slate-800">{o.window_id}</b>
                                <span className="text-slate-500">P{o.p_rank}</span>
                                <b className="text-emerald-700">{o.pct}%</b>
                              </span>
                            ))}
                        </div>
                        {(entry.overdueOccs.length > 0 || entry.deepOccs.length > 0) && (
                          <div className="px-3 pb-2 pt-0 flex flex-wrap gap-1 border-t border-slate-100 mt-1">
                            {entry.overdueOccs
                              .slice()
                              .sort((a, b) => b.pct - a.pct)
                              .map((o, i) => (
                                <span
                                  key={`od-${i}`}
                                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono bg-rose-50 border-rose-300"
                                  title={`Overdue · ${o.window_id} · ${o.bucket} · P${o.p_rank} · ${o.pct}%`}
                                >
                                  <b className="text-slate-800">{o.window_id}</b>
                                  <span className="text-slate-500">{o.bucket}</span>
                                  <b className="text-rose-700">Overdue={o.pct}%</b>
                                </span>
                              ))}
                            {entry.deepOccs
                              .slice()
                              .sort((a, b) => b.pct - a.pct)
                              .map((o, i) => (
                                <span
                                  key={`dp-${i}`}
                                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono bg-rose-100 border-rose-400"
                                  title={`Deep Overdue · ${o.window_id} · ${o.bucket} · P${o.p_rank} · ${o.pct}%`}
                                >
                                  <b className="text-slate-800">{o.window_id}</b>
                                  <span className="text-slate-500">{o.bucket}</span>
                                  <b className="text-rose-800">Deep={o.pct}%</b>
                                </span>
                              ))}
                          </div>
                        )}
                      </details>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ZeroHitPicks({ rows, ballKeys, rankSource = 'current' }) {
  // Threshold: include every top-20 P-slot whose (rounded) hit% is ≤ this value.
  // Default 3% per user request. Kept as a constant (not stateful) since the
  // header text also references it — change here to expand/shrink coverage.
  const THRESHOLD = 3;

  const summary = useMemo(() => {
    // per ball → { number → [{window_id, p_rank, pct}] } for slots where 0 ≤ pct ≤ THRESHOLD
    const out = {};
    ballKeys.forEach((bk) => { out[bk] = {}; });
    rows.forEach((w) => {
      const bd = w.bucket_diagnostic || {};
      ballKeys.forEach((bk) => {
        const b = pickDiag(bd[bk], rankSource);
        if (!b) return;
        const top20 = b.current_top20 || [];
        const perP = b.per_p || {};
        const predCount = (b.pred_count != null) ? b.pred_count : 50;
        for (let p = 1; p <= Math.min(20, predCount); p++) {
          const n = top20[p - 1];
          if (n == null) continue;
          const raw = perP[String(p)]?.pct || 0;
          const pctRounded = Math.round(raw);
          if (pctRounded <= THRESHOLD) {
            if (!out[bk][n]) out[bk][n] = [];
            out[bk][n].push({ window_id: w.window_id, p_rank: p, pct: pctRounded });
          }
        }
      });
    });
    const result = {};
    ballKeys.forEach((bk) => {
      result[bk] = Object.entries(out[bk])
        .map(([n, occ]) => {
          const zeroCount = occ.filter((o) => o.pct === 0).length;
          const lowCount = occ.length - zeroCount;
          return { number: parseInt(n, 10), count: occ.length, zeroCount, lowCount, occurrences: occ };
        })
        // Ascending by ball number (smallest → largest) per user request
        .sort((a, b) => a.number - b.number);
    });
    return result;
  }, [rows, ballKeys, rankSource]);

  const ballColors = {
    ball_1: 'from-slate-700 to-slate-800',
    ball_2: 'from-zinc-700 to-zinc-800',
    ball_3: 'from-neutral-700 to-neutral-800',
    ball_4: 'from-stone-700 to-stone-800',
    ball_5: 'from-slate-600 to-slate-700',
  };
  const totalSlots = rows.length * 20;

  const handleExportBallExcel = (bk, bi) => {
    const list = summary[bk] || [];
    const wb = XLSX.utils.book_new();
    // Sheet 1: Summary — one row per number (ascending)
    const summaryRows = list.map((entry) => ({
      'Number': entry.number,
      [`Total slots ≤ ${THRESHOLD}%`]: entry.count,
      '0% slots': entry.zeroCount,
      [`1–${THRESHOLD}% slots`]: entry.lowCount,
    }));
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Sheet 2: All occurrences (one row per window × P-rank)
    const detailRows = [];
    list.forEach((entry) => {
      entry.occurrences
        .slice()
        .sort((a, b) => a.pct - b.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
        .forEach((o) => {
          detailRows.push({
            'Number': entry.number,
            'Window': o.window_id,
            'P-Rank': o.p_rank,
            'Hit %': o.pct,
          });
        });
    });
    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    XLSX.utils.book_append_sheet(wb, wsDetail, 'All Occurrences');

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_LowHit_Ball${bi + 1}_le${THRESHOLD}pct_${rankSource}_${today}.xlsx`);
  };

  return (
    <div className="space-y-3" data-testid="zero-hit-picks">
      <div className="rounded-xl border-2 border-slate-400 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-slate-700 via-slate-800 to-neutral-800 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-black uppercase tracking-wider">🚫 Low-Hit Predictions — Numbers stuck at ≤ {THRESHOLD}%</h3>
            <p className="text-[11px] text-white/85 italic mt-1">
              For each ball, lists every top-20 predicted number whose historical hit rate is <b>0%</b> or between <b>1%–{THRESHOLD}%</b> in that (window × P-slot) — the &quot;dead&quot; and &quot;near-dead&quot; picks the model still surfaces. Numbers are sorted <b>ascending</b> (smallest → largest). Max possible per number = <b>{totalSlots}</b> ({rows.length} windows × 20 P-slots). Ranking source: <b>{rankSource === 'frozen' ? 'Frozen ❄️' : 'Current'}</b>.
            </p>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {ballKeys.map((bk, bi) => {
            const list = summary[bk] || [];
            const ballLabel = `Ball ${bi + 1}`;
            const totalZero = list.reduce((s, e) => s + e.zeroCount, 0);
            const totalLow = list.reduce((s, e) => s + e.lowCount, 0);
            return (
              <div key={bk} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`zero-hit-${bk}`}>
                <div className={`px-3 py-2 bg-gradient-to-r ${ballColors[bk]} text-white flex items-center justify-between gap-2`}>
                  <div className="flex flex-col min-w-0">
                    <span className="font-black text-sm uppercase tracking-wider">{ballLabel}</span>
                    <span className="text-[9px] font-mono opacity-90 leading-tight">
                      {list.length} nums · <b>{totalZero}</b>×0% · <b>{totalLow}</b>×1-{THRESHOLD}%
                    </span>
                  </div>
                  <button
                    onClick={() => handleExportBallExcel(bk, bi)}
                    disabled={list.length === 0}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-black border border-white/30 shrink-0"
                    data-testid={`zero-hit-export-${bk}`}
                    title={`Download Ball ${bi + 1} low-hit numbers as Excel`}
                  >
                    <DownloadSimple size={12} weight="bold" />
                    Excel
                  </button>
                </div>
                {list.length === 0 ? (
                  <div className="px-3 py-4 text-center text-[11px] text-slate-500 italic">No low-hit predictions — every top-20 slot is &gt; {THRESHOLD}%.</div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
                    {list.map((entry) => (
                      <details key={entry.number} className="group" data-testid={`zero-hit-${bk}-${entry.number}`}>
                        <summary className="cursor-pointer list-none px-3 py-2 hover:bg-slate-100 select-none">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center rounded-full bg-slate-800 text-white font-black w-7 h-7 text-xs shrink-0">
                              {entry.number}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] font-mono leading-tight">
                                <b className="text-slate-900">{entry.count}</b> low-hit slot{entry.count === 1 ? '' : 's'}
                                {entry.zeroCount > 0 && entry.lowCount > 0 && (
                                  <span className="text-slate-500"> ({entry.zeroCount}×0% + {entry.lowCount}×1-{THRESHOLD}%)</span>
                                )}
                                {entry.zeroCount > 0 && entry.lowCount === 0 && (
                                  <span className="text-slate-500"> (all 0%)</span>
                                )}
                                {entry.zeroCount === 0 && entry.lowCount > 0 && (
                                  <span className="text-slate-500"> (1-{THRESHOLD}%)</span>
                                )}
                              </div>
                            </div>
                            <span className="text-slate-400 group-open:rotate-90 transition-transform text-[10px] shrink-0">▶</span>
                          </div>
                        </summary>
                        <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1">
                          {entry.occurrences
                            .slice()
                            .sort((a, b) => a.pct - b.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
                            .map((o, i) => (
                              <span
                                key={i}
                                className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono ${o.pct === 0 ? 'bg-white border-slate-300' : 'bg-amber-50 border-amber-200'}`}
                                title={`${o.window_id} · P${o.p_rank} · ${o.pct}%`}
                              >
                                <b className="text-slate-800">{o.window_id}</b>
                                <span className="text-slate-500">P{o.p_rank}</span>
                                <b className={o.pct === 0 ? 'text-slate-400' : 'text-amber-700'}>{o.pct}%</b>
                              </span>
                            ))}
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Overdue / Deep Overdue Pool — aggregates every P-slot occurrence of every
// number across (window × bucket) pairs whose verdict is OVERDUE or DEEP
// OVERDUE (Due-Next is intentionally excluded — we only want windows that
// have already EXCEEDED their expected miss gap). For each number we show
// the pct distribution ("1×14% 2×10% …"), the sum of those pcts, the count
// of "strong" occurrences (≥ threshold) and where the single best occurrence
// came from ("best P2 14% in W17"). Sorted DESC by best single % (so the
// number with the strongest overdue signal in any single window floats to
// the top), then by sum, count, number. Sits at the very bottom of the
// Bucket Diagnostic tab, below Strong-Pick and Low-Hit sections.
// ─────────────────────────────────────────────────────────────────────────────
function OverdueDuePool({ rows, ballKeys, rankSource = 'current' }) {
  const HIGH_THRESHOLD = 9; // % — the "N hits ≥ X%" cutoff

  const summary = useMemo(() => {
    const bucketDefs = [
      { label: 'T5',  lo: 1,  hi: 5  },
      { label: 'T10', lo: 6,  hi: 10 },
      { label: 'T15', lo: 11, hi: 15 },
      { label: 'T20', lo: 16, hi: 20 },
    ];
    const out = {};
    ballKeys.forEach((bk) => {
      const perNum = new Map();
      rows.forEach((w) => {
        const bd = w.bucket_diagnostic || {};
        const b = pickDiag(bd[bk], rankSource);
        if (!b) return;
        const top20 = b.current_top20 || [];
        const perP = b.per_p || {};
        const buckets = b.buckets || {};
        const streaks = b.bucket_miss_streaks || {};
        bucketDefs.forEach(({ label, lo, hi }) => {
          const bucketPct = buckets[label]?.pct ?? 0;
          const streak = streaks[label] ?? 0;
          const v = computeVerdict(streak, bucketPct);
          // Only OVERDUE + DEEP OVERDUE — "Due Next" is intentionally excluded
          // per user request (streak must EXCEED expected gap, not just meet it).
          if (!['over', 'deep'].includes(v.tier)) return;
          for (let p = lo; p <= hi; p++) {
            const n = top20[p - 1];
            if (n == null) continue;
            const pct = Math.round(perP[String(p)]?.pct || 0);
            if (pct <= 0) continue; // dead pick — skip
            const entry = perNum.get(n) || { occurrences: [] };
            entry.occurrences.push({
              window_id: w.window_id,
              p_rank: p,
              pct,
              bucket: label,
              tier: v.tier,
            });
            perNum.set(n, entry);
          }
        });
      });
      const list = [...perNum.entries()].map(([n, meta]) => {
        const occs = meta.occurrences;
        const sum = occs.reduce((s, o) => s + o.pct, 0);
        const best = occs.reduce((a, b) => (b.pct > a.pct ? b : a), occs[0]);
        const highCount = occs.filter((o) => o.pct >= HIGH_THRESHOLD).length;
        // Distribution: descending by pct, "cnt × pct%" pairs.
        const distMap = new Map();
        occs.forEach((o) => distMap.set(o.pct, (distMap.get(o.pct) || 0) + 1));
        const distribution = [...distMap.entries()]
          .sort((a, b) => b[0] - a[0])
          .map(([pct, cnt]) => ({ pct, cnt }));
        return {
          n: parseInt(n, 10),
          count: occs.length,
          sum,
          best,
          highCount,
          distribution,
          occurrences: occs,
        };
      });
      // Primary sort: best single % desc (so the number with the biggest
      // single overdue-signal shows up first). Tie-breakers: sum → count →
      // number ascending for determinism.
      list.sort((a, b) =>
        (b.best.pct - a.best.pct)
        || (b.sum - a.sum)
        || (b.count - a.count)
        || (a.n - b.n)
      );
      out[bk] = list;
    });
    return out;
  }, [rows, ballKeys, rankSource]);

  const ballColors = {
    ball_1: 'from-emerald-600 to-teal-700',
    ball_2: 'from-emerald-700 to-cyan-700',
    ball_3: 'from-teal-700 to-emerald-800',
    ball_4: 'from-cyan-700 to-teal-800',
    ball_5: 'from-emerald-600 to-emerald-800',
  };

  const handleExportBallExcel = (bk, bi) => {
    const list = summary[bk] || [];
    const wb = XLSX.utils.book_new();
    // Sheet 1: Per-number summary (one row per number, ordered by sum desc)
    const summaryRows = list.map((entry) => ({
      'Number': entry.n,
      'Occurrences': entry.count,
      [`Sum of %`]: entry.sum,
      [`Hits ≥${HIGH_THRESHOLD}%`]: entry.highCount,
      'Best %': entry.best?.pct ?? 0,
      'Best P-rank': entry.best?.p_rank ?? '',
      'Best window': entry.best?.window_id ?? '',
      'Best bucket': entry.best?.bucket ?? '',
      'Distribution': entry.distribution.map((d) => `${d.cnt}x${d.pct}%`).join(' '),
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');

    // Sheet 2: All occurrences (Due / Overdue / Deep Overdue only)
    const detailRows = [];
    list.forEach((entry) => {
      entry.occurrences
        .slice()
        .sort((a, b) => b.pct - a.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
        .forEach((o) => {
          detailRows.push({
            'Number': entry.n,
            'Window': o.window_id,
            'Bucket': o.bucket,
            'P-Rank': o.p_rank,
            'Hit %': o.pct,
            'Verdict': o.tier === 'deep' ? 'DEEP OVERDUE' : 'OVERDUE',
          });
        });
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'All Occurrences');

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_DueOverduePool_Ball${bi + 1}_${rankSource}_${today}.xlsx`);
  };

  const anyRows = ballKeys.some((bk) => (summary[bk] || []).length > 0);

  return (
    <div className="space-y-3" data-testid="overdue-due-pool">
      <div className="rounded-xl border-2 border-emerald-500 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-emerald-700 via-teal-700 to-emerald-800 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-black uppercase tracking-wider">🔥 Overdue / Deep Overdue Pool — per-number pile-up</h3>
            <p className="text-[11px] text-white/90 italic mt-1">
              For each ball, aggregates every P-slot occurrence of every number across (window × bucket) pairs whose bucket verdict is <b>OVERDUE</b> or <b>DEEP OVERDUE</b> (Due-Next is <i>not</i> included — the streak must already <i>exceed</i> the expected gap). The distribution shows how many occurrences fall at each rounded pct, the sum is the total (a rough "conviction weight"), and the footer highlights the single highest-pct occurrence — this is the number's <b>best entry</b> in the current overdue pool. Sorted DESC by <b>best single %</b>. Ranking source: <b>{rankSource === 'frozen' ? 'Frozen ❄️' : 'Current'}</b>.
            </p>
          </div>
        </div>
        {!anyRows ? (
          <div className="p-6 text-center text-slate-500 italic text-sm">
            No windows are currently in Overdue or Deep Overdue state for any ball position.
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {ballKeys.map((bk, bi) => {
              const list = summary[bk] || [];
              return (
                <div key={bk} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`overdue-pool-${bk}`}>
                  <div className={`px-3 py-2 bg-gradient-to-r ${ballColors[bk]} text-white flex items-center justify-between gap-2`}>
                    <div className="flex flex-col min-w-0">
                      <span className="font-black text-sm uppercase tracking-wider">Ball {bi + 1}</span>
                      <span className="text-[9px] font-mono opacity-90 leading-tight">{list.length} numbers</span>
                    </div>
                    <button
                      onClick={() => handleExportBallExcel(bk, bi)}
                      disabled={list.length === 0}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/15 hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-black border border-white/30 shrink-0"
                      data-testid={`overdue-pool-export-${bk}`}
                      title={`Download Ball ${bi + 1} due/overdue pool as Excel`}
                    >
                      <DownloadSimple size={12} weight="bold" />
                      Excel
                    </button>
                  </div>
                  {list.length === 0 ? (
                    <div className="px-3 py-4 text-center text-[11px] text-slate-500 italic">No due/overdue picks for this ball.</div>
                  ) : (
                    <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
                      {list.map((entry) => (
                        <details key={entry.n} className="group" data-testid={`overdue-pool-${bk}-${entry.n}`}>
                          <summary className="cursor-pointer list-none px-3 py-2 hover:bg-slate-100 select-none">
                            <div className="flex items-start gap-2">
                              <span className="inline-flex items-center justify-center rounded-full bg-emerald-800 text-white font-black w-8 h-8 text-sm shrink-0 mt-0.5">
                                {entry.n}
                              </span>
                              <div className="flex-1 min-w-0 space-y-0.5">
                                <div className="text-[11px] font-mono font-bold text-emerald-900 leading-snug break-words">
                                  {entry.distribution.map((d, i) => (
                                    <span key={i} className={d.pct >= HIGH_THRESHOLD ? 'text-emerald-700' : 'text-slate-600'}>
                                      ({d.cnt}×{d.pct}%){i < entry.distribution.length - 1 ? ' ' : ''}
                                    </span>
                                  ))}
                                  {' = '}
                                  <span className="text-emerald-900 font-black">{entry.sum}%</span>
                                </div>
                                <div className="text-[10px] text-slate-600 leading-tight">
                                  <b className="text-emerald-700">{entry.highCount}</b> hits ≥{HIGH_THRESHOLD}% · best <b>P{entry.best.p_rank}</b> <b className="text-emerald-700">{entry.best.pct}%</b> in <b>{entry.best.window_id}</b>
                                </div>
                              </div>
                              <span className="text-slate-400 group-open:rotate-90 transition-transform text-[10px] shrink-0 mt-1">▶</span>
                            </div>
                          </summary>
                          <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1">
                            {entry.occurrences
                              .slice()
                              .sort((a, b) => b.pct - a.pct || a.p_rank - b.p_rank || a.window_id.localeCompare(b.window_id))
                              .map((o, i) => {
                                const badge =
                                  o.tier === 'deep' ? 'bg-rose-100 border-rose-300 text-rose-800' :
                                  'bg-orange-100 border-orange-300 text-orange-800';
                                return (
                                  <span
                                    key={i}
                                    className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono ${badge}`}
                                    title={`${o.window_id} · ${o.bucket} · P${o.p_rank} · ${o.pct}% · ${o.tier === 'deep' ? 'DEEP OVERDUE' : 'OVERDUE'}`}
                                  >
                                    <b>{o.window_id}</b>
                                    <span>{o.bucket}</span>
                                    <span>P{o.p_rank}</span>
                                    <b>{o.pct}%</b>
                                  </span>
                                );
                              })}
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// Shared Strong-Pick computation — used by StrongPickSummary and FreqStrongPickPanel
// so both panels always agree on the same ranked list.
function computeStrongPick(rows, ballKeys, rankSource, threshold) {
  // per ball → { number → { count, occurrences: [{window_id, p_rank, pct}] } }
  const out = {};
  ballKeys.forEach((bk) => { out[bk] = {}; });
  rows.forEach((w) => {
    const bd = w.bucket_diagnostic || {};
    ballKeys.forEach((bk) => {
      const b = pickDiag(bd[bk], rankSource);
      if (!b) return;
      const top20 = b.current_top20 || [];
      const perP = b.per_p || {};
      const predCount = (b.pred_count != null) ? b.pred_count : 50;
      for (let p = 1; p <= Math.min(20, predCount); p++) {
        const n = top20[p - 1];
        if (n == null) continue;
        const rawPct = perP[String(p)]?.pct || 0;
        const pctRounded = Math.round(rawPct);
        if (!out[bk][n]) {
          out[bk][n] = { count: 0, occurrences: [], all_occurrences: [] };
        }
        out[bk][n].all_occurrences.push({ window_id: w.window_id, p_rank: p, pct: pctRounded });
        // Count-toward-threshold uses the ROUNDED value so it matches
        // what the Bucket table displays (e.g. raw 8.6% → shows "9%").
        if (pctRounded >= threshold) {
          out[bk][n].count += 1;
          out[bk][n].occurrences.push({ window_id: w.window_id, p_rank: p, pct: pctRounded });
        }
      }
    });
  });
  const result = {};
  ballKeys.forEach((bk) => {
    result[bk] = Object.entries(out[bk])
      .filter(([, obj]) => obj.count > 0)
      .map(([n, obj]) => {
        const byPct = {};
        obj.all_occurrences.forEach((o) => {
          if (o.pct <= 0) return;
          byPct[o.pct] = (byPct[o.pct] || 0) + 1;
        });
        const pctGroups = Object.entries(byPct)
          .map(([p, c]) => ({ pct: parseInt(p, 10), count: c }))
          .sort((a, b) => b.pct - a.pct);
        const totalScore = pctGroups.reduce((s, g) => s + g.count * g.pct, 0);
        return { number: parseInt(n, 10), ...obj, pctGroups, totalScore };
      })
      .sort((a, b) => (b.totalScore - a.totalScore) || (b.count - a.count) || (a.number - b.number));
  });
  return result;
}

// "Freq ∩ Strong-Pick" — positional-frequency top-10 filtered by the Strong-Pick
// top-10 (threshold 7%). Walk-forward hit rate comes from /analysis/w50-freq-sp.
// Sits at the very bottom of the Bucket Diagnostic tab.
function FreqStrongPickPanel({ rows, ballKeys, rankSource = 'current' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [computing, setComputing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // The backend computes this walk-forward audit in a background task on a
  // cache miss (it can take well over a minute on a large draw history) and
  // returns `{status: "computing"}` immediately instead of blocking the
  // request past the platform's 120s proxy read timeout. Poll until the
  // real result lands.
  useEffect(() => {
    let alive = true;
    let timer = null;
    const poll = () => {
      axios.get(`${API}/analysis/w50-freq-sp`)
        .then((res) => {
          if (!alive) return;
          if (res.data?.status === 'computing') {
            setComputing(true);
            timer = setTimeout(poll, 4000);
          } else {
            setComputing(false);
            setData(res.data);
          }
        })
        .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message || 'Failed to load'); });
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  const spTop10 = useMemo(() => {
    const sp = computeStrongPick(rows, ballKeys, rankSource, 7);
    const out = {};
    ballKeys.forEach((bk) => { out[bk] = (sp[bk] || []).slice(0, 10).map((e) => e.number); });
    return out;
  }, [rows, ballKeys, rankSource]);

  const perBall = useMemo(() => {
    if (!data?.balls) return {};
    const out = {};
    ballKeys.forEach((bk) => {
      const b = data.balls[bk];
      if (!b) return;
      const freq = b.current?.freq_top10 || [];
      const sp = spTop10[bk] || [];
      const candidates = freq.filter((f) => sp.includes(f.number)).map((f, i) => ({ ...f, freqRank: freq.indexOf(f) + 1, spRank: sp.indexOf(f.number) + 1, idx: i }));
      out[bk] = { ...b, freq, sp, candidates };
    });
    return out;
  }, [data, spTop10, ballKeys]);

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const summary = [['Ball', 'Candidates (Freq ∩ SP)', 'Set size', `Walk-forward hits / ${data.test_count}`, 'Hit %', 'Avg size (backtest)', 'Freq top-5 %', 'Freq top-10 %', 'SP top-5 %', 'SP top-10 %', 'H1 %', 'H2 %', 'Miss streak']];
    const rowsSheet = [['Ball', 'Draw #', 'Date', 'Actual', 'Candidates', 'Hit']];
    ballKeys.forEach((bk, i) => {
      const b = perBall[bk];
      if (!b) return;
      summary.push([`Ball ${i + 1}`, b.candidates.map((c) => c.number).join(', '), b.candidates.length, `${b.hits}/${b.n}`, b.pct, b.avg_size, b.baselines.freq5, b.baselines.freq10, b.baselines.sp5, b.baselines.sp10, b.h1_pct, b.h2_pct, b.miss_streak]);
      (b.per_draw || []).forEach((d) => rowsSheet.push([`Ball ${i + 1}`, d.draw_number, d.date || '', d.actual, d.candidates.join(', '), d.hit ? 'HIT' : '']));
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rowsSheet), 'Per Draw');
    XLSX.writeFile(wb, `W50_FreqStrongPick_${data.test_count}draws_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const ballColors = {
    ball_1: 'from-emerald-500 to-emerald-600',
    ball_2: 'from-sky-500 to-sky-600',
    ball_3: 'from-violet-500 to-violet-600',
    ball_4: 'from-amber-500 to-amber-600',
    ball_5: 'from-rose-500 to-rose-600',
  };

  return (
    <div className="rounded-xl border-2 border-indigo-300 bg-white shadow-sm overflow-hidden" data-testid="freq-sp-panel">
      <div className="px-5 py-3 bg-gradient-to-r from-indigo-800 via-indigo-700 to-blue-700 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-black uppercase tracking-wider">🔗 Freq ∩ Strong-Pick — Positional-frequency top-10 filtered by Strong-Pick top-10</h3>
          <p className="text-[11px] text-white/85 italic mt-1">
            Frequency = how often each number has been drawn at this ball position (all draws). Strong-Pick = the top-10 of the Strong-Pick Summary above (≥ 7%). The candidate set is the overlap, ordered by frequency.
            {data ? <> Walk-forward hit rate over the last <b>{data.test_count}</b> draws (#{data.first_test_draw} → #{data.last_draw}) — both lists were rebuilt at every draw from data available <em>before</em> it.</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <button onClick={() => setShowAll((v) => !v)} className="px-2 py-1 rounded border border-white/40 bg-indigo-900/40 hover:bg-indigo-900/60" data-testid="freq-sp-toggle-draws">
            {showAll ? 'Last 12 draws' : 'All draws'}
          </button>
          <button onClick={handleExport} disabled={!data} className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-white text-indigo-800 border border-white font-black hover:bg-indigo-50 disabled:opacity-50" data-testid="freq-sp-export">
            <DownloadSimple size={14} weight="bold" /> Download Excel
          </button>
        </div>
      </div>

      {error && <div className="p-4 text-sm text-rose-700" data-testid="freq-sp-error">Could not load the walk-forward audit: {error}</div>}
      {!data && !error && (
        <div className="p-6 flex items-center gap-2 text-sm text-slate-600" data-testid="freq-sp-loading">
          <CircleNotch size={18} className="animate-spin" />
          {computing
            ? 'Computing walk-forward audit in the background — this can take a minute on a large draw history…'
            : 'Running walk-forward audit (first load can take ~30 s)…'}
        </div>
      )}

      {data && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {ballKeys.map((bk, bi) => {
            const b = perBall[bk];
            if (!b) return null;
            const [baseLabel, baseVal] = b.avg_size >= 7.5 ? ['Freq top-10', b.baselines.freq10] : ['Freq top-5', b.baselines.freq5];
            const lift = Math.round((b.pct - baseVal) * 10) / 10;
            const perNumber = b.avg_size ? Math.round((b.pct / b.avg_size) * 10) / 10 : 0;
            const draws = showAll ? b.per_draw : b.per_draw.slice(-12);
            return (
              <div key={bk} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`freq-sp-${bk}`}>
                <div className={`px-3 py-2 bg-gradient-to-r ${ballColors[bk]} text-white flex items-center justify-between`}>
                  <span className="font-black text-sm uppercase tracking-wider">Ball {bi + 1}</span>
                  <span className="text-[10px] font-mono opacity-90">{b.candidates.length} candidates</span>
                </div>
                <div className="p-3 space-y-3">
                  <div className="flex flex-wrap gap-1.5" data-testid={`freq-sp-candidates-${bk}`}>
                    {b.candidates.length === 0 && <span className="text-[11px] italic text-slate-500">No overlap between Freq top-10 and Strong-Pick top-10 right now.</span>}
                    {b.candidates.map((c) => (
                      <span key={c.number} className="inline-flex flex-col items-center rounded-lg border border-indigo-300 bg-indigo-50 px-2 py-1" title={`Freq rank ${c.freqRank} · ${c.count} draws (${c.pct}%) · Strong-Pick rank ${c.spRank}`} data-testid={`freq-sp-candidate-${bk}-${c.number}`}>
                        <b className="text-indigo-900 text-base leading-none">{c.number}</b>
                        <span className="text-[9px] font-mono text-indigo-700 mt-0.5">F{c.freqRank} · SP{c.spRank}</span>
                      </span>
                    ))}
                  </div>
                  <div className="rounded-md bg-white border border-slate-200 p-2" data-testid={`freq-sp-hitrate-${bk}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Walk-forward hit rate</span>
                      <span className="font-mono text-lg font-black text-indigo-800">{b.pct}%</span>
                    </div>
                    <div className="text-[10px] text-slate-600 font-mono">{b.hits}/{b.n} draws · avg set {b.avg_size} numbers · miss streak {b.miss_streak}</div>
                    <div className="text-[10px] text-slate-600 font-mono">halves {b.h1_pct}% / {b.h2_pct}% · {perNumber}% per number</div>
                    <div className={`text-[10px] font-mono mt-1 font-bold ${lift >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid={`freq-sp-lift-${bk}`}>
                      {lift >= 0 ? '+' : ''}{lift} pts vs {baseLabel} (size-matched)
                    </div>
                  </div>
                  <table className="w-full text-[10px] font-mono">
                    <tbody>
                      {[['Freq top-5', b.baselines.freq5], ['Freq top-10', b.baselines.freq10], ['SP top-5', b.baselines.sp5], ['SP top-10', b.baselines.sp10]].map(([lab, v]) => (
                        <tr key={lab} className="border-t border-slate-100">
                          <td className="py-0.5 text-slate-600">{lab}</td>
                          <td className="py-0.5 text-right text-slate-800 font-bold">{v}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{showAll ? `All ${b.per_draw.length} draws` : 'Last 12 draws'} (● hit · ○ miss)</div>
                    <div className="flex flex-wrap gap-1" data-testid={`freq-sp-strip-${bk}`}>
                      {draws.map((d) => (
                        <span key={d.draw_number} title={`#${d.draw_number} · actual ${d.actual} · set [${d.candidates.join(', ')}]`} className={`inline-flex items-center justify-center w-6 h-6 rounded text-[9px] font-mono font-bold ${d.hit ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                          {d.actual}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="rounded-b-lg border-t border-slate-200 bg-slate-50 px-4 py-3 text-[11px] text-slate-600 leading-relaxed">
        <b>How to read this:</b> use frequency as the ranker and Strong-Pick as a filter. A number must sit in the position&apos;s all-time top-10 <em>and</em> in the Strong-Pick top-10 to qualify. Hit rate counts draws where the actual ball was inside the set (typically 5–7 numbers). Compare against the Freq / SP baselines: with n≈{data?.test_count || '—'} the 95% noise band is about ±9 pts, so treat smaller gaps as noise. Backtest rankings use each window&apos;s predictor-native ordering.
      </div>
    </div>
  );
}

// Per-ball "sweet-spot" Sum-of-% bands — derived from a walk-forward audit
// of the last 88 draws (no look-ahead): the range within which the actual
// drawn number's Strong-Pick totalScore most reliably lands, and how much
// that narrows the candidate pool vs picking blind.
// See /app/memory/analysis_strong_pick_totalscore_60_80.md for the full audit.
const STRONG_PICK_SWEET_SPOT = {
  ball_1: { lo: 60, hi: Infinity, hitRate: 84.1, avgCandidates: 13.3, baseline: 26.6, label: '≥ 60%' },
  ball_2: { lo: 27, hi: 55, hitRate: 56.8, avgCandidates: 13.4, baseline: 26.8, label: '27% – 55%' },
  ball_3: { lo: 14, hi: 39, hitRate: 53.4, avgCandidates: 23.1, baseline: 46.2, label: '14% – 39%' },
  ball_4: { lo: 22.5, hi: 45, hitRate: 52.3, avgCandidates: 13.8, baseline: 27.6, label: '22.5% – 45%' },
  ball_5: { lo: 60, hi: Infinity, hitRate: 68.2, avgCandidates: 9.5, baseline: 19.0, label: '≥ 60%' },
};

const STRONG_PICK_DECADE_RANGES = [
  { lo: 1, hi: 10, label: '1-10' },
  { lo: 11, hi: 20, label: '11-20' },
  { lo: 21, hi: 30, label: '21-30' },
  { lo: 31, hi: 40, label: '31-40' },
  { lo: 41, hi: 50, label: '41-50' },
];
const STRONG_PICK_BALL_LABELS = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

/** For each ball: how many of the gold-ring sweet-spot numbers (currently
 * >= threshold% AND inside this ball's sweet-spot totalScore band) fall into
 * each decade range 1-10/11-20/21-30/31-40/41-50 — the range with the most
 * gold-ring numbers is the "dominant bracket" (e.g. Ball 1: 1-10 = 10 gold
 * numbers predicted -> dominant). Then, over the last 88 ACTUAL draws, shows
 * what % of those 88 draws had their real value land inside that SAME
 * bracket's numeric range — irrespective of whether that specific actual
 * number was itself one of the gold-ring predicted numbers. */
function DecadeRangeBreakdown({ summary, ballKeys, threshold }) {
  const [wf, setWf] = React.useState(null); // walk-forward bracket-check result
  const [wfError, setWfError] = React.useState(null);
  const [retryKey, setRetryKey] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    let timer = null;
    let retries = 0;
    const MAX_RETRIES = 8; // ~32s of transient-failure tolerance (e.g. a backend restart) before giving up
    const poll = () => {
      axios.get(`${API}/analysis/w50-bracket-walkforward`, { params: { test_count: 300, threshold } })
        .then((res) => {
          if (!alive) return;
          retries = 0;
          if (res.data?.status === 'computing') {
            timer = setTimeout(poll, 4000);
          } else if (res.data?.error) {
            setWfError(res.data.error);
          } else {
            setWf(res.data);
          }
        })
        .catch(() => {
          if (!alive) return;
          retries += 1;
          if (retries <= MAX_RETRIES) {
            timer = setTimeout(poll, 4000);
          } else {
            setWfError('Could not reach the server for the walk-forward check.');
          }
        });
    };
    setWf(null);
    setWfError(null);
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [threshold, retryKey]);

  const ballData = ballKeys.map((bk, bi) => {
    const spot = STRONG_PICK_SWEET_SPOT[bk];
    const fullList = summary[bk] || [];
    const sweetList = fullList.filter((e) => e.totalScore >= spot.lo && e.totalScore <= spot.hi);
    const groups = STRONG_PICK_DECADE_RANGES.map((range) => {
      const nums = sweetList.filter((e) => e.number >= range.lo && e.number <= range.hi).map((e) => e.number);
      return { ...range, count: nums.length, nums };
    });
    const maxCount = Math.max(...groups.map((g) => g.count));
    const dominant = maxCount > 0 ? groups.filter((g) => g.count === maxCount) : [];
    return { bk, sweetList, groups, maxCount, dominant };
  }).reduce((acc, item) => { acc[item.bk] = item; return acc; }, {});

  // Last-88-draws dominant-bracket hit rate — TRUE WALK-FORWARD, no look-ahead.
  // Sourced from /api/analysis/w50-bracket-walkforward, which re-derives the
  // Gold-Ring Sweet-Spot dominant bracket at EACH of the last 88 draws using
  // ONLY data available strictly BEFORE that draw (so a draw is never graded
  // against a bracket that was itself shaped by that same draw's outcome).
  const last88Stats = ballKeys.map((bk) => {
    const wfBall = wf?.balls?.[bk];
    if (!wfBall) {
      return { bk, dominantLabels: [], successPct: null, hits: null, misses: null, missPct: null, total: null, currentGap: null, verdict: null };
    }
    const dominantLabels = wfBall.latest_dominant || [];
    const successPct = wfBall.hit_pct;
    const verdict = computeVerdict(wfBall.current_gap == null ? wfBall.total : wfBall.current_gap, successPct);
    return {
      bk,
      dominantLabels,
      successPct,
      hits: wfBall.hits,
      misses: wfBall.misses,
      missPct: wfBall.miss_pct,
      total: wfBall.total,
      currentGap: wfBall.current_gap,
      verdict,
    };
  }).reduce((acc, item) => { acc[item.bk] = item; return acc; }, {});

  return (
    <div className="rounded-xl border-2 border-indigo-200 bg-white shadow-sm overflow-hidden" data-testid="strong-pick-decade-breakdown">

      <div className="px-5 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
        <h3 className="text-base font-black uppercase tracking-wider">Range Breakdown — Where Do the Gold-Ring Sweet-Spot Numbers Fall?</h3>
        <p className="text-[11px] text-white/85 italic mt-1">
          For each ball, how many of the gold-ringed sweet-spot numbers (>= {threshold}% AND inside the ball&apos;s sweet-spot band) fall in each decade range — narrows down which part of 1-50 the next draw is likely to land in.
        </p>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {ballKeys.map((bk) => {
          const { sweetList, groups, maxCount } = ballData[bk];
          return (
            <div key={bk} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`strong-pick-decade-${bk}`}>
              <div className="px-3 py-2 bg-indigo-600 text-white flex items-center justify-between">
                <span className="font-black text-sm uppercase tracking-wider">{STRONG_PICK_BALL_LABELS[bk]}</span>
                <span className="text-[10px] font-mono opacity-90">🎯 {sweetList.length} gold</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <li
                    key={g.label}
                    className={`px-3 py-2 text-xs flex items-start justify-between gap-2 ${g.count === maxCount && g.count > 0 ? 'bg-indigo-50' : ''}`}
                    data-testid={`strong-pick-decade-${bk}-${g.label}`}
                  >
                    <span className="font-mono font-bold text-slate-700 shrink-0">{g.label}</span>
                    <span className="text-right">
                      <b className={`font-mono ${g.count === maxCount && g.count > 0 ? 'text-indigo-700' : 'text-slate-600'}`}>{g.count}</b>
                      <span className="text-slate-400"> number{g.count !== 1 ? 's' : ''}</span>
                      {g.count > 0 && <div className="text-[10px] text-slate-500 font-mono">({g.nums.join(', ')})</div>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Last-N-draws hit rate for the dominant bracket — TRUE WALK-FORWARD (no look-ahead). N = wf.test_count (dynamic, driven by the backend's test window). */}
      <div className="px-4 pb-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="strong-pick-decade-last88-hitrate">
          <h4 className="text-[11px] font-black text-amber-800 uppercase tracking-wide mb-2">
            Last {wf?.test_count ?? 300} Draws — Did the Actual Number Land in the Dominant Bracket?
          </h4>
          <p className="text-[10px] text-amber-700 mb-2">
            Walk-forward check: at EACH of the last {wf?.test_count ?? 300} draws, the dominant bracket is rebuilt using ONLY data available strictly before that draw (never the draw's own outcome), then checked against what actually landed. "Miss" = draws where the actual number landed OUTSIDE that at-the-time dominant bracket. The verdict badge compares the current miss-streak (draws since the bracket was last hit) against its statistically expected gap.
          </p>
          {wfError && (
            <div className="text-[10px] text-red-600 mb-2 flex items-center gap-2">
              <span>{wfError}</span>
              <button
                type="button"
                onClick={() => setRetryKey((k) => k + 1)}
                className="px-2 py-0.5 rounded bg-red-600 text-white font-bold hover:bg-red-700"
                data-testid="strong-pick-decade-last88-retry"
              >
                Retry
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {ballKeys.map((bk) => {
              const { dominantLabels, successPct, hits, misses, missPct, total, currentGap, verdict } = last88Stats[bk];
              return (
                <div key={bk} className="rounded-md bg-white border border-amber-200 px-2 py-2 text-center" data-testid={`strong-pick-decade-last88-${bk}`}>
                  <div className="text-[10px] font-bold text-slate-600">{STRONG_PICK_BALL_LABELS[bk]}</div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {dominantLabels.length > 0 ? dominantLabels.join(' / ') : '—'}
                  </div>
                  <div className="text-lg font-black font-mono text-amber-700">
                    {successPct != null ? `${successPct}%` : wf === null ? '…' : 'N/A'}
                  </div>
                  {hits != null && (
                    <>
                      <div className="text-[9px] text-emerald-600 font-mono">Hit: {hits}/{total}</div>
                      <div className="text-[9px] text-red-500 font-mono" data-testid={`strong-pick-decade-last88-miss-${bk}`}>
                        Miss: {misses}/{total} ({missPct}%)
                      </div>
                      {verdict && (
                        <div
                          className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase leading-none ${verdict.badge}`}
                          data-testid={`strong-pick-decade-last88-gap-${bk}`}
                          title={`Current gap: ${currentGap ?? `${total}+`} draws since last hit`}
                        >
                          {formatVerdict(verdict)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Elite P-Slot Pool (Top 2%) — for each ball, pools every individual
 * (window × P-slot) combination's predicted number, ranks all of them by
 * historical hit % across ALL windows/P-slots for that ball, and keeps only
 * the top 2% (the statistically rarest, highest-accuracy single slots).
 * Shows the resulting distinct numbers + their LIVE pooled hit rate (did
 * ANY of them land on each of the last `test_count` actual draws).
 */
function ElitePSlotPool({ rows, ballKeys, rankSource = 'current' }) {
  const pdd = rows?.[0]?.per_draw_detail || [];
  const testCount = pdd.length;

  const perBall = useMemo(() => {
    return ballKeys.map((bk, bi) => {
      const combos = [];
      rows.forEach((w) => {
        const b = pickDiag(w.bucket_diagnostic?.[bk], rankSource);
        if (!b) return;
        const perP = b.per_p || {};
        const top20 = b.current_top20 || [];
        const predCount = (b.pred_count != null) ? b.pred_count : 50;
        for (let p = 1; p <= Math.min(top20.length, predCount); p++) {
          const pct = perP[String(p)]?.pct ?? 0;
          combos.push({ pct, windowId: w.window_id, p, number: top20[p - 1] });
        }
      });
      combos.sort((a, b2) => b2.pct - a.pct);
      const n = combos.length;
      const k = Math.max(1, Math.round(n * 0.02));
      const top = combos.slice(0, k);
      const numbers = [...new Set(top.map((c) => c.number))].sort((a, b2) => a - b2);
      const hits = pdd.filter((dd) => numbers.includes(dd.ball_by_ball?.[bi]?.actual)).length;
      const hitPct = testCount > 0 ? Math.round((hits / testCount) * 1000) / 10 : null;
      const baselinePct = Math.round((numbers.length / 50) * 1000) / 10;
      return { bk, top, numbers, hits, hitPct, baselinePct, n, k };
    }).reduce((acc, item) => { acc[item.bk] = item; return acc; }, {});
  }, [rows, ballKeys, rankSource]);

  const handleExportExcel = () => {
    const detailRows = [];
    ballKeys.forEach((bk, bi) => {
      const info = perBall[bk];
      info.top.forEach((c) => {
        detailRows.push({
          'Ball': `Ball ${bi + 1}`,
          'Window': c.windowId,
          'P-Rank': c.p,
          'Number': c.number,
          'Hit %': c.pct,
        });
      });
    });
    const summaryRows = ballKeys.map((bk, bi) => {
      const info = perBall[bk];
      return {
        'Ball': `Ball ${bi + 1}`,
        'Elite Numbers': info.numbers.join(', '),
        'Count': info.numbers.length,
        [`Pooled Hit Rate (last ${testCount})`]: info.hitPct != null ? `${info.hitPct}%` : 'N/A',
        'Random Baseline %': `${info.baselinePct}%`,
        'Combos Considered': info.n,
        'Top 2% Cutoff (count)': info.k,
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Elite Combos Detail');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_ElitePSlotPool_top2pct_${today}.xlsx`);
    toast.success('Downloaded Elite P-Slot Pool workbook');
  };

  return (
    <div className="rounded-xl border-2 border-cyan-300 bg-white shadow-sm overflow-hidden" data-testid="elite-pslot-pool">
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white px-4 py-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wide">Elite P-Slot Pool (Top 2%)</h3>
          <p className="text-[11px] text-cyan-100 mt-0.5">
            Pools every (window × P-slot) combo's predicted number for each ball, keeps only the top 2% by historical hit % across all {rows.length} windows × 20 P-slots, and checks the LIVE pooled hit rate of that elite number set over the last {testCount} draws.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportExcel}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded font-mono bg-white text-cyan-700 hover:bg-cyan-50 font-bold text-xs shrink-0"
          data-testid="elite-pslot-pool-export"
        >
          <DownloadSimple size={14} weight="bold" /> Download Excel
        </button>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
        {ballKeys.map((bk, bi) => {
          const info = perBall[bk];
          const beatsBaseline = info.hitPct != null && info.hitPct > info.baselinePct;
          return (
            <div key={bk} className="rounded-md bg-cyan-50 border border-cyan-200 px-2 py-2" data-testid={`elite-pslot-pool-${bk}`}>
              <div className="text-[10px] font-bold text-cyan-800 text-center">{STRONG_PICK_BALL_LABELS[bk]}</div>
              <div className="flex flex-wrap gap-1 justify-center my-1.5" data-testid={`elite-pslot-pool-numbers-${bk}`}>
                {info.numbers.map((n) => (
                  <span key={n} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-600 text-white text-[10px] font-black">
                    {n}
                  </span>
                ))}
              </div>
              <div className="text-center">
                <div className="text-lg font-black font-mono text-cyan-700" data-testid={`elite-pslot-pool-hitrate-${bk}`}>
                  {info.hitPct != null ? `${info.hitPct}%` : 'N/A'}
                </div>
                <div className={`text-[9px] font-bold ${beatsBaseline ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {beatsBaseline ? '▲' : '▼'} vs {info.baselinePct}% random baseline
                </div>
                <div className="text-[9px] text-cyan-600 font-mono mt-0.5">Hits: {info.hits}/{testCount}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StrongPickSummary({ rows, ballKeys, rankSource = 'current' }) {
  const [threshold, setThreshold] = React.useState(5);
  const [sweetSpotOnly, setSweetSpotOnly] = React.useState(false);

  const summary = useMemo(() => computeStrongPick(rows, ballKeys, rankSource, threshold), [rows, ballKeys, threshold, rankSource]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    // Sheet 1: Summary — one row per (ball, number) with count + best stats
    const summaryRows = [];
    ballKeys.forEach((bk, bi) => {
      const list = summary[bk] || [];
      const spot = STRONG_PICK_SWEET_SPOT[bk];
      list.forEach((entry) => {
        const best = entry.occurrences.reduce((b, o) => (o.pct > b.pct ? o : b), entry.occurrences[0] || { pct: 0, p_rank: null, window_id: '' });
        const breakdown = entry.pctGroups.map((g) => `(${g.count}×${g.pct}%)`).join('');
        summaryRows.push({
          'Ball': `Ball ${bi + 1}`,
          'Number': entry.number,
          'Breakdown (count × pct%)': breakdown,
          'Total Score': entry.totalScore,
          [`Hits ≥ ${threshold}%`]: entry.count,
          'Best %': best.pct,
          'Best P-Rank': best.p_rank,
          'Best Window': best.window_id,
          [`Sweet-Spot (${spot.label})`]: (entry.totalScore >= spot.lo && entry.totalScore <= spot.hi) ? 'Y' : '',
        });
      });
    });
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Sheet 2: Detail — one row per (ball, number, window, p-rank) occurrence (ALL, not just ≥ threshold)
    const detailRows = [];
    ballKeys.forEach((bk, bi) => {
      const list = summary[bk] || [];
      list.forEach((entry) => {
        entry.all_occurrences.forEach((o) => {
          detailRows.push({
            'Ball': `Ball ${bi + 1}`,
            'Number': entry.number,
            'Window': o.window_id,
            'P-Rank': o.p_rank,
            'Hit %': o.pct,
            [`≥ ${threshold}%`]: o.pct >= threshold ? 'Y' : '',
          });
        });
      });
    });
    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    XLSX.utils.book_append_sheet(wb, wsDetail, 'All Occurrences');

    // Sheet 3: Per-ball breakdown (one sheet per ball for easy scanning)
    ballKeys.forEach((bk, bi) => {
      const list = summary[bk] || [];
      const perBall = list.map((entry) => {
        const best = entry.occurrences.reduce((b, o) => (o.pct > b.pct ? o : b), entry.occurrences[0] || { pct: 0, p_rank: null, window_id: '' });
        const breakdown = entry.pctGroups.map((g) => `(${g.count}×${g.pct}%)`).join('');
        return {
          'Number': entry.number,
          'Breakdown (count × pct%)': breakdown,
          'Total Score': entry.totalScore,
          [`Hits ≥ ${threshold}%`]: entry.count,
          'Best %': best.pct,
          'Best P-Rank': best.p_rank,
          'Best Window': best.window_id,
          'All Occurrences (window · P · %)': entry.all_occurrences
            .filter((o) => o.pct > 0)
            .sort((a, b) => (b.pct - a.pct) || (a.p_rank - b.p_rank))
            .map((o) => `${o.window_id} P${o.p_rank} ${o.pct}%`).join(' | '),
        };
      });
      const ws = XLSX.utils.json_to_sheet(perBall);
      XLSX.utils.book_append_sheet(wb, ws, `Ball ${bi + 1}`);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_StrongPickSummary_ge${threshold}pct_${today}.xlsx`);
  };

  const totalOpportunities = rows.length * 20; // max possible count per number per ball
  const ballColors = {
    ball_1: 'from-emerald-500 to-emerald-600',
    ball_2: 'from-sky-500 to-sky-600',
    ball_3: 'from-violet-500 to-violet-600',
    ball_4: 'from-amber-500 to-amber-600',
    ball_5: 'from-rose-500 to-rose-600',
  };

  return (
    <div className="space-y-3" data-testid="strong-pick-summary">
      <ElitePSlotPool rows={rows} ballKeys={ballKeys} rankSource={rankSource} />

      <DecadeRangeBreakdown summary={summary} ballKeys={ballKeys} threshold={threshold} />

      {/* Header */}
      <div className="rounded-xl border-2 border-emerald-300 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-black uppercase tracking-wider">🎯 Strong-Pick Summary — Numbers hit % ≥ threshold across ALL windows × P-slots</h3>
            <p className="text-[11px] text-white/85 italic mt-1">
              Each number in a ball&apos;s list shows a <b>(count × pct%)</b> breakdown of every P-slot where it appears across all {rows.length} windows × 20 P-slots (max <b>{totalOpportunities}</b> slots). Format: <code>(1×8%)(6×7%)(3×3%)(7×2%) = 73%</code> — read as &quot;1 slot at 8%, 6 slots at 7%, 3 slots at 3%, 7 slots at 2%, total score 73%&quot;. Score = Σ (count × pct). Groups ≥ the current threshold (<b>{threshold}%</b>) are highlighted emerald; sub-threshold groups are shown for context. Numbers are listed only if they hit ≥{threshold}% at ≥1 slot, and sorted by total score desc.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono flex-wrap">
            <span className="text-white/80">Threshold %:</span>
            {[5, 7, 9, 12, 15].map((t) => (
              <button
                key={t}
                onClick={() => setThreshold(t)}
                className={`px-2 py-1 rounded border ${threshold === t ? 'bg-white text-emerald-800 border-white font-black' : 'bg-emerald-800/40 text-white border-white/40 hover:bg-emerald-800/60'}`}
                data-testid={`strong-pick-threshold-${t}`}
              >
                ≥ {t}%
              </button>
            ))}
            <button
              onClick={handleExportExcel}
              className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded bg-white text-emerald-800 border border-white font-black hover:bg-emerald-50"
              data-testid="strong-pick-export-excel"
              title="Download the current summary as an Excel file"
            >
              <DownloadSimple size={14} weight="bold" />
              Download Excel
            </button>
            <button
              onClick={() => setSweetSpotOnly((v) => !v)}
              className={`ml-1 inline-flex items-center gap-1.5 px-3 py-1 rounded border font-black ${sweetSpotOnly ? 'bg-amber-400 text-amber-950 border-amber-300' : 'bg-emerald-800/40 text-white border-white/40 hover:bg-emerald-800/60'}`}
              data-testid="strong-pick-sweet-spot-toggle"
              title="Show only numbers whose total score falls in this ball's historically-strongest band (band boundaries were calibrated via an 88-draw walk-forward audit; not tied to the current test window)"
            >
              🎯 Sweet-Spot Only
            </button>
          </div>
        </div>

        <p className="px-5 pb-2 text-[11px] text-white/85 italic bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 -mt-3">
          Sweet-spot bands (band boundaries calibrated via an 88-draw walk-forward audit, held fixed): Ball 1 <b>≥60%</b> (84.1% hit rate) · Ball 2 <b>27–55%</b> (56.8%) · Ball 3 <b>14–39%</b> (53.4%, weak edge) · Ball 4 <b>22.5–45%</b> (52.3%) · Ball 5 <b>≥60%</b> (68.2%). Numbers inside their ball&apos;s band are ringed gold below. See the live, 300-draw dominant-bracket hit-rate further down for an up-to-date read.
        </p>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {ballKeys.map((bk, bi) => {
            const fullList = summary[bk] || [];
            const spot = STRONG_PICK_SWEET_SPOT[bk];
            const inSpot = (entry) => entry.totalScore >= spot.lo && entry.totalScore <= spot.hi;
            const sweetCount = fullList.filter(inSpot).length;
            const list = sweetSpotOnly ? fullList.filter(inSpot) : fullList;
            const ballLabel = `Ball ${bi + 1}`;
            return (
              <div key={bk} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`strong-pick-${bk}`}>
                <div className={`px-3 py-2 bg-gradient-to-r ${ballColors[bk]} text-white flex items-center justify-between`}>
                  <span className="font-black text-sm uppercase tracking-wider">{ballLabel}</span>
                  <span className="text-[10px] font-mono opacity-90">{list.length} numbers</span>
                </div>
                <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between text-[10px]" data-testid={`sweet-spot-badge-${bk}`}>
                  <span className="text-amber-800 font-bold">🎯 Sweet-spot {spot.label}</span>
                  <span className="text-amber-700 font-mono">{sweetCount} of {fullList.length} · {spot.hitRate}% hit rate</span>
                </div>
                {list.length === 0 ? (
                  <div className="px-3 py-4 text-center text-[11px] text-slate-500 italic">
                    {sweetSpotOnly ? `No numbers currently in the ${spot.label} sweet-spot.` : `No numbers meet ≥${threshold}% at any P-slot.`}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                    {list.map((entry) => {
                      const bestOcc = entry.occurrences.reduce((best, o) => (o.pct > best.pct ? o : best), entry.occurrences[0]);
                      const isSweet = inSpot(entry);
                      return (
                        <details key={entry.number} className={`group ${isSweet ? 'bg-amber-50/70 ring-1 ring-inset ring-amber-300' : ''}`} data-testid={`strong-pick-${bk}-${entry.number}`}>
                          <summary className="cursor-pointer list-none px-3 py-2 hover:bg-emerald-50 select-none">
                            <div className="flex items-start gap-2">
                              <span className={`inline-flex items-center justify-center rounded-full font-black w-7 h-7 text-xs shrink-0 mt-0.5 ${isSweet ? 'bg-amber-500 text-white ring-2 ring-amber-300' : 'bg-slate-800 text-white'}`}>
                                {entry.number}
                              </span>
                              <div className="flex-1 min-w-0">
                                {/* Grouped (count × pct%) breakdown + total score */}
                                <div className="text-[11px] font-mono leading-tight" data-testid={`strong-pick-breakdown-${bk}-${entry.number}`}>
                                  {isSweet && <span className="mr-1 text-amber-600" title={`In the ${spot.label} sweet-spot for ${ballLabel}`}>🎯</span>}
                                  {entry.pctGroups.map((g) => (
                                    <span key={g.pct} className={`inline-block mr-0.5 ${g.pct >= threshold ? 'text-emerald-700 font-bold' : 'text-slate-500'}`}>
                                      (<b>{g.count}</b>×{g.pct}%)
                                    </span>
                                  ))}
                                  <span className="ml-1 text-slate-800">= <b className={isSweet ? 'text-amber-700' : 'text-emerald-700'}>{entry.totalScore}%</b></span>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  <b className="text-slate-800">{entry.count}</b> hits ≥{threshold}% · best P{bestOcc.p_rank} <b>{bestOcc.pct}%</b> in {bestOcc.window_id}
                                </div>
                              </div>
                              <span className="text-slate-400 group-open:rotate-90 transition-transform text-[10px] shrink-0 mt-1">▶</span>
                            </div>
                          </summary>
                          <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1">
                            {entry.all_occurrences
                              .filter((o) => o.pct > 0)
                              .sort((a, b) => (b.pct - a.pct) || (a.p_rank - b.p_rank))
                              .map((o, i) => (
                                <span key={i} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-mono ${o.pct >= threshold ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                                      title={`${o.window_id} · P${o.p_rank} · ${o.pct}%`}>
                                  <b className="text-slate-800">{o.window_id}</b>
                                  <span className="text-slate-500">P{o.p_rank}</span>
                                  <b className={o.pct >= threshold ? 'text-emerald-700' : 'text-slate-500'}>{o.pct}%</b>
                                </span>
                              ))}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function MissStreaksPanel({ data, onGoTo }) {
  const [sortKey, setSortKey] = React.useState('rank');
  const [showTop, setShowTop] = React.useState('top5'); // 'top5' | 'top10' | 'both'

  const rows = useMemo(() => {
    const list = (data?.ball_accuracy || []).map((w) => {
      const streaks = computeMissStreaks(w.per_draw_detail || []);
      const maxMid5 = Math.max(streaks.top5.ball_2 || 0, streaks.top5.ball_3 || 0, streaks.top5.ball_4 || 0);
      const maxMid10 = Math.max(streaks.top10.ball_2 || 0, streaks.top10.ball_3 || 0, streaks.top10.ball_4 || 0);
      return { w, streaks, maxMid5, maxMid10 };
    });
    const cmp = {
      rank: (a, b) => (a.w.rank || 999) - (b.w.rank || 999),
      max_top5: (a, b) => (b.streaks.maxStreak5 - a.streaks.maxStreak5) || (b.streaks.maxStreak10 - a.streaks.maxStreak10),
      max_top10: (a, b) => (b.streaks.maxStreak10 - a.streaks.maxStreak10) || (b.streaks.maxStreak5 - a.streaks.maxStreak5),
      max_mid5: (a, b) => (b.maxMid5 - a.maxMid5) || (b.streaks.maxStreak5 - a.streaks.maxStreak5),
      max_mid10: (a, b) => (b.maxMid10 - a.maxMid10) || (b.streaks.maxStreak10 - a.streaks.maxStreak10),
      window: (a, b) => (a.w.window_id > b.w.window_id ? 1 : -1),
    }[sortKey];
    return list.sort(cmp);
  }, [data, sortKey]);

  // Consolidated verdict-based pool: for each ball, gather Top-5 projections
  // from EVERY window whose verdict is OVERDUE / DEEP OVERDUE (i.e. streak >
  // expected gap). "Due next" windows are intentionally excluded — we only
  // want strong reversion candidates that have already exceeded their
  // expected miss gap. Duplicates across windows are tallied so the user can
  // see which numbers appear on multiple overdue windows for the same ball
  // position.
  const consolidated = useMemo(() => {
    const acc = {};
    BALL_KEYS.forEach((bk) => { acc[bk] = { sources: [], counts: new Map() }; });
    rows.forEach(({ w, streaks }) => {
      const pba = w.per_ball_accuracy || {};
      BALL_KEYS.forEach((bk) => {
        const streak = streaks.top5[bk];
        const pct = pba[bk]?.pct_top5 ?? 0;
        const verdict = computeVerdict(streak, pct);
        if (!['over', 'deep'].includes(verdict.tier)) return;
        // Use the CURRENT projection (computed from every available draw) so
        // the pool numbers match what W41 Summary Dashboard shows for the
        // next draw. Falls back to the backtest-frozen top_predictions if the
        // current-projection field is not yet populated by the backend.
        const picks = (w.current_top_predictions?.[bk] || w.top_predictions?.[bk] || []).slice(0, 5);
        acc[bk].sources.push({
          window_id: w.window_id,
          window_name: w.window_name,
          window_num: w.window_num,
          streak,
          pct,
          gap: verdict.gap,
          tier: verdict.tier,
          label: verdict.label,
          badge: verdict.badge,
          picks,
        });
        picks.forEach((n) => {
          const prev = acc[bk].counts.get(n) || { count: 0, from: [] };
          prev.count += 1;
          prev.from.push(w.window_id);
          acc[bk].counts.set(n, prev);
        });
      });
    });
    // Sort sources by verdict severity: deep > over, then by streak desc
    const tierRank = { deep: 3, over: 2 };
    BALL_KEYS.forEach((bk) => {
      acc[bk].sources.sort((a, b) => (tierRank[b.tier] - tierRank[a.tier]) || (b.streak - a.streak));
    });
    return acc;
  }, [rows]);

  const totalDraws = data?.test_draws_count || 50;
  const anyOverdue = BALL_KEYS.some(bk => consolidated[bk].sources.length > 0);

  // Cross-ball number tally — irrespective of which ball position the number
  // came from, sum how many times each number was projected across ALL five
  // balls by the pool of OVERDUE / DEEP OVERDUE windows.
  //
  // For each number we store:
  //   total       — grand total across all 5 balls
  //   per_ball    — { ball_1: N, ball_2: N, ... }  (0 = didn't appear at that ball)
  //   ball_count  — number of DISTINCT ball positions the number showed up at
  const crossBall = useMemo(() => {
    const map = new Map(); // n -> { total, per_ball, ball_count }
    BALL_KEYS.forEach((bk) => {
      const counts = consolidated[bk]?.counts || new Map();
      counts.forEach((meta, num) => {
        const prev = map.get(num) || { total: 0, per_ball: { ball_1: 0, ball_2: 0, ball_3: 0, ball_4: 0, ball_5: 0 }, ball_count: 0 };
        prev.per_ball[bk] = meta.count;
        prev.total += meta.count;
        prev.ball_count = BALL_KEYS.filter(k => (prev.per_ball[k] || 0) > 0).length;
        map.set(num, prev);
      });
    });
    const list = [...map.entries()]
      .map(([n, meta]) => ({ n, ...meta }))
      .sort((a, b) => (b.total - a.total) || (b.ball_count - a.ball_count) || (a.n - b.n));
    return list;
  }, [consolidated]);
  const grandTotalCount = crossBall.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-5" data-testid="miss-streaks-panel">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 rounded-xl p-5 text-white shadow-lg">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex w-11 h-11 rounded-full bg-white/20 backdrop-blur items-center justify-center">
            <Fire size={26} weight="fill" className="text-yellow-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-black uppercase tracking-wider">Miss Streaks — Due vs Overdue per Window × Ball</h3>
            <p className="text-sm text-white/90 mt-1">
              Each cell shows <strong>3 numbers</strong>: the current miss <strong>streak</strong> (large), the window&apos;s <strong>hit % at that ball position</strong> (labelled <strong>T5</strong> in Top-5 mode / <strong>T10</strong> in Top-10 mode), and the expected miss gap (<em>exp N</em> — how many misses statistically fit between hits). The verdict badge underneath compares the streak to the expected gap.
            </p>
            <p className="text-xs text-white/85 mt-2">
              Below each cell you&apos;ll see the <strong>top-5 by hit %</strong> among the window&apos;s current top-10 predictions — labelled P1..P10 (model rank), then the predicted number, then its actual hit rate at this ball position in the last {totalDraws} draws. If <em>P8</em> has hit more often than <em>P1</em>, P8 appears higher in the list — so you can prefer historically stronger picks over model-ranked ones.
            </p>
            <p className="text-xs text-white/85 mt-2">
              Example: <strong>W21 Ball 1 T5 = 47%</strong> hits every alternate draw → expected gap 1. If the current streak is already 1+, the window is <em>due</em> or <em>overdue</em> — its Top-5 projection for that ball is a strong reversion pick.
            </p>
            <p className="text-xs text-white/85 mt-1">
              <strong>Note:</strong> The per-ball cell rate is <em>position-specific</em> — Ball 1 (range 1–50, easiest to hit) often beats the window&apos;s overall T5 average, while Ball 5 lags below it. So a Ball 1 T5 of 47% is legitimate even when the overall row header shows T5 ≈ 30% (the overall is the mean across all 5 balls).
            </p>
            <p className="text-xs text-white/75 mt-1 italic">
              Computed from the last {totalDraws} test draws. Rows are sorted by overall Accuracy Ranking by default (identical order to the Per-Ball Accuracy tab).
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Show:</label>
          {[['top5', 'Top-5'], ['top10', 'Top-10'], ['both', 'Both']].map(([k, label]) => (
            <Button key={k} size="sm" variant={showTop === k ? 'default' : 'outline'}
              onClick={() => setShowTop(k)}
              className={showTop === k ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
              data-testid={`miss-show-${k}`}>{label}</Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Sort by:</label>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="text-xs font-mono border border-gray-300 rounded px-2 py-1" data-testid="miss-sort">
            <option value="rank">Accuracy Ranking (same as Per-Ball Accuracy)</option>
            <option value="max_top5">Longest Top-5 miss streak (any ball)</option>
            <option value="max_top10">Longest Top-10 miss streak (any ball)</option>
            <option value="max_mid5">Longest Top-5 miss on balls 2/3/4</option>
            <option value="max_mid10">Longest Top-10 miss on balls 2/3/4</option>
            <option value="window">Window ID (A→Z)</option>
          </select>
        </div>
      </div>

      {/* Main heatmap */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto" data-testid="miss-heatmap">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th rowSpan={2} className="px-3 py-2 text-left text-[11px] font-black text-gray-700 uppercase tracking-wider border-r border-gray-300 sticky left-0 bg-gray-100 z-10 w-52">Window</th>
                {BALL_KEYS.map((bk, i) => {
                  const isMid = i >= 1 && i <= 3;
                  return (
                    <th key={bk} colSpan={showTop === 'both' ? 2 : 1}
                      className={`px-2 py-2 text-center text-[11px] font-black uppercase tracking-wider border-r border-gray-300 ${isMid ? 'bg-amber-100 text-amber-900' : 'text-gray-700'}`}>
                      Ball {i + 1}{isMid ? ' ⚠' : ''}
                    </th>
                  );
                })}
                <th rowSpan={2} className="px-3 py-2 text-center text-[11px] font-black text-rose-800 uppercase tracking-wider bg-rose-50 border-l border-rose-200">Max<br />streak</th>
              </tr>
              <tr>
                {BALL_KEYS.map((bk, i) => {
                  const isMid = i >= 1 && i <= 3;
                  const bgCls = isMid ? 'bg-amber-50' : 'bg-gray-50';
                  if (showTop === 'both') {
                    return (
                      <React.Fragment key={bk}>
                        <th className={`px-1 py-1 text-center text-[9px] font-black text-gray-500 uppercase border-r border-gray-200 ${bgCls}`}>T5</th>
                        <th className={`px-1 py-1 text-center text-[9px] font-black text-gray-500 uppercase border-r border-gray-300 ${bgCls}`}>T10</th>
                      </React.Fragment>
                    );
                  }
                  return <th key={bk} className={`px-1 py-1 text-center text-[9px] font-black text-gray-500 uppercase border-r border-gray-300 ${bgCls}`}>{showTop === 'top5' ? 'T5' : 'T10'}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ w, streaks }, idx) => {
                const pba = w.per_ball_accuracy || {};
                return (
                <tr key={w.window_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`miss-row-${w.window_id}`}>
                  <td className="px-3 py-2 border-r border-gray-200 sticky left-0 bg-inherit z-10">
                    <button className="text-left w-full hover:underline" onClick={() => onGoTo(w.window_num)} data-testid={`miss-open-${w.window_id}`}>
                      <span className="text-[10px] font-mono text-gray-400">#{w.rank}</span>{' '}
                      <span className="font-black text-gray-900">{w.window_id}</span>
                      <span className="text-gray-500 text-[11px] ml-1">{w.window_name}</span>
                      <div className="text-[10px] text-gray-500 mt-0.5 font-mono">
                        {showTop === 'top5'  && <>overall T5 <b>{w.top5_hit_rate}%</b></>}
                        {showTop === 'top10' && <>overall T10 <b>{w.top10_hit_rate}%</b></>}
                        {showTop === 'both'  && <>overall T5 {w.top5_hit_rate}% · T10 {w.top10_hit_rate}%</>}
                      </div>
                    </button>
                  </td>
                  {BALL_KEYS.map((bk, bi) => {
                    const isMid = bi >= 1 && bi <= 3;
                    const s5 = streaks.top5[bk];
                    const s10 = streaks.top10[bk];
                    const pct5 = pba[bk]?.pct_top5 ?? 0;
                    const pct10 = pba[bk]?.pct_top10 ?? 0;
                    const v5 = computeVerdict(s5, pct5);
                    const v10 = computeVerdict(s10, pct10);
                    // Current top-10 predictions with each number's hit % in
                    // the backtest window. Sorted by hit % desc so P8/P9 can
                    // legitimately outrank P1 if they've hit more often.
                    const t10 = (w.current_top10_hits && w.current_top10_hits[bk]) || [];
                    const top5ByHit = [...t10].sort((a, b) => (b.pct - a.pct) || (a.rank - b.rank)).slice(0, 5);
                    const hitCls = (p) =>
                      p >= 20 ? 'bg-emerald-500 text-white'
                      : p >= 10 ? 'bg-emerald-100 text-emerald-900'
                      : p > 0   ? 'bg-amber-100 text-amber-900'
                      :           'bg-gray-100 text-gray-500';

                    const renderCell = (streak, pct, verdict, kind) => (
                      <td key={`${bk}-${kind}`}
                        className={`px-1.5 py-1.5 text-center border-r ${kind === 'top10' ? 'border-gray-300' : 'border-gray-200'} font-mono ${streakCellClasses(streak, isMid)}`}
                        title={`Ball ${bi + 1} · ${kind === 'top5' ? 'Top-5' : 'Top-10'} miss streak: ${streak} · per-ball ${kind === 'top5' ? 'T5' : 'T10'} hit rate ${pct}% · expected gap ${verdict.gap ?? '—'} · ${verdict.label}`}
                        data-testid={`miss-cell-${w.window_id}-${bk}-${kind}`}>
                        <div className="text-lg leading-none">{streak}</div>
                        <div className="text-[10px] leading-tight opacity-90 mt-0.5">
                          <span className="opacity-60 mr-0.5">{kind === 'top5' ? 'T5' : 'T10'}</span>{pct}%
                        </div>
                        {verdict.gap != null && (
                          <div className="text-[8px] leading-tight opacity-70 mt-0.5">exp {verdict.gap}</div>
                        )}
                        <div className={`mt-1 inline-block px-1 py-0.5 rounded text-[8px] font-black uppercase leading-none ${verdict.badge}`}
                          data-testid={`miss-verdict-${w.window_id}-${bk}-${kind}`}>
                          {verdict.label}
                        </div>
                        {/* Top-5 by historical hit % among the current top-10
                            predictions. Each row: model rank (P1..P10) ·
                            predicted number · its hit % in the backtest. */}
                        {top5ByHit.length > 0 && (
                          <div className="mt-1.5 border-t border-gray-300/60 pt-1 flex flex-col gap-0.5 items-stretch"
                               data-testid={`miss-tophits-${w.window_id}-${bk}-${kind}`}>
                            {top5ByHit.map((h) => (
                              <div key={h.number}
                                   className={`flex items-center justify-between rounded px-1 py-0.5 text-[8px] leading-none font-mono ${hitCls(h.pct)}`}
                                   title={`Model rank P${h.rank} · prediction ${h.number} · hit ${h.hits}/${totalDraws} = ${h.pct}%`}
                                   data-testid={`miss-tophit-${w.window_id}-${bk}-${kind}-p${h.rank}`}>
                                <span className="opacity-70 font-bold">P{h.rank}</span>
                                <span className="font-black">{h.number}</span>
                                <span className="font-bold">{h.pct}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );

                    if (showTop === 'both') {
                      return [renderCell(s5, pct5, v5, 'top5'), renderCell(s10, pct10, v10, 'top10')];
                    }
                    return showTop === 'top5'
                      ? renderCell(s5, pct5, v5, 'top5')
                      : renderCell(s10, pct10, v10, 'top10');
                  })}
                  <td className="px-3 py-2 text-center bg-rose-50 border-l border-rose-200 font-mono font-black">
                    <div className="leading-tight">
                      <div className="text-rose-800">T5: {streaks.maxStreak5}</div>
                      <div className="text-rose-600 text-[10px]">T10: {streaks.maxStreak10}</div>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="px-4 py-3 bg-gray-50 border-t text-[11px] text-gray-600 space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-black uppercase tracking-wider text-gray-700">Streak colour:</span>
            <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 font-bold">0 (just hit)</span>
            <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-bold">1</span>
            <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-black">2 (cooling)</span>
            <span className="inline-block px-2 py-0.5 rounded bg-rose-400 text-white font-black">3–4</span>
            <span className="inline-block px-2 py-0.5 rounded bg-rose-600 text-white font-black">5+ (deep)</span>
            <span className="ml-auto italic">⚠ = mid balls (2/3/4) — historically weaker positions.</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-black uppercase tracking-wider text-gray-700">Verdict badge:</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-black uppercase">Fresh hit</span><span className="text-gray-500">= streak 0</span></span>
            <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded bg-gray-300 text-gray-800 text-[9px] font-black uppercase">Within norm</span><span className="text-gray-500">= streak &lt; expected gap</span></span>
            <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 text-[9px] font-black uppercase">Due next</span><span className="text-gray-500">= streak equals expected gap</span></span>
            <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded bg-rose-500 text-white text-[9px] font-black uppercase">Overdue</span><span className="text-gray-500">= 1 draw past gap</span></span>
            <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded bg-rose-700 text-white text-[9px] font-black uppercase">Deep overdue</span><span className="text-gray-500">= 2+ draws past gap</span></span>
          </div>
          <div className="text-[11px] text-gray-500 italic bg-white/60 rounded px-2 py-1">
            <strong>How the verdict works:</strong> Expected gap = round(100 / hit%) − 1 draws between hits. E.g. hit% 47.1% → 1 miss expected between hits; hit% 25.5% → 3 misses expected. If the current miss streak equals or exceeds that gap, the window is due / overdue for a hit on the next draw.
          </div>
        </div>
      </div>

      {/* ================== LUCKY STAR MISS STREAKS ================== */}
      {/* Same miss-streak / expected-gap / verdict math as the main ball
          heatmap above, but iterated over `data.ls_accuracy` and the two
          LS positions. Rows are Consensus + all LS windows tracked in W50
          (W9 LS Difference, W10 LS Appearance, W20 LS Spread Constraints,
          W39 LS Consensus, W20 Bayesian variants). */}
      <LSMissStreaks lsAccuracy={data?.ls_accuracy || []} onGoTo={onGoTo} showTop={showTop} totalDraws={totalDraws} />

      {/* Consolidated Picks — verdict-based multiplicity pool per ball */}
      <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-400 rounded-xl p-5" data-testid="consolidated-picks">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-emerald-600 text-white items-center justify-center font-black text-base">★</div>
          <div className="flex-1">
            <h3 className="text-base font-black text-emerald-900 uppercase tracking-wider">Consolidated Picks — pooled Top-5 from OVERDUE / DEEP OVERDUE windows</h3>
            <p className="text-sm text-emerald-800 mt-1.5 mb-3">
              For every window whose verdict is{' '}
              <span className="inline-block px-1.5 py-0.5 rounded bg-rose-500 text-white text-[10px] font-black uppercase align-middle">Overdue</span> or{' '}
              <span className="inline-block px-1.5 py-0.5 rounded bg-rose-700 text-white text-[10px] font-black uppercase align-middle">Deep overdue</span>,
              this panel pools <strong>all 5 projected numbers per ball</strong> and shows the multiplicity in brackets (how many windows agreed on the same number).
              Higher multiplicity = stronger cross-window consensus for the next draw. <em>Due-next windows are excluded</em> — only windows that have already exceeded their expected miss gap qualify as reversion candidates.
            </p>
            {!anyOverdue && (
              <p className="text-sm text-gray-600 italic">
                No window is currently OVERDUE / DEEP OVERDUE on any ball. All windows are within their normal hit rhythm — no reversion candidates to consolidate right now.
              </p>
            )}
            {anyOverdue && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {BALL_KEYS.map((bk, i) => {
                  const isMid = i >= 1 && i <= 3;
                  const box = consolidated[bk];
                  const counts = box.counts;
                  const sources = box.sources;
                  // Sort by multiplicity desc, then by number asc
                  const rankedNums = [...counts.entries()]
                    .map(([n, meta]) => ({ n, count: meta.count, from: meta.from }))
                    .sort((a, b) => (b.count - a.count) || (a.n - b.n));
                  const totalUniqueNums = rankedNums.length;
                  const dupNums = rankedNums.filter(x => x.count >= 2);

                  return (
                    <div key={bk} className={`rounded-lg p-3 border ${isMid ? 'bg-white border-amber-300 shadow-sm' : 'bg-white/70 border-gray-200'}`} data-testid={`consolidated-${bk}`}>
                      <div className={`flex items-center justify-between text-xs font-black uppercase tracking-wider mb-2 ${isMid ? 'text-amber-900' : 'text-gray-700'}`}>
                        <span>Ball {i + 1}{isMid ? ' ⚠' : ''}</span>
                        <span className="text-[10px] font-mono text-gray-500">
                          {sources.length} window{sources.length !== 1 ? 's' : ''} · {totalUniqueNums} unique
                        </span>
                      </div>

                      {sources.length === 0 ? (
                        <p className="text-[11px] text-gray-400 italic">No overdue windows on this ball.</p>
                      ) : (
                        <>
                          {/* Pooled numbers with multiplicity */}
                          <div className="flex flex-wrap gap-1 mb-2" data-testid={`consolidated-pool-${bk}`}>
                            {rankedNums.map(({ n, count, from }) => {
                              // Colour ladder for multiplicity
                              const chipCls = count >= 4
                                ? 'bg-rose-700 text-white ring-2 ring-amber-400'
                                : count === 3
                                ? 'bg-rose-500 text-white'
                                : count === 2
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-emerald-900 border border-emerald-400';
                              return (
                                <span key={n}
                                  className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono font-black gap-0.5 ${chipCls}`}
                                  title={`${count}× from: ${from.join(', ')}`}
                                  data-testid={`consolidated-pick-${bk}-${n}`}>
                                  <span className="text-sm leading-none">{n}</span>
                                  {count > 1 && <span className="text-[9px] opacity-90">({count})</span>}
                                </span>
                              );
                            })}
                          </div>

                          {/* Consensus summary */}
                          {dupNums.length > 0 && (
                            <div className="text-[10px] text-emerald-900 bg-emerald-100 border border-emerald-300 rounded px-2 py-1 mb-2 font-mono">
                              <strong>{dupNums.length} consensus number{dupNums.length !== 1 ? 's' : ''}</strong> (2× or more): {dupNums.map(x => `${x.n} (${x.count})`).join(', ')}
                            </div>
                          )}

                          {/* Sources */}
                          <details className="text-[10px] text-gray-600" data-testid={`consolidated-sources-${bk}`}>
                            <summary className="cursor-pointer hover:text-gray-900 font-bold">
                              Sources — {sources.length} overdue window{sources.length !== 1 ? 's' : ''}
                            </summary>
                            <ul className="mt-1 space-y-1 pl-3 list-disc">
                              {sources.map((s) => (
                                <li key={s.window_id}>
                                  <span className="font-black text-gray-900">{s.window_id}</span>
                                  <span className={`inline-block ml-1 px-1 py-0.5 rounded text-[8px] font-black uppercase leading-none ${s.badge}`}>{s.label}</span>
                                  <span className="text-gray-500 ml-1">
                                    streak {s.streak} vs exp {s.gap} · hit% {s.pct}
                                  </span>
                                  <div className="text-gray-700 ml-2">→ projects [<span className="font-mono">{(s.picks || []).join(', ') || '—'}</span>]</div>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Chip legend */}
            <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-emerald-900">
              <span className="font-black uppercase tracking-wider text-emerald-800">Chip legend:</span>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 font-mono font-black bg-white text-emerald-900 border border-emerald-400 text-sm">N</span>
              <span>1 window projects it</span>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 font-mono font-black bg-emerald-600 text-white text-sm">N <span className="text-[9px] opacity-90 ml-0.5">(2)</span></span>
              <span>2 windows agree</span>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 font-mono font-black bg-rose-500 text-white text-sm">N <span className="text-[9px] opacity-90 ml-0.5">(3)</span></span>
              <span>3 windows agree</span>
              <span className="inline-flex items-center rounded px-1.5 py-0.5 font-mono font-black bg-rose-700 text-white ring-2 ring-amber-400 text-sm">N <span className="text-[9px] opacity-90 ml-0.5">(4+)</span></span>
              <span>strong consensus</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Ball Number Tally — irrespective of ball position */}
      <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-fuchsia-50 border-2 border-indigo-400 rounded-xl p-5" data-testid="cross-ball-tally">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-indigo-600 text-white items-center justify-center font-black text-base">Σ</div>
          <div className="flex-1">
            <h3 className="text-base font-black text-indigo-900 uppercase tracking-wider">Cross-Ball Tally — total appearances irrespective of ball position</h3>
            <p className="text-sm text-indigo-800 mt-1.5 mb-3">
              Sums the multiplicities from the panel above <strong>across all 5 balls</strong>. A number appearing on multiple balls (e.g. 22 in Ball 1, Ball 2 twice, Ball 3 twice = 5 total) shows as a super-consensus candidate — worth including in the ticket even if you&apos;re unsure which ball position it will land at.
            </p>

            {crossBall.length === 0 ? (
              <p className="text-sm text-gray-600 italic">No numbers to tally — no window is currently OVERDUE / DEEP OVERDUE.</p>
            ) : (
              <>
                {/* Header line */}
                <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-indigo-900">
                  <span className="font-black uppercase tracking-wider">{crossBall.length} distinct numbers</span>
                  <span>·</span>
                  <span className="font-mono">total projections: {grandTotalCount}</span>
                  <span>·</span>
                  <span className="font-mono">multi-ball (2+ balls): <b>{crossBall.filter(r => r.ball_count >= 2).length}</b></span>
                </div>

                {/* Top-tier tally table */}
                <div className="bg-white rounded-lg border border-indigo-200 overflow-hidden">
                  <div className="max-h-[420px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="cross-ball-table">
                      <thead className="bg-indigo-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left font-black text-indigo-900 uppercase tracking-wider">#</th>
                          <th className="px-3 py-2 text-left font-black text-indigo-900 uppercase tracking-wider">Number</th>
                          <th className="px-3 py-2 text-center font-black text-indigo-900 uppercase tracking-wider">Total</th>
                          <th className="px-3 py-2 text-center font-black text-indigo-900 uppercase tracking-wider">Balls</th>
                          <th className="px-3 py-2 text-left font-black text-indigo-900 uppercase tracking-wider">Per-ball breakdown</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossBall.map((row, idx) => {
                          const totalCls =
                            row.total >= 10 ? 'bg-rose-700 text-white ring-2 ring-amber-400' :
                            row.total >= 7  ? 'bg-rose-500 text-white' :
                            row.total >= 4  ? 'bg-emerald-600 text-white' :
                            row.total >= 2  ? 'bg-emerald-100 text-emerald-900' :
                            'bg-gray-100 text-gray-700';
                          const isSuperConsensus = row.ball_count >= 3 && row.total >= 4;
                          const parts = BALL_KEYS.map((bk, i) => ({ bk, i, cnt: row.per_ball[bk] })).filter(p => p.cnt > 0);
                          return (
                            <tr key={row.n}
                              className={`${idx % 2 === 0 ? 'bg-white' : 'bg-indigo-50/40'} ${isSuperConsensus ? 'border-l-4 border-l-amber-500' : ''}`}
                              data-testid={`cross-ball-row-${row.n}`}>
                              <td className="px-3 py-2 font-mono text-gray-400 font-bold">{idx + 1}</td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-indigo-600 text-white font-mono font-black text-sm">{row.n}</span>
                                {isSuperConsensus && (
                                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-amber-400 text-amber-900">SUPER</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center justify-center min-w-[42px] rounded px-2 py-1 font-mono font-black text-base ${totalCls}`}>
                                  {row.total}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center font-mono font-bold text-indigo-900">
                                {row.ball_count}<span className="text-gray-400">/5</span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1.5 items-center">
                                  {parts.map(({ bk, i, cnt }) => {
                                    const isMid = i >= 1 && i <= 3;
                                    return (
                                      <span key={bk} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono font-bold ${isMid ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-800'}`}>
                                        <span className="uppercase text-[9px] opacity-70">B{i + 1}</span>
                                        <span className="font-black">×{cnt}</span>
                                      </span>
                                    );
                                  })}
                                  <span className="text-[10px] text-gray-500 font-mono">
                                    = {parts.map(p => p.cnt).join(' + ')} = <b>{row.total}</b>
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Colour ladder */}
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-indigo-900">
                  <span className="font-black uppercase tracking-wider text-indigo-800">Total colour ladder:</span>
                  <span className="inline-block rounded px-2 py-0.5 font-mono font-black bg-gray-100 text-gray-700">1</span>
                  <span className="inline-block rounded px-2 py-0.5 font-mono font-black bg-emerald-100 text-emerald-900">2–3</span>
                  <span className="inline-block rounded px-2 py-0.5 font-mono font-black bg-emerald-600 text-white">4–6</span>
                  <span className="inline-block rounded px-2 py-0.5 font-mono font-black bg-rose-500 text-white">7–9</span>
                  <span className="inline-block rounded px-2 py-0.5 font-mono font-black bg-rose-700 text-white ring-2 ring-amber-400">10+</span>
                  <span>·</span>
                  <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-amber-400 text-amber-900">SUPER</span>
                  <span>= appears on 3+ different balls with total ≥ 4 (position-agnostic strong bet)</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ================== TOP-2 BY HIT % PER WINDOW × BALL ==================
          For every window × every ball, we take its current top-10 predictions
          and pick the 2 with the highest historical hit % in the last N draws.
          Grouped by ball so you can scan "for Ball 1, which pair of numbers
          does each window most reliably hit?". Complements Consolidated Picks
          (which pools numbers by multiplicity) with a per-window best-of-2. */}
      <Top2ByHitBlock rows={rows} totalDraws={totalDraws} onGoTo={onGoTo} />

      {/* ================== W2 SUM RANGE VERDICT BACKTEST ================== */}
      {/* Dedicated block that scores W2's "FINAL VERDICT — Most Likely Next
          Range" prediction across the same backtest window as the ball rows.
          At every test draw, W2 emits a range (e.g. "120-129") + a confidence
          tier (LOW / MEDIUM / HIGH). A "hit" = the actual next-draw sum falls
          inside the predicted range. We report the overall hit rate, break it
          down by tier, and show the current streak + expected gap. */}
      <W2SumRangeVerdictBlock w2={data?.w2_sum_range_accuracy} onGoTo={() => onGoTo(14)} />

      {/* ================== W2 DIRECTION + CORRIDOR BACKTEST ================== */}
      {/* Scores the new W2 verdict card: direction call, 30-pt primary
          corridor, extended pool (~4 ranges) and exact best range. */}
      <W2DirectionCorridorBlock dc={data?.w2_direction_corridor_accuracy} onGoTo={() => onGoTo(14)} />

      {/* ================== W17 RANGE TRANSITION BACKTEST ================== */}
      {/* Walk-forward backtest of W17's Range Transition projection. For
          each of the last 55 draws we reconstruct history-as-of-that-draw,
          apply the exact same `overall + 0.3 × gap` formula per ball, and
          score projected-range vs actual-range. Reports per-ball and
          overall hit rate — surfaces Ball 1 & Ball 5 as the strongest
          positions (both ~70%) while middle balls sit around 33-44%. */}
      <W17RangeTransitionBlock rt={data?.w17_range_transition_accuracy} onGoTo={() => onGoTo(17)} />

      {/* ================== W17 DIGIT VERDICT BACKTEST ================== */}
      {/* Walk-forward backtest of W17's Digit Verdict (top-3 digit groups)
          on the last 55 draws. For each draw we call the standalone
          `compute_digit_verdict_top3()` helper against history-as-of-that
          -draw, then count how many of the actual 5 main balls have their
          last-digit in the projected top-3. Reports overall main-ball hit
          rate plus ≥2-hits and ≥3-hits per-draw tiers. */}
      <W17DigitVerdictBlock dv={data?.w17_digit_verdict_accuracy} onGoTo={() => onGoTo(17)} />

      {/* ================== WINDOW PROJECTION MATRIX (per-ball %) ================== */}
      {/* Sits at the very end of the Miss Streaks panel. For each ball
          position, shows a 50×N matrix — rows = numbers 1..50, columns =
          every window in W50's ball_accuracy list. Each cell holds the
          window's normalised % score for that number at that ball position
          (blank if the number isn't in the window's Top-K picks). A Sum
          column tallies the cross-window signal. Excel download exports
          all 5 balls in one workbook. */}
      <WindowProjectionMatrix data={data} />
    </div>
  );
}


/* ================================================================
 * WindowProjectionMatrix
 * --------------------------------------------------------------------
 * Per-ball projection matrix. For each ball position (1..5):
 *   - Rows = numbers 1..50
 *   - Columns = every window in `data.ball_accuracy` (dynamic order —
 *     highest-rank / strongest windows first, then the rest)
 *   - Cells  = the window's normalised probability % for that number at
 *              that ball position (blank if the number is not in the
 *              window's Top-K picks). Normalisation is done PER WINDOW
 *              PER BALL — each column's scores sum to 100% so different
 *              scoring scales (W3 vs W8 vs W22-Bayesian) become directly
 *              comparable.
 *   - Sum column = row total (higher = stronger cross-window signal).
 *
 * Excel download: one workbook, one sheet per ball position, sheet body
 * matches the on-screen table exactly (row 1 = window headers, rows 2..51
 * = numbers 1..50 + values, last column = Sum).
 * ================================================================ */
function WindowProjectionMatrix({ data }) {
  const [activeBall, setActiveBall] = useState('ball_1');

  const windows = useMemo(() => data?.ball_accuracy || [], [data]);

  // Build the 50-row matrix for each ball position.
  //   perBall[bk] = { columns: [{id,name}], rows: [{number, values: [pct or null], sum}] }
  //
  // Cell value = the HISTORICAL HIT-RATE of the P-slot that the number
  // currently occupies inside that window at that ball position. The
  // backend exposes this ready-to-use as
  //   ball_accuracy[i].current_top10_hits[ball_k] = [
  //     { rank: 1, number: <n>, hits: <k>, pct: <p> },
  //     ...
  //   ]
  // so we just look up the row whose `number == n` and read `pct`.
  // If the number is not in the window's Top-10 → the cell is blank.
  const perBall = useMemo(() => {
    const out = {};
    BALL_KEYS.forEach((bk) => {
      const columns = [...windows]
        .sort((a, b) => (a.rank || 999) - (b.rank || 999))
        .map(w => ({ id: w.window_id, name: w.window_name || w.window_id, num: w.window_num }));

      // window_id -> { number -> { pct, rank } }
      const winPct = {};
      windows.forEach((w) => {
        const t10 = w.current_top10_hits?.[bk] || [];
        const map = {};
        t10.forEach((entry) => {
          if (entry && typeof entry.number === 'number') {
            map[entry.number] = { pct: Number(entry.pct) || 0, rank: entry.rank };
          }
        });
        winPct[w.window_id] = map;
      });

      // Build 50 rows.
      const rows = [];
      for (let n = 1; n <= 50; n += 1) {
        const values = columns.map((c) => {
          const hit = winPct[c.id]?.[n];
          return hit ? { pct: hit.pct, rank: hit.rank } : null;
        });
        const sum = values.reduce((acc, v) => acc + (v ? v.pct : 0), 0);
        rows.push({ number: n, values, sum });
      }
      out[bk] = { columns, rows };
    });
    return out;
  }, [windows]);

  // Ranked list (highest Sum first) per ball for the "top 10" preview strip.
  const topSum = useMemo(() => {
    const out = {};
    BALL_KEYS.forEach((bk) => {
      out[bk] = [...(perBall[bk]?.rows || [])].sort((a, b) => b.sum - a.sum).slice(0, 10);
    });
    return out;
  }, [perBall]);

  const handleExportExcel = () => {
    if (!perBall) return;
    const wb = XLSX.utils.book_new();
    BALL_KEYS.forEach((bk, idx) => {
      const box = perBall[bk];
      if (!box || box.columns.length === 0) return;
      const header = [`Ball ${idx + 1}`, ...box.columns.map(c => c.id), 'Sum'];
      const body = box.rows.map(r => [
        r.number,
        ...r.values.map(v => (v == null ? '' : Math.round(v.pct * 10) / 10)),
        Math.round(r.sum * 10) / 10,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      XLSX.utils.book_append_sheet(wb, ws, `Ball ${idx + 1}`);
    });
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W50_ProjectionMatrix_${today}.xlsx`);
    toast.success('Downloaded projection matrix workbook');
  };

  if (windows.length === 0) return null;

  const activeBox = perBall[activeBall] || { columns: [], rows: [] };
  const activeTop = topSum[activeBall] || [];
  const activeSums = activeBox.rows.map(r => r.sum);
  const maxSum = activeSums.length ? Math.max(...activeSums, 1) : 1;

  const cellCls = (pct) => {
    if (pct == null) return 'text-gray-300';
    if (pct >= 12) return 'bg-emerald-500 text-white font-black';
    if (pct >= 9)  return 'bg-emerald-300 text-emerald-950 font-bold';
    if (pct >= 6)  return 'bg-emerald-100 text-emerald-900 font-semibold';
    if (pct >= 3)  return 'bg-emerald-50 text-emerald-800';
    return 'text-emerald-700';
  };

  const sumCls = (s) => {
    const ratio = maxSum > 0 ? s / maxSum : 0;
    if (ratio >= 0.85) return 'bg-amber-500 text-amber-950 font-black';
    if (ratio >= 0.7)  return 'bg-amber-300 text-amber-950 font-bold';
    if (ratio >= 0.5)  return 'bg-amber-100 text-amber-900 font-semibold';
    if (ratio >= 0.3)  return 'bg-amber-50 text-amber-800';
    return 'text-gray-500';
  };

  return (
    <div className="bg-white border-2 border-indigo-300 rounded-xl overflow-hidden" data-testid="window-projection-matrix">
      {/* Header banner */}
      <div className="px-4 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white flex items-center gap-3 flex-wrap">
        <TableIcon size={20} weight="fill" className="text-yellow-300" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-black uppercase tracking-wider">Per-Ball Projection Matrix — window vs. number (historical hit&nbsp;%)</h3>
          <p className="text-[11px] text-white/90 mt-0.5">
            For each ball position, every window ranks its Top-10 picks P1&hellip;P10. Each cell shows the <strong>historical hit-rate</strong> of the specific P-slot the number sits in (e.g. W21 &middot; Ball 1 &middot; P1 = #5 &middot; P1 hit 9 times over the last 68 backtest draws &rarr; 13%). The <strong>Sum</strong> column adds every window&apos;s hit-% for that number &mdash; highest Sum = strongest cross-window historical signal. Blank cell = number is not in that window&apos;s Top-10.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportExcel}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-indigo-700 hover:bg-yellow-300 hover:text-indigo-900 font-black text-xs uppercase transition"
          data-testid="projection-matrix-export"
        >
          <DownloadSimple size={16} weight="bold" />
          Download Excel
        </button>
      </div>

      {/* Ball tabs */}
      <div className="px-4 pt-3 pb-1 border-b bg-indigo-50 flex flex-wrap gap-1">
        {BALL_KEYS.map((bk, i) => {
          const active = bk === activeBall;
          return (
            <button
              key={bk}
              type="button"
              onClick={() => setActiveBall(bk)}
              className={`px-3 py-1.5 rounded-t-md text-xs font-black uppercase tracking-wider transition ${active ? 'bg-white text-indigo-700 border-t border-l border-r border-indigo-300' : 'text-indigo-400 hover:text-indigo-700'}`}
              data-testid={`projection-matrix-tab-${bk}`}
            >
              Ball {i + 1}
            </button>
          );
        })}
      </div>

      {/* Top-10 strongest signals preview */}
      {activeTop.length > 0 && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 flex-wrap" data-testid={`projection-matrix-top-${activeBall}`}>
          <span className="text-[11px] font-black text-amber-900 uppercase">🏆 Top 10 by Sum:</span>
          {activeTop.map((r, i) => (
            <span
              key={r.number}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded font-mono text-xs ${i < 3 ? 'bg-amber-400 text-amber-950 font-black' : 'bg-white text-amber-900 border border-amber-200 font-bold'}`}
              title={`Number ${r.number} · Sum ${r.sum.toFixed(1)}%`}
            >
              <strong>{r.number}</strong>
              <span className="opacity-70">·</span>
              <span>{r.sum.toFixed(0)}%</span>
            </span>
          ))}
        </div>
      )}

      {/* Matrix table */}
      <div className="overflow-auto max-h-[560px]" data-testid={`projection-matrix-table-${activeBall}`}>
        <table className="w-full text-[11px]">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr className="border-b border-slate-300">
              <th className="px-2 py-2 text-left font-black text-slate-700 uppercase tracking-wider sticky left-0 bg-slate-100 z-20 w-14">
                Ball {BALL_KEYS.indexOf(activeBall) + 1}
              </th>
              {activeBox.columns.map(c => (
                <th key={c.id} className="px-2 py-2 text-center font-black text-slate-700 uppercase tracking-wider whitespace-nowrap" title={c.name}>
                  {c.id}
                </th>
              ))}
              <th className="px-2 py-2 text-center font-black text-amber-800 uppercase tracking-wider bg-amber-100 sticky right-0 z-20 border-l border-amber-300">
                Sum
              </th>
            </tr>
          </thead>
          <tbody>
            {activeBox.rows.map((r, ri) => (
              <tr key={r.number} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} data-testid={`projection-matrix-row-${activeBall}-${r.number}`}>
                <td className="px-2 py-1 font-mono font-black text-slate-800 sticky left-0 bg-inherit z-10 border-r border-slate-200">
                  {r.number}
                </td>
                {r.values.map((v, ci) => (
                  <td
                    key={activeBox.columns[ci].id}
                    className={`px-2 py-1 text-center font-mono ${cellCls(v ? v.pct : null)}`}
                    title={v ? `${activeBox.columns[ci].id} · P${v.rank} · historical hit-rate ${v.pct.toFixed(1)}%` : ''}
                  >
                    {v ? `${v.pct.toFixed(0)}%` : ''}
                  </td>
                ))}
                <td className={`px-2 py-1 text-center font-mono sticky right-0 z-10 border-l border-amber-200 ${sumCls(r.sum)}`} data-testid={`projection-matrix-sum-${activeBall}-${r.number}`}>
                  {r.sum > 0 ? `${r.sum.toFixed(0)}%` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-600 italic">
        <strong>How to read:</strong> each cell = the historical hit-rate of the P-slot the number occupies inside that window at Ball {BALL_KEYS.indexOf(activeBall) + 1}. Hover a cell to see the P-rank. Blank = number is not in the window&apos;s Top-10 picks. <strong>Sum</strong> = cross-window historical signal strength. Windows ordered strongest first (by W50 rank).
      </div>
    </div>
  );
}


/**
 * LSMissStreaks — parallel Miss-Streaks table for Lucky Star windows.
 *
 * Structurally identical to the main ball heatmap but iterates
 * `data.ls_accuracy` (each entry has the same schema: `per_ball_accuracy`
 * keyed by `lucky_star_1/2`, `per_draw_detail` with `ball_by_ball` entries
 * whose `ball` field is `lucky_star_1` or `lucky_star_2`).
 *
 * Only 2 positions so the visual footprint is small — sits directly below
 * the main ball heatmap. Shares the same `showTop` toggle so both tables
 * stay in sync visually.
 */
function LSMissStreaks({ lsAccuracy, onGoTo, showTop, totalDraws }) {
  if (!Array.isArray(lsAccuracy) || lsAccuracy.length === 0) return null;

  const rows = lsAccuracy.map((w) => ({
    w,
    streaks: computeMissStreaks(w.per_draw_detail || [], LS_KEYS),
  }));

  return (
    <div className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid="ls-miss-streaks">
      {/* LS-specific header banner (yellow to keep visually distinct from the main heatmap) */}
      <div className="px-4 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-white flex items-center gap-2">
        <Star size={18} weight="fill" />
        <div className="flex-1">
          <h3 className="text-sm font-black uppercase tracking-wider">Miss Streaks — Lucky Star Windows</h3>
          <p className="text-[11px] text-white/90 mt-0.5">
            Same streak / expected-gap / verdict math as the main heatmap, but iterated over LS1 &amp; LS2 for each Lucky Star window. Last {totalDraws} test draws.
          </p>
        </div>
        <span className="text-[10px] font-mono opacity-80">{rows.length} LS windows</span>
      </div>

      <div className="overflow-x-auto" data-testid="ls-miss-heatmap">
        <table className="w-full text-sm">
          <thead className="bg-amber-50">
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left text-[11px] font-black text-amber-900 uppercase tracking-wider border-r border-amber-200 sticky left-0 bg-amber-50 z-10 w-52">Window</th>
              {LS_KEYS.map((lk, i) => (
                <th key={lk} colSpan={showTop === 'both' ? 2 : 1}
                    className="px-2 py-2 text-center text-[11px] font-black uppercase tracking-wider border-r border-amber-200 text-amber-900 bg-amber-100">
                  LS{i + 1}
                </th>
              ))}
              <th rowSpan={2} className="px-3 py-2 text-center text-[11px] font-black text-rose-800 uppercase tracking-wider bg-rose-50 border-l border-rose-200">Max<br />streak</th>
            </tr>
            <tr>
              {LS_KEYS.map((lk) => {
                if (showTop === 'both') {
                  return (
                    <React.Fragment key={lk}>
                      <th className="px-1 py-1 text-center text-[9px] font-black text-amber-800 uppercase border-r border-amber-200 bg-amber-50">T5</th>
                      <th className="px-1 py-1 text-center text-[9px] font-black text-amber-800 uppercase border-r border-amber-300 bg-amber-50">T10</th>
                    </React.Fragment>
                  );
                }
                return <th key={lk} className="px-1 py-1 text-center text-[9px] font-black text-amber-800 uppercase border-r border-amber-300 bg-amber-50">{showTop === 'top5' ? 'T5' : 'T10'}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ w, streaks }, idx) => {
              const pba = w.per_ball_accuracy || {};
              return (
                <tr key={w.window_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'} data-testid={`ls-miss-row-${w.window_id}`}>
                  <td className="px-3 py-2 border-r border-amber-200 sticky left-0 bg-inherit z-10">
                    <button className="text-left w-full hover:underline" onClick={() => onGoTo && onGoTo(w.window_num)} data-testid={`ls-miss-open-${w.window_id}`}>
                      <span className="text-[10px] font-mono text-amber-400">#{w.rank}</span>{' '}
                      <span className="font-black text-amber-900">{w.window_id}</span>
                      <span className="text-amber-700 text-[11px] ml-1">{w.window_name}</span>
                      <div className="text-[10px] text-amber-600 mt-0.5 font-mono">
                        overall T5 {w.top5_hit_rate}% · T10 {w.top10_hit_rate}%
                      </div>
                    </button>
                  </td>
                  {LS_KEYS.map((lk, li) => {
                    const s5 = streaks.top5[lk];
                    const s10 = streaks.top10[lk];
                    const pct5 = pba[lk]?.pct_top5 ?? 0;
                    const pct10 = pba[lk]?.pct_top10 ?? 0;
                    const v5 = computeVerdict(s5, pct5);
                    const v10 = computeVerdict(s10, pct10);

                    const renderCell = (streak, pct, verdict, kind) => (
                      <td key={`${lk}-${kind}`}
                          className={`px-1.5 py-1.5 text-center border-r ${kind === 'top10' ? 'border-amber-300' : 'border-amber-200'} font-mono ${streakCellClasses(streak, false)}`}
                          title={`LS${li + 1} · ${kind === 'top5' ? 'Top-5' : 'Top-10'} miss streak: ${streak} · hit rate ${pct}% · expected gap ${verdict.gap ?? '—'} · ${verdict.label}`}
                          data-testid={`ls-miss-cell-${w.window_id}-${lk}-${kind}`}>
                        <div className="text-lg leading-none">{streak}</div>
                        <div className="text-[10px] leading-tight opacity-90 mt-0.5">{pct}%</div>
                        {verdict.gap != null && (
                          <div className="text-[8px] leading-tight opacity-70 mt-0.5">exp {verdict.gap}</div>
                        )}
                        <div className={`mt-1 inline-block px-1 py-0.5 rounded text-[8px] font-black uppercase leading-none ${verdict.badge}`}
                             data-testid={`ls-miss-verdict-${w.window_id}-${lk}-${kind}`}>
                          {verdict.label}
                        </div>
                      </td>
                    );

                    if (showTop === 'both') {
                      return [renderCell(s5, pct5, v5, 'top5'), renderCell(s10, pct10, v10, 'top10')];
                    }
                    return showTop === 'top5'
                      ? renderCell(s5, pct5, v5, 'top5')
                      : renderCell(s10, pct10, v10, 'top10');
                  })}
                  <td className="px-3 py-2 text-center bg-rose-50 border-l border-rose-200 font-mono font-black">
                    <div className="leading-tight">
                      <div className="text-rose-800">T5: {streaks.maxStreak5}</div>
                      <div className="text-rose-600 text-[10px]">T10: {streaks.maxStreak10}</div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 bg-amber-50/50 border-t border-amber-200 text-[11px] text-amber-800 italic">
        LS1/LS2 verdict math is identical to the main balls. A DUE/OVERDUE LS window&apos;s top-5 projection for that position is a strong reversion pick.
      </div>
    </div>
  );
}

/* ================================================================
 * W2SumRangeVerdictBlock — backtest scorecard for W2's FINAL VERDICT
 * ("Most Likely Next Range") prediction.
 *
 * Unlike the per-ball backtests above, W2 emits a SUM RANGE
 * (e.g. "120-129") with a confidence tier (LOW / MEDIUM / HIGH).
 * A "hit" = the actual next-draw's ball-sum falls INSIDE that range.
 * We surface: overall hit rate, per-tier hit rate, current tail
 * streak (of hits or misses), longest streaks, expected gap.
 * ================================================================ */
function W2SumRangeVerdictBlock({ w2, onGoTo }) {
  if (!w2 || w2.total_tested === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500" data-testid="w2-sum-range-empty">
        Insufficient data to backtest W2 Sum Range Verdict yet.
      </div>
    );
  }
  const rate = w2.overall_hit_rate || 0;
  const tone = rate >= 15 ? 'emerald' : rate >= 8 ? 'amber' : 'rose';
  const grad = {
    emerald: 'from-emerald-600 via-teal-600 to-emerald-700',
    amber:   'from-amber-500 via-orange-500 to-amber-600',
    rose:    'from-rose-600 via-pink-600 to-rose-700',
  }[tone];
  const tierStyle = {
    HIGH:   { bar: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    MEDIUM: { bar: 'bg-amber-500',   chip: 'bg-amber-100 text-amber-800 border-amber-300' },
    LOW:    { bar: 'bg-slate-400',   chip: 'bg-slate-100 text-slate-700 border-slate-300' },
  };
  const streakBadge = w2.current_streak_type === 'hit'
    ? { bg: 'bg-emerald-500', label: `${w2.current_streak} HIT${w2.current_streak > 1 ? 'S' : ''} in a row` }
    : { bg: 'bg-rose-500',    label: `${w2.current_streak} MISS${w2.current_streak > 1 ? 'ES' : ''} in a row` };

  return (
    <div className="rounded-xl overflow-hidden shadow-md border-2 border-slate-200" data-testid="w2-sum-range-verdict-block">
      <div className={`bg-gradient-to-r ${grad} text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3`}>
        <div>
          <h3 className="text-lg font-black uppercase tracking-wider" data-testid="w2-sum-range-title">
            W2 Sum Range Verdict — Backtest
          </h3>
          <p className="text-xs text-white/90 mt-0.5">
            &quot;FINAL VERDICT — Most Likely Next Range&quot; checked on the last {w2.total_tested} draws · a hit = actual sum landed inside the predicted 10-unit range.
          </p>
        </div>
        <button
          type="button"
          onClick={onGoTo}
          className="text-xs font-bold bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg px-3 py-1.5 backdrop-blur"
          data-testid="w2-sum-range-open-btn"
        >
          Open W2 →
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-white">
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w2-sum-range-overall">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Overall Hit Rate</div>
          <div className="text-3xl font-black text-slate-900 font-mono mt-0.5">{rate}%</div>
          <div className="text-[11px] text-slate-500">{w2.total_hits}/{w2.total_tested} draws</div>
        </div>
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w2-sum-range-streak">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Current Streak</div>
          <div className="mt-1"><span className={`inline-block px-2 py-1 rounded-md text-white text-sm font-black ${streakBadge.bg}`}>{streakBadge.label}</span></div>
        </div>
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w2-sum-range-longest">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Longest Streaks</div>
          <div className="text-sm font-bold text-slate-800 mt-1">
            <span className="text-emerald-700">Hit {w2.longest_hit_streak}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="text-rose-700">Miss {w2.longest_miss_streak}</span>
          </div>
        </div>
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w2-sum-range-expected-gap">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Expected Gap</div>
          <div className="text-2xl font-black text-slate-900 font-mono mt-0.5">{w2.expected_gap ?? '—'}</div>
          <div className="text-[11px] text-slate-500">draws between hits</div>
        </div>
      </div>

      <div className="px-4 pb-4 bg-white">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">By Confidence Tier</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="w2-sum-range-by-tier">
          {['HIGH', 'MEDIUM', 'LOW'].map((tier) => {
            const t = w2.by_tier?.[tier] || { hits: 0, total: 0, pct: 0 };
            const st = tierStyle[tier];
            return (
              <div key={tier} className={`rounded-lg border p-3 ${st.chip}`} data-testid={`w2-sum-range-tier-${tier.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-wider">{tier}</span>
                  <span className="text-xl font-black font-mono">{t.pct}%</span>
                </div>
                <div className="text-[11px] opacity-80 mt-0.5">{t.hits}/{t.total} draws</div>
                <div className="mt-1.5 h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div className={`h-full ${st.bar}`} style={{ width: `${Math.min(100, t.pct * 3)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2 bg-slate-50/70 border-t border-slate-200 text-[11px] text-slate-600 italic">
        Backtest: at every test draw N, W2&apos;s verdict is recomputed using only draws before N. A HIGH-tier prediction that lands inside its own range is the strongest signal.
      </div>
    </div>
  );
}


/* ==========================================================================
 * W2DirectionCorridorBlock — backtest scorecard for the new W2
 * "Direction + Corridor" verdict card (Consolidated tab).
 * Four graded outcomes per draw: direction (LOWER/HIGHER), primary
 * 30-pt corridor, extended pool (~4 ranges), exact best range.
 * ========================================================================== */
function W2DirectionCorridorBlock({ dc, onGoTo }) {
  if (!dc || !dc.tested) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500" data-testid="w2-dc-empty">
        Insufficient data to backtest W2 Direction + Corridor yet.
      </div>
    );
  }
  const stats = [
    { key: 'direction', label: 'Direction Call', sub: 'LOWER / HIGHER vs prev sum', d: dc.direction, color: 'text-rose-700', bar: 'bg-rose-500' },
    { key: 'pool', label: 'Extended Pool', sub: '~4 ranges (corridor ∪ reversion)', d: dc.extended_pool, color: 'text-indigo-700', bar: 'bg-indigo-500' },
    { key: 'primary', label: 'Primary Corridor', sub: '30-pt band (top ±1 range)', d: dc.primary_corridor, color: 'text-teal-700', bar: 'bg-teal-500' },
    { key: 'exact', label: 'Exact Range', sub: 'single 10-pt range', d: dc.exact_range, color: 'text-slate-700', bar: 'bg-slate-500' },
  ];
  const recent = (dc.per_draw_detail || []).slice(-12).reverse();
  return (
    <div className="rounded-xl overflow-hidden shadow-md border-2 border-slate-200" data-testid="w2-direction-corridor-block">
      <div className="bg-gradient-to-r from-slate-800 to-teal-900 text-white px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-black uppercase tracking-wider" data-testid="w2-dc-title">
            W2 Direction + Corridor — Backtest
          </h3>
          <p className="text-[11px] text-teal-200">
            New verdict card on the W2 Consolidated tab · {dc.tested} draws tested · 4 graded outcomes per draw
          </p>
        </div>
        <button
          onClick={onGoTo}
          className="text-xs font-bold bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-lg"
          data-testid="w2-dc-open-btn"
        >
          Open W2 →
        </button>
      </div>

      <div className="bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.key} className="rounded-lg border-2 border-slate-200 p-3" data-testid={`w2-dc-${s.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{s.label}</div>
            <div className={`text-2xl font-black font-mono ${s.color}`}>{s.d?.pct}%</div>
            <div className="text-[11px] text-slate-500">{s.d?.hits}/{dc.tested} hits</div>
            <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${s.bar}`} style={{ width: `${Math.min(100, s.d?.pct || 0)}%` }} />
            </div>
            <div className="text-[9px] text-slate-400 mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="bg-white px-4 pb-4">
        <div className="text-xs font-bold text-slate-700 mb-2">Last {recent.length} test draws</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="w2-dc-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-1.5 text-center uppercase">Date</th>
                <th className="px-2 py-1.5 text-center uppercase">Prev Sum</th>
                <th className="px-2 py-1.5 text-center uppercase">Direction</th>
                <th className="px-2 py-1.5 text-center uppercase">Corridor</th>
                <th className="px-2 py-1.5 text-center uppercase">Exact Best</th>
                <th className="px-2 py-1.5 text-center uppercase">Actual</th>
                <th className="px-2 py-1.5 text-center uppercase">Dir</th>
                <th className="px-2 py-1.5 text-center uppercase">Corr</th>
                <th className="px-2 py-1.5 text-center uppercase">Pool</th>
              </tr>
            </thead>
            <tbody>
              {dc.next_prediction && (
                <tr className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-b-2 border-amber-400 ring-2 ring-amber-300" data-testid="w2-dc-next-row">
                  <td className="px-2 py-2 text-center font-black text-amber-900">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider mr-1 align-middle">Pred.</span>
                    {dc.next_prediction.draw_date}
                  </td>
                  <td className="px-2 py-2 text-center font-mono font-bold text-amber-900">{dc.next_prediction.prev_sum}</td>
                  <td className={`px-2 py-2 text-center font-black ${dc.next_prediction.direction === 'LOWER' ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {dc.next_prediction.direction === 'LOWER' ? '⬇' : '⬆'} {dc.next_prediction.direction}
                    <span className="ml-1 text-[9px] font-mono opacity-70">({dc.next_prediction.direction_pct}%)</span>
                  </td>
                  <td className="px-2 py-2 text-center font-mono font-bold text-amber-900">{dc.next_prediction.corridor}</td>
                  <td className="px-2 py-2 text-center font-mono font-bold text-amber-900">{dc.next_prediction.exact_best}</td>
                  <td className="px-2 py-2 text-center text-[10px] italic text-amber-700" colSpan={4}>
                    Pending — actual result not yet drawn
                  </td>
                </tr>
              )}
              {recent.map((r) => (
                <tr key={r.draw_number} className="odd:bg-white even:bg-slate-50 border-b" data-testid={`w2-dc-row-${r.draw_number}`}>
                  <td className="px-2 py-1.5 text-center">{r.draw_date}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.prev_sum}</td>
                  <td className={`px-2 py-1.5 text-center font-bold ${r.direction === 'LOWER' ? 'text-rose-600' : 'text-emerald-600'}`}>{r.direction === 'LOWER' ? '⬇' : '⬆'} {r.direction}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.corridor}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.exact_best}</td>
                  <td className="px-2 py-1.5 text-center font-mono font-black">{r.actual_sum}</td>
                  <td className="px-2 py-1.5 text-center">{r.direction_hit ? '✅' : '❌'}</td>
                  <td className="px-2 py-1.5 text-center">{r.primary_hit ? '✅' : '❌'}</td>
                  <td className="px-2 py-1.5 text-center">{r.pool_hit ? '✅' : '❌'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-4 py-2 bg-slate-50/70 border-t border-slate-200 text-[11px] text-slate-600 italic">
        Backtest: at every test draw N the Direction + Corridor verdict is recomputed using only draws before N. Direction is the strongest signal; the corridor and pool trade precision for a much higher hit rate than any exact-range engine.
      </div>
    </div>
  );
}


/**
 * W17 Range Transition — Backtest block.
 * Renders the walk-forward accuracy of the W17 Range Transition projection
 * across the last N test draws. For each of the 5 sorted-ball positions the
 * projection uses `overall + 0.3 × (overall − same_date)` scoring; the
 * block reports overall hit rate + per-ball hit rate + a recent-draws
 * table showing projected-vs-actual range for each ball.
 */
export function W17RangeTransitionBlock({ rt, onGoTo }) {
  if (!rt || !rt.total_comparisons) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500" data-testid="w17-rt-empty">
        Insufficient data to backtest W17 Range Transition yet.
      </div>
    );
  }
  const perBall = rt.per_ball || {};
  const recent = (rt.per_draw_detail || []).slice(-15).reverse();
  const ballCards = [1, 2, 3, 4, 5].map(i => ({
    key: `ball_${i}`,
    label: `Ball ${i}`,
    d: perBall[`ball_${i}`] || { hits: 0, tested: 0, pct: 0 },
  }));

  const barColour = (pct) => pct >= 60 ? 'bg-emerald-500' : pct >= 45 ? 'bg-amber-500' : 'bg-rose-500';
  const textColour = (pct) => pct >= 60 ? 'text-emerald-700' : pct >= 45 ? 'text-amber-700' : 'text-rose-700';

  return (
    <div className="rounded-xl overflow-hidden shadow-md border-2 border-slate-200" data-testid="w17-rt-block">
      <div className="bg-gradient-to-r from-orange-800 to-amber-800 text-white px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-black uppercase tracking-wider" data-testid="w17-rt-title">
            W17 Range Transition — Backtest
          </h3>
          <p className="text-[11px] text-amber-200">
            {rt.tested_draws} draws tested · {rt.total_comparisons} comparisons · projected range vs actual range
          </p>
        </div>
        <button
          onClick={onGoTo}
          className="text-xs font-bold bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-lg"
          data-testid="w17-rt-open-btn"
        >
          Open W17 →
        </button>
      </div>

      {/* Overall + per-ball hit-rate cards */}
      <div className="bg-white p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="rounded-lg border-2 border-orange-300 p-3 bg-gradient-to-br from-orange-50 to-amber-50" data-testid="w17-rt-overall">
          <div className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">Overall</div>
          <div className={`text-2xl font-black font-mono ${textColour(rt.overall_pct)}`}>{rt.overall_pct}%</div>
          <div className="text-[11px] text-orange-600">{rt.total_hits}/{rt.total_comparisons} hits</div>
          <div className="mt-1.5 h-1.5 bg-orange-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColour(rt.overall_pct)}`} style={{ width: `${Math.min(100, rt.overall_pct)}%` }} />
          </div>
          <div className="text-[9px] text-orange-400 mt-1">Across all 5 balls</div>
        </div>
        {ballCards.map(c => (
          <div key={c.key} className="rounded-lg border-2 border-slate-200 p-3" data-testid={`w17-rt-${c.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{c.label}</div>
            <div className={`text-2xl font-black font-mono ${textColour(c.d.pct)}`}>{c.d.pct}%</div>
            <div className="text-[11px] text-slate-500">{c.d.hits}/{c.d.tested} hits</div>
            <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${barColour(c.d.pct)}`} style={{ width: `${Math.min(100, c.d.pct)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Recent draws detail — projected vs actual */}
      <div className="bg-white px-4 pb-4">
        <div className="text-xs font-bold text-slate-700 mb-2">Last {recent.length} test draws — projected range vs actual range</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="w17-rt-table">
            <thead className="bg-orange-50">
              <tr>
                <th className="px-2 py-1.5 text-center uppercase text-orange-800">Date</th>
                <th className="px-2 py-1.5 text-center uppercase text-orange-800">Draw #</th>
                {[1, 2, 3, 4, 5].map(i => (
                  <th key={i} className="px-2 py-1.5 text-center uppercase text-orange-800" colSpan={2}>Ball {i}</th>
                ))}
                <th className="px-2 py-1.5 text-center uppercase text-orange-800">Hits</th>
              </tr>
              <tr className="text-[9px]">
                <th className="px-2 py-0.5"></th>
                <th className="px-2 py-0.5"></th>
                {[1, 2, 3, 4, 5].map(i => (
                  <React.Fragment key={i}>
                    <th className="px-1 py-0.5 text-slate-500">proj</th>
                    <th className="px-1 py-0.5 text-slate-500">actual</th>
                  </React.Fragment>
                ))}
                <th className="px-2 py-0.5"></th>
              </tr>
            </thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.draw_number} className="odd:bg-white even:bg-slate-50 border-b" data-testid={`w17-rt-row-${r.draw_number}`}>
                  <td className="px-2 py-1.5 text-center">{r.draw_date}</td>
                  <td className="px-2 py-1.5 text-center font-mono text-slate-500">{r.draw_number}</td>
                  {[1, 2, 3, 4, 5].map(i => {
                    const bk = `ball_${i}`;
                    const proj = r.projected_ranges?.[bk] || '—';
                    const actual = r.actual_ranges?.[bk] || '—';
                    const hit = r.hits?.[bk];
                    return (
                      <React.Fragment key={i}>
                        <td className="px-1 py-1.5 text-center font-mono text-[10px] text-slate-600">{proj}</td>
                        <td className={`px-1 py-1.5 text-center font-mono text-[10px] font-bold ${hit ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>{actual}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className={`px-2 py-1.5 text-center font-black ${r.hits_count >= 4 ? 'text-emerald-700' : r.hits_count >= 3 ? 'text-amber-700' : 'text-slate-600'}`}>
                    {r.hits_count}/{r.total_balls}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-4 py-2 bg-slate-50/70 border-t border-slate-200 text-[11px] text-slate-600 italic">
        Backtest: for each test draw N, the Range Transition projection is recomputed from scratch using only draws before N (walk-forward). Ball 1 &amp; Ball 5 hit at ~70% because their range distributions are very skewed (small B1 &amp; large B5 dominate). Middle balls trail because their range distributions are flatter.
      </div>
    </div>
  );
}

/**
 * W17 Digit Verdict — Backtest block.
 * Grades the walk-forward accuracy of the top-3 digit-group projection
 * over the last N test draws. Per-draw the projection returns 3 digits
 * (0-9); the actual draw's 5 main balls are then scored by their last
 * digit against that top-3 set. Reports overall hit rate, ≥2 hits and
 * ≥3 hits tiers, and a per-draw table.
 */
export function W17DigitVerdictBlock({ dv, onGoTo }) {
  if (!dv || !dv.total_ball_comparisons) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500" data-testid="w17-dv-empty">
        Insufficient data to backtest W17 Digit Verdict yet.
      </div>
    );
  }
  const recent = (dv.per_draw_detail || []).slice(-15).reverse();

  const barColour = (pct) => pct >= 45 ? 'bg-emerald-500' : pct >= 30 ? 'bg-amber-500' : 'bg-rose-500';
  const textColour = (pct) => pct >= 45 ? 'text-emerald-700' : pct >= 30 ? 'text-amber-700' : 'text-rose-700';

  return (
    <div className="rounded-xl overflow-hidden shadow-md border-2 border-slate-200" data-testid="w17-dv-block">
      <div className="bg-gradient-to-r from-emerald-800 to-teal-800 text-white px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-black uppercase tracking-wider" data-testid="w17-dv-title">
            W17 Digit Verdict — Backtest
          </h3>
          <p className="text-[11px] text-emerald-200">
            {dv.tested_draws} draws tested · {dv.total_ball_comparisons} main-ball comparisons · projected top-3 digits vs actual last-digits
          </p>
        </div>
        <button
          onClick={onGoTo}
          className="text-xs font-bold bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-lg"
          data-testid="w17-dv-open-btn"
        >
          Open W17 →
        </button>
      </div>

      {/* Overall + tier cards */}
      <div className="bg-white p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border-2 border-emerald-300 p-3 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="w17-dv-overall">
          <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">Ball-Level Hit Rate</div>
          <div className={`text-2xl font-black font-mono ${textColour(dv.overall_pct)}`}>{dv.overall_pct}%</div>
          <div className="text-[11px] text-emerald-600">{dv.total_ball_hits} / {dv.total_ball_comparisons} balls</div>
          <div className="mt-1.5 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColour(dv.overall_pct)}`} style={{ width: `${Math.min(100, dv.overall_pct)}%` }} />
          </div>
          <div className="text-[9px] text-emerald-500 mt-1">Baseline for top-3 out of 10 digits = 30%</div>
        </div>
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w17-dv-ge2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Draws with ≥ 2 hits</div>
          <div className={`text-2xl font-black font-mono ${textColour(dv.hit_rate_ge_2_pct)}`}>{dv.hit_rate_ge_2_pct}%</div>
          <div className="text-[11px] text-slate-500">draws / {dv.tested_draws}</div>
          <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColour(dv.hit_rate_ge_2_pct)}`} style={{ width: `${Math.min(100, dv.hit_rate_ge_2_pct)}%` }} />
          </div>
          <div className="text-[9px] text-slate-400 mt-1">At least two balls&apos; digits landed in top-3</div>
        </div>
        <div className="rounded-lg border-2 border-slate-200 p-3" data-testid="w17-dv-ge3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Draws with ≥ 3 hits</div>
          <div className={`text-2xl font-black font-mono ${textColour(dv.hit_rate_ge_3_pct)}`}>{dv.hit_rate_ge_3_pct}%</div>
          <div className="text-[11px] text-slate-500">draws / {dv.tested_draws}</div>
          <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColour(dv.hit_rate_ge_3_pct)}`} style={{ width: `${Math.min(100, dv.hit_rate_ge_3_pct)}%` }} />
          </div>
          <div className="text-[9px] text-slate-400 mt-1">Three-plus balls aligned with projected digits</div>
        </div>
      </div>

      {/* Recent draws — projected top-3 digits vs actual ball digits */}
      <div className="bg-white px-4 pb-4">
        <div className="text-xs font-bold text-slate-700 mb-2">Last {recent.length} test draws — projected TOP-3 vs actual ball digits</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="w17-dv-table">
            <thead className="bg-emerald-50">
              <tr>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Date</th>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Draw #</th>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Projected Top-3</th>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Actual Balls</th>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Actual Digits</th>
                <th className="px-2 py-1.5 text-center uppercase text-emerald-800">Hits</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(r => {
                const top3set = new Set((r.projected_top3 || []).map(p => p.digit));
                return (
                  <tr key={r.draw_number} className="odd:bg-white even:bg-slate-50 border-b" data-testid={`w17-dv-row-${r.draw_number}`}>
                    <td className="px-2 py-1.5 text-center">{r.draw_date}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-slate-500">{r.draw_number}</td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="inline-flex gap-1">
                        {(r.projected_top3 || []).map(p => (
                          <span key={p.digit} className="inline-flex flex-col items-center bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5">
                            <span className="text-xs font-black font-mono text-emerald-800">{p.digit}</span>
                            <span className="text-[8px] text-emerald-600">{p.chance_pct}%</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono text-[10px] text-slate-600">
                      {(r.actual_balls || []).join(', ')}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="inline-flex gap-1">
                        {(r.actual_digits || []).map((dg, idx) => {
                          const hit = top3set.has(dg);
                          return (
                            <span key={idx} className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-[10px] font-mono font-black ${hit ? 'bg-emerald-600 text-white' : 'bg-rose-100 text-rose-600 border border-rose-300'}`}>
                              {dg}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`px-2 py-1.5 text-center font-black ${r.ball_hits >= 3 ? 'text-emerald-700' : r.ball_hits >= 2 ? 'text-amber-700' : 'text-slate-600'}`}>
                      {r.ball_hits}/5
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-4 py-2 bg-slate-50/70 border-t border-slate-200 text-[11px] text-slate-600 italic">
        Backtest: for each test draw N, the top-3 digit-group projection is recomputed from scratch using only draws before N (walk-forward). Baseline chance = 30% (top-3 out of 10 digit groups). Hit rate above baseline means the projection has real edge. The &ldquo;≥ 2 hits&rdquo; tier is the practical signal — {dv.hit_rate_ge_2_pct}% of tested draws had at least two balls whose last-digit landed in the projected top-3.
      </div>
    </div>
  );
}
